import 'timers-browserify';
import { binaryToHex, hexToBinary, randomBytes, areEqualBinaries, Logger, computeMd5, Defer, MetricsContext, CountMetric, LevelMetric, RateMetric, waitForEvent, setAbortableTimeout, raceForEvent, ipv4ToNumber, wait, EcdsaSecp256k1Evm, Gate, withTimeout, scheduleAtInterval, MapWithTtl, executeSafePromise, merge, addManagedEventListener, until } from '@streamr/utils';
import { EventEmitter } from 'eventemitter3';
import sample from 'lodash/sample';
import sortedIndexBy from 'lodash/sortedIndexBy';
import KBucket from 'k-bucket';
import { MessageType, reflectionMergePartial, UnknownFieldHandler, jsonWriteOptions, isJsonObject, typeofJsonValue, WireType, PbLong } from '@protobuf-ts/runtime';
import { ServiceType, stackIntercept } from '@protobuf-ts/runtime-rpc';
import { v4 } from 'uuid';
import { ProtoCallContext, RpcCommunicator, toProtoRpcClient, protoClasses as protoClasses$1, RpcError } from '@streamr/proto-rpc';
import ipaddr from 'ipaddr.js';
import { w3cwebsocket } from 'websocket';
import { SERVICE_ID } from '@streamr/autocertifier-client';
import shuffle from 'lodash/shuffle';
import { OrderedMap } from '@js-sdsl/ordered-map';
import last from 'lodash/last';
import pull from 'lodash/pull';
import { LRUCache } from 'lru-cache';
import { getLocalRegionByCoordinates, getLocalRegionWithCache } from '@streamr/cdn-location';
import Heap from 'heap';

const getPeerDistance = (nodeIdOrDataKeyRaw1, nodeIdOrDataKeyRaw2) => {
    return KBucket.distance(nodeIdOrDataKeyRaw1, nodeIdOrDataKeyRaw2);
};

// https://www.scs.stanford.edu/~dm/home/papers/kpos.pdf
const KADEMLIA_ID_LENGTH_IN_BYTES = 20;
const toDhtAddress = (raw) => {
    return binaryToHex(raw);
};
const toDhtAddressRaw = (address) => {
    return hexToBinary(address);
};
const toNodeId = (peerDescriptor) => {
    return toDhtAddress(peerDescriptor.nodeId);
};
const areEqualPeerDescriptors = (peerDescriptor1, peerDescriptor2) => {
    return areEqualBinaries(peerDescriptor1.nodeId, peerDescriptor2.nodeId);
};
const randomDhtAddress = () => {
    return toDhtAddress(randomBytes(KADEMLIA_ID_LENGTH_IN_BYTES));
};

class SortedContactList extends EventEmitter {
    options;
    contactsById = new Map();
    contactIds = [];
    constructor(options) {
        super();
        this.options = options;
        this.compareIds = this.compareIds.bind(this);
    }
    getClosestContactId() {
        return this.contactIds[0];
    }
    getContactIds() {
        return this.contactIds;
    }
    addContact(contact) {
        const contactId = contact.getNodeId();
        if (this.options.excludedNodeIds?.has(contactId)) {
            return;
        }
        if ((!this.options.allowToContainReferenceId && (this.options.referenceId === contactId)) ||
            (this.options.nodeIdDistanceLimit !== undefined && this.compareIds(this.options.nodeIdDistanceLimit, contactId) < 0)) {
            return;
        }
        if (!this.contactsById.has(contactId)) {
            if ((this.options.maxSize === undefined) || (this.contactIds.length < this.options.maxSize)) {
                this.contactsById.set(contactId, contact);
                const index = sortedIndexBy(this.contactIds, contactId, (id) => { return this.distanceToReferenceId(id); });
                this.contactIds.splice(index, 0, contactId);
                if (this.hasEventListeners()) {
                    this.emit('contactAdded', contact);
                }
            }
            else if (this.compareIds(this.contactIds[this.options.maxSize - 1], contactId) > 0) {
                const removedId = this.contactIds.pop();
                const removedContact = this.contactsById.get(removedId);
                this.contactsById.delete(removedId);
                this.contactsById.set(contactId, contact);
                const index = sortedIndexBy(this.contactIds, contactId, (id) => { return this.distanceToReferenceId(id); });
                this.contactIds.splice(index, 0, contactId);
                if (this.hasEventListeners()) {
                    this.emit('contactRemoved', removedContact);
                    this.emit('contactAdded', contact);
                }
            }
        }
    }
    addContacts(contacts) {
        contacts.forEach((contact) => this.addContact(contact));
    }
    getContact(id) {
        return this.contactsById.get(id);
    }
    has(id) {
        return this.contactsById.has(id);
    }
    /*
     * Closest first then others in ascending distance order
     */
    getClosestContacts(limit) {
        const limitedContactIds = (limit === undefined) ? this.contactIds : this.contactIds.slice(0, Math.max(limit, 0));
        return limitedContactIds.map((nodeId) => this.contactsById.get(nodeId));
    }
    /*
     * Furthest first then others in descending distance order
     */
    getFurthestContacts(limit) {
        const ret = [...this.getClosestContacts()].reverse();
        return (limit === undefined)
            ? ret
            : ret.slice(0, Math.max(limit, 0));
    }
    compareIds(id1, id2) {
        const distance1 = this.distanceToReferenceId(id1);
        const distance2 = this.distanceToReferenceId(id2);
        return distance1 - distance2;
    }
    // TODO inline this method?
    distanceToReferenceId(id) {
        // TODO maybe this class should store the referenceId also as DhtAddressRaw so that we don't need to convert it here?
        return getPeerDistance(toDhtAddressRaw(this.options.referenceId), toDhtAddressRaw(id));
    }
    removeContact(id) {
        if (this.contactsById.has(id)) {
            const removed = this.contactsById.get(id);
            // TODO use sortedIndexBy?
            const index = this.contactIds.findIndex((nodeId) => (nodeId === id));
            this.contactIds.splice(index, 1);
            this.contactsById.delete(id);
            if (this.hasEventListeners()) {
                this.emit('contactRemoved', removed);
            }
            return true;
        }
        return false;
    }
    getAllContactsInUndefinedOrder() {
        return this.contactsById.values();
    }
    getSize(excludedNodeIds) {
        let excludedCount = 0;
        if (excludedNodeIds !== undefined) {
            for (const nodeId of excludedNodeIds) {
                if (this.has(nodeId)) {
                    excludedCount++;
                }
            }
        }
        return this.contactIds.length - excludedCount;
    }
    clear() {
        this.contactsById.clear();
        this.contactIds = [];
    }
    stop() {
        this.removeAllListeners();
        this.clear();
    }
    hasEventListeners() {
        return this.eventNames().length > 0;
    }
}

class DuplicateDetector {
    values = new Set();
    queue = [];
    maxItemCount;
    constructor(maxItemCount) {
        this.maxItemCount = maxItemCount;
    }
    add(value) {
        this.values.add(value);
        this.queue.push(value);
        if (this.queue.length > this.maxItemCount) {
            const removed = this.queue.shift();
            this.values.delete(removed);
        }
    }
    isMostLikelyDuplicate(value) {
        return this.values.has(value);
    }
    size() {
        return this.values.size;
    }
    clear() {
        this.values.clear();
        this.queue = [];
    }
}

/* eslint-disable max-len */
var ErrorCode;
(function (ErrorCode) {
    ErrorCode["CONNECTION_FAILED"] = "CONNECTION_FAILED";
    ErrorCode["COULD_NOT_ROUTE"] = "COULD_NOT_ROUTE";
    ErrorCode["STARTING_WEBSOCKET_SERVER_FAILED"] = "STARTING_WEBSOCKET_SERVER_FAILED";
    ErrorCode["WEBSOCKET_CONNECTION_REQUEST_REJECTED"] = "WEBSOCKET_CONNECTION_REQUEST_REJECTED";
    ErrorCode["COULD_NOT_START"] = "COULD_NOT_START";
    ErrorCode["COULD_NOT_STOP"] = "COULD_NOT_STOP";
    ErrorCode["CANNOT_CONNECT_TO_SELF"] = "CANNOT_CONNECT_TO_SELF";
    ErrorCode["NOT_IMPLEMENTED"] = "NOT_IMPLEMENTED";
    ErrorCode["ILLEGAL_RTC_PEER_CONNECTION_STATE"] = "ILLEGAL_RTC_PEER_CONNECTION_STATE";
    ErrorCode["ILLEGAL_ARGUMENTS"] = "ILLEGAL_ARGUMENTS";
    ErrorCode["CONNECTIVITY_RESPONSE_NOT_RECEIVED_BEFORE_TIMEOUT"] = "CONNECTIVITY_RESPONSE_NOT_RECEIVED_BEFORE_TIMEOUT";
    ErrorCode["CONNECTION_LOCKER"] = "CONNECTION_LOCKER";
    ErrorCode["DHT_JOIN_TIMEOUT"] = "DHT_JOIN_TIMEOUT";
    ErrorCode["SEND_FAILED"] = "SEND_FAILED";
    ErrorCode["GETTING_DATA_FAILED"] = "GETTING_DATA_FAILED";
    ErrorCode["CONNECTION_NOT_OPEN"] = "CONNECTION_NOT_OPEN";
})(ErrorCode || (ErrorCode = {}));
class Err extends Error {
    code;
    originalError;
    constructor(code, message, originalError) {
        super(message);
        this.code = code;
        this.originalError = originalError;
    }
}
class ConnectionFailed extends Err {
    constructor(message, originalError) { super(ErrorCode.CONNECTION_FAILED, message, originalError); }
}
class WebsocketServerStartError extends Err {
    constructor(message, originalError) { super(ErrorCode.STARTING_WEBSOCKET_SERVER_FAILED, message, originalError); }
}
class CouldNotStart extends Err {
    constructor(message, originalError) { super(ErrorCode.COULD_NOT_START, message, originalError); }
}
class CannotConnectToSelf extends Err {
    constructor(message, originalError) { super(ErrorCode.CANNOT_CONNECT_TO_SELF, message, originalError); }
}
class ConnectivityResponseTimeout extends Err {
    constructor(message, originalError) { super(ErrorCode.CONNECTIVITY_RESPONSE_NOT_RECEIVED_BEFORE_TIMEOUT, message, originalError); }
}
class SendFailed extends Err {
    constructor(message, originalError) { super(ErrorCode.SEND_FAILED, message, originalError); }
}

// @generated message type with reflection information, may provide speed optimized methods
class Empty$Type extends MessageType {
    constructor() {
        super("google.protobuf.Empty", []);
    }
    create(value) {
        const message = globalThis.Object.create((this.messagePrototype));
        if (value !== undefined)
            reflectionMergePartial(this, message, value);
        return message;
    }
    internalBinaryRead(reader, length, options, target) {
        let message = target ?? this.create(), end = reader.pos + length;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                default:
                    let u = options.readUnknownField;
                    if (u === "throw")
                        throw new globalThis.Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.typeName}`);
                    let d = reader.skip(wireType);
                    if (u !== false)
                        (u === true ? UnknownFieldHandler.onRead : u)(this.typeName, message, fieldNo, wireType, d);
            }
        }
        return message;
    }
    internalBinaryWrite(message, writer, options) {
        let u = options.writeUnknownFields;
        if (u !== false)
            (u == true ? UnknownFieldHandler.onWrite : u)(this.typeName, message, writer);
        return writer;
    }
}
/**
 * @generated MessageType for protobuf message google.protobuf.Empty
 */
const Empty = new Empty$Type();

// @generated message type with reflection information, may provide speed optimized methods
class Any$Type extends MessageType {
    constructor() {
        super("google.protobuf.Any", [
            { no: 1, name: "type_url", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "value", kind: "scalar", T: 12 /*ScalarType.BYTES*/ }
        ]);
    }
    /**
     * Pack the message into a new `Any`.
     *
     * Uses 'type.googleapis.com/full.type.name' as the type URL.
     */
    pack(message, type) {
        return {
            typeUrl: this.typeNameToUrl(type.typeName), value: type.toBinary(message),
        };
    }
    /**
     * Unpack the message from the `Any`.
     */
    unpack(any, type, options) {
        if (!this.contains(any, type))
            throw new Error("Cannot unpack google.protobuf.Any with typeUrl '" + any.typeUrl + "' as " + type.typeName + ".");
        return type.fromBinary(any.value, options);
    }
    /**
     * Does the given `Any` contain a packed message of the given type?
     */
    contains(any, type) {
        if (!any.typeUrl.length)
            return false;
        let wants = typeof type == "string" ? type : type.typeName;
        let has = this.typeUrlToName(any.typeUrl);
        return wants === has;
    }
    /**
     * Convert the message to canonical JSON value.
     *
     * You have to provide the `typeRegistry` option so that the
     * packed message can be converted to JSON.
     *
     * The `typeRegistry` option is also required to read
     * `google.protobuf.Any` from JSON format.
     */
    internalJsonWrite(any, options) {
        if (any.typeUrl === "")
            return {};
        let typeName = this.typeUrlToName(any.typeUrl);
        let opt = jsonWriteOptions(options);
        let type = opt.typeRegistry?.find(t => t.typeName === typeName);
        if (!type)
            throw new globalThis.Error("Unable to convert google.protobuf.Any with typeUrl '" + any.typeUrl + "' to JSON. The specified type " + typeName + " is not available in the type registry.");
        let value = type.fromBinary(any.value, { readUnknownField: false });
        let json = type.internalJsonWrite(value, opt);
        if (typeName.startsWith("google.protobuf.") || !isJsonObject(json))
            json = { value: json };
        json["@type"] = any.typeUrl;
        return json;
    }
    internalJsonRead(json, options, target) {
        if (!isJsonObject(json))
            throw new globalThis.Error("Unable to parse google.protobuf.Any from JSON " + typeofJsonValue(json) + ".");
        if (typeof json["@type"] != "string" || json["@type"] == "")
            return this.create();
        let typeName = this.typeUrlToName(json["@type"]);
        let type = options?.typeRegistry?.find(t => t.typeName == typeName);
        if (!type)
            throw new globalThis.Error("Unable to parse google.protobuf.Any from JSON. The specified type " + typeName + " is not available in the type registry.");
        let value;
        if (typeName.startsWith("google.protobuf.") && json.hasOwnProperty("value"))
            value = type.fromJson(json["value"], options);
        else {
            let copy = Object.assign({}, json);
            delete copy["@type"];
            value = type.fromJson(copy, options);
        }
        if (target === undefined)
            target = this.create();
        target.typeUrl = json["@type"];
        target.value = type.toBinary(value);
        return target;
    }
    typeNameToUrl(name) {
        if (!name.length)
            throw new Error("invalid type name: " + name);
        return "type.googleapis.com/" + name;
    }
    typeUrlToName(url) {
        if (!url.length)
            throw new Error("invalid type url: " + url);
        let slash = url.lastIndexOf("/");
        let name = slash > 0 ? url.substring(slash + 1) : url;
        if (!name.length)
            throw new Error("invalid type url: " + url);
        return name;
    }
    create(value) {
        const message = globalThis.Object.create((this.messagePrototype));
        message.typeUrl = "";
        message.value = new Uint8Array(0);
        if (value !== undefined)
            reflectionMergePartial(this, message, value);
        return message;
    }
    internalBinaryRead(reader, length, options, target) {
        let message = target ?? this.create(), end = reader.pos + length;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                case /* string type_url */ 1:
                    message.typeUrl = reader.string();
                    break;
                case /* bytes value */ 2:
                    message.value = reader.bytes();
                    break;
                default:
                    let u = options.readUnknownField;
                    if (u === "throw")
                        throw new globalThis.Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.typeName}`);
                    let d = reader.skip(wireType);
                    if (u !== false)
                        (u === true ? UnknownFieldHandler.onRead : u)(this.typeName, message, fieldNo, wireType, d);
            }
        }
        return message;
    }
    internalBinaryWrite(message, writer, options) {
        /* string type_url = 1; */
        if (message.typeUrl !== "")
            writer.tag(1, WireType.LengthDelimited).string(message.typeUrl);
        /* bytes value = 2; */
        if (message.value.length)
            writer.tag(2, WireType.LengthDelimited).bytes(message.value);
        let u = options.writeUnknownFields;
        if (u !== false)
            (u == true ? UnknownFieldHandler.onWrite : u)(this.typeName, message, writer);
        return writer;
    }
}
/**
 * @generated MessageType for protobuf message google.protobuf.Any
 */
const Any = new Any$Type();

// @generated by protobuf-ts 2.11.1 with parameter server_generic,generate_dependencies,long_type_number
// @generated from protobuf file "packages/proto-rpc/protos/ProtoRpc.proto" (package "protorpc", syntax proto3)
// tslint:disable
/**
 * @generated from protobuf enum protorpc.RpcErrorType
 */
var RpcErrorType;
(function (RpcErrorType) {
    /**
     * @generated from protobuf enum value: SERVER_TIMEOUT = 0;
     */
    RpcErrorType[RpcErrorType["SERVER_TIMEOUT"] = 0] = "SERVER_TIMEOUT";
    /**
     * @generated from protobuf enum value: CLIENT_TIMEOUT = 1;
     */
    RpcErrorType[RpcErrorType["CLIENT_TIMEOUT"] = 1] = "CLIENT_TIMEOUT";
    /**
     * @generated from protobuf enum value: UNKNOWN_RPC_METHOD = 2;
     */
    RpcErrorType[RpcErrorType["UNKNOWN_RPC_METHOD"] = 2] = "UNKNOWN_RPC_METHOD";
    /**
     * @generated from protobuf enum value: CLIENT_ERROR = 3;
     */
    RpcErrorType[RpcErrorType["CLIENT_ERROR"] = 3] = "CLIENT_ERROR";
    /**
     * @generated from protobuf enum value: SERVER_ERROR = 4;
     */
    RpcErrorType[RpcErrorType["SERVER_ERROR"] = 4] = "SERVER_ERROR";
})(RpcErrorType || (RpcErrorType = {}));
// @generated message type with reflection information, may provide speed optimized methods
class RpcMessage$Type extends MessageType {
    constructor() {
        super("protorpc.RpcMessage", [
            { no: 1, name: "header", kind: "map", K: 9 /*ScalarType.STRING*/, V: { kind: "scalar", T: 9 /*ScalarType.STRING*/ } },
            { no: 2, name: "body", kind: "message", T: () => Any },
            { no: 3, name: "requestId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 4, name: "errorType", kind: "enum", opt: true, T: () => ["protorpc.RpcErrorType", RpcErrorType] },
            { no: 5, name: "errorClassName", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ },
            { no: 6, name: "errorCode", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ },
            { no: 7, name: "errorMessage", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message protorpc.RpcMessage
 */
const RpcMessage = new RpcMessage$Type();
// @generated message type with reflection information, may provide speed optimized methods
class Mnfo2uhnf92hvqi2nviouq2hv9puhq$Type extends MessageType {
    constructor() {
        super("protorpc.Mnfo2uhnf92hvqi2nviouq2hv9puhq", [
            { no: 1, name: "empty", kind: "message", T: () => Empty }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message protorpc.Mnfo2uhnf92hvqi2nviouq2hv9puhq
 */
new Mnfo2uhnf92hvqi2nviouq2hv9puhq$Type();

// @generated message type with reflection information, may provide speed optimized methods
class Timestamp$Type extends MessageType {
    constructor() {
        super("google.protobuf.Timestamp", [
            { no: 1, name: "seconds", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 2 /*LongType.NUMBER*/ },
            { no: 2, name: "nanos", kind: "scalar", T: 5 /*ScalarType.INT32*/ }
        ]);
    }
    /**
     * Creates a new `Timestamp` for the current time.
     */
    now() {
        const msg = this.create();
        const ms = Date.now();
        msg.seconds = PbLong.from(Math.floor(ms / 1000)).toNumber();
        msg.nanos = (ms % 1000) * 1000000;
        return msg;
    }
    /**
     * Converts a `Timestamp` to a JavaScript Date.
     */
    toDate(message) {
        return new Date(PbLong.from(message.seconds).toNumber() * 1000 + Math.ceil(message.nanos / 1000000));
    }
    /**
     * Converts a JavaScript Date to a `Timestamp`.
     */
    fromDate(date) {
        const msg = this.create();
        const ms = date.getTime();
        msg.seconds = PbLong.from(Math.floor(ms / 1000)).toNumber();
        msg.nanos = ((ms % 1000) + (ms < 0 && ms % 1000 !== 0 ? 1000 : 0)) * 1000000;
        return msg;
    }
    /**
     * In JSON format, the `Timestamp` type is encoded as a string
     * in the RFC 3339 format.
     */
    internalJsonWrite(message, options) {
        let ms = PbLong.from(message.seconds).toNumber() * 1000;
        if (ms < Date.parse("0001-01-01T00:00:00Z") || ms > Date.parse("9999-12-31T23:59:59Z"))
            throw new Error("Unable to encode Timestamp to JSON. Must be from 0001-01-01T00:00:00Z to 9999-12-31T23:59:59Z inclusive.");
        if (message.nanos < 0)
            throw new Error("Unable to encode invalid Timestamp to JSON. Nanos must not be negative.");
        let z = "Z";
        if (message.nanos > 0) {
            let nanosStr = (message.nanos + 1000000000).toString().substring(1);
            if (nanosStr.substring(3) === "000000")
                z = "." + nanosStr.substring(0, 3) + "Z";
            else if (nanosStr.substring(6) === "000")
                z = "." + nanosStr.substring(0, 6) + "Z";
            else
                z = "." + nanosStr + "Z";
        }
        return new Date(ms).toISOString().replace(".000Z", z);
    }
    /**
     * In JSON format, the `Timestamp` type is encoded as a string
     * in the RFC 3339 format.
     */
    internalJsonRead(json, options, target) {
        if (typeof json !== "string")
            throw new Error("Unable to parse Timestamp from JSON " + typeofJsonValue(json) + ".");
        let matches = json.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2}):([0-9]{2})(?:Z|\.([0-9]{3,9})Z|([+-][0-9][0-9]:[0-9][0-9]))$/);
        if (!matches)
            throw new Error("Unable to parse Timestamp from JSON. Invalid format.");
        let ms = Date.parse(matches[1] + "-" + matches[2] + "-" + matches[3] + "T" + matches[4] + ":" + matches[5] + ":" + matches[6] + (matches[8] ? matches[8] : "Z"));
        if (Number.isNaN(ms))
            throw new Error("Unable to parse Timestamp from JSON. Invalid value.");
        if (ms < Date.parse("0001-01-01T00:00:00Z") || ms > Date.parse("9999-12-31T23:59:59Z"))
            throw new globalThis.Error("Unable to parse Timestamp from JSON. Must be from 0001-01-01T00:00:00Z to 9999-12-31T23:59:59Z inclusive.");
        if (!target)
            target = this.create();
        target.seconds = PbLong.from(ms / 1000).toNumber();
        target.nanos = 0;
        if (matches[7])
            target.nanos = (parseInt("1" + matches[7] + "0".repeat(9 - matches[7].length)) - 1000000000);
        return target;
    }
    create(value) {
        const message = globalThis.Object.create((this.messagePrototype));
        message.seconds = 0;
        message.nanos = 0;
        if (value !== undefined)
            reflectionMergePartial(this, message, value);
        return message;
    }
    internalBinaryRead(reader, length, options, target) {
        let message = target ?? this.create(), end = reader.pos + length;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                case /* int64 seconds */ 1:
                    message.seconds = reader.int64().toNumber();
                    break;
                case /* int32 nanos */ 2:
                    message.nanos = reader.int32();
                    break;
                default:
                    let u = options.readUnknownField;
                    if (u === "throw")
                        throw new globalThis.Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.typeName}`);
                    let d = reader.skip(wireType);
                    if (u !== false)
                        (u === true ? UnknownFieldHandler.onRead : u)(this.typeName, message, fieldNo, wireType, d);
            }
        }
        return message;
    }
    internalBinaryWrite(message, writer, options) {
        /* int64 seconds = 1; */
        if (message.seconds !== 0)
            writer.tag(1, WireType.Varint).int64(message.seconds);
        /* int32 nanos = 2; */
        if (message.nanos !== 0)
            writer.tag(2, WireType.Varint).int32(message.nanos);
        let u = options.writeUnknownFields;
        if (u !== false)
            (u == true ? UnknownFieldHandler.onWrite : u)(this.typeName, message, writer);
        return writer;
    }
}
/**
 * @generated MessageType for protobuf message google.protobuf.Timestamp
 */
const Timestamp = new Timestamp$Type();

// @generated by protobuf-ts 2.11.1 with parameter server_generic,generate_dependencies,long_type_number
// @generated from protobuf file "packages/dht/protos/DhtRpc.proto" (package "dht", syntax proto3)
// tslint:disable
/**
 * @generated from protobuf enum dht.RecursiveOperation
 */
var RecursiveOperation;
(function (RecursiveOperation) {
    /**
     * @generated from protobuf enum value: FIND_CLOSEST_NODES = 0;
     */
    RecursiveOperation[RecursiveOperation["FIND_CLOSEST_NODES"] = 0] = "FIND_CLOSEST_NODES";
    /**
     * @generated from protobuf enum value: FETCH_DATA = 1;
     */
    RecursiveOperation[RecursiveOperation["FETCH_DATA"] = 1] = "FETCH_DATA";
    /**
     * @generated from protobuf enum value: DELETE_DATA = 2;
     */
    RecursiveOperation[RecursiveOperation["DELETE_DATA"] = 2] = "DELETE_DATA";
})(RecursiveOperation || (RecursiveOperation = {}));
/**
 * @generated from protobuf enum dht.NodeType
 */
var NodeType;
(function (NodeType) {
    /**
     * @generated from protobuf enum value: NODEJS = 0;
     */
    NodeType[NodeType["NODEJS"] = 0] = "NODEJS";
    /**
     * @generated from protobuf enum value: BROWSER = 1;
     */
    NodeType[NodeType["BROWSER"] = 1] = "BROWSER";
})(NodeType || (NodeType = {}));
/**
 * @generated from protobuf enum dht.RpcResponseError
 */
var RpcResponseError;
(function (RpcResponseError) {
    /**
     * @generated from protobuf enum value: SERVER_TIMOUT = 0;
     */
    RpcResponseError[RpcResponseError["SERVER_TIMOUT"] = 0] = "SERVER_TIMOUT";
    /**
     * @generated from protobuf enum value: CLIENT_TIMEOUT = 1;
     */
    RpcResponseError[RpcResponseError["CLIENT_TIMEOUT"] = 1] = "CLIENT_TIMEOUT";
    /**
     * @generated from protobuf enum value: SERVER_ERROR = 2;
     */
    RpcResponseError[RpcResponseError["SERVER_ERROR"] = 2] = "SERVER_ERROR";
    /**
     * @generated from protobuf enum value: UNKNOWN_RPC_METHOD = 3;
     */
    RpcResponseError[RpcResponseError["UNKNOWN_RPC_METHOD"] = 3] = "UNKNOWN_RPC_METHOD";
})(RpcResponseError || (RpcResponseError = {}));
/**
 * @generated from protobuf enum dht.RouteMessageError
 */
var RouteMessageError;
(function (RouteMessageError) {
    /**
     * @generated from protobuf enum value: NO_TARGETS = 0;
     */
    RouteMessageError[RouteMessageError["NO_TARGETS"] = 0] = "NO_TARGETS";
    /**
     * @generated from protobuf enum value: DUPLICATE = 1;
     */
    RouteMessageError[RouteMessageError["DUPLICATE"] = 1] = "DUPLICATE";
    /**
     * TODO: can this be removed? If DhtNode is already stopped the server side requests
     * should not be processed
     *
     * @generated from protobuf enum value: STOPPED = 2;
     */
    RouteMessageError[RouteMessageError["STOPPED"] = 2] = "STOPPED";
})(RouteMessageError || (RouteMessageError = {}));
/**
 * @generated from protobuf enum dht.HandshakeError
 */
var HandshakeError;
(function (HandshakeError) {
    /**
     * @generated from protobuf enum value: DUPLICATE_CONNECTION = 0;
     */
    HandshakeError[HandshakeError["DUPLICATE_CONNECTION"] = 0] = "DUPLICATE_CONNECTION";
    /**
     * @generated from protobuf enum value: INVALID_TARGET_PEER_DESCRIPTOR = 1;
     */
    HandshakeError[HandshakeError["INVALID_TARGET_PEER_DESCRIPTOR"] = 1] = "INVALID_TARGET_PEER_DESCRIPTOR";
    /**
     * @generated from protobuf enum value: UNSUPPORTED_PROTOCOL_VERSION = 2;
     */
    HandshakeError[HandshakeError["UNSUPPORTED_PROTOCOL_VERSION"] = 2] = "UNSUPPORTED_PROTOCOL_VERSION";
})(HandshakeError || (HandshakeError = {}));
/**
 * @generated from protobuf enum dht.DisconnectMode
 */
var DisconnectMode;
(function (DisconnectMode) {
    /**
     * @generated from protobuf enum value: NORMAL = 0;
     */
    DisconnectMode[DisconnectMode["NORMAL"] = 0] = "NORMAL";
    /**
     * @generated from protobuf enum value: LEAVING = 1;
     */
    DisconnectMode[DisconnectMode["LEAVING"] = 1] = "LEAVING";
})(DisconnectMode || (DisconnectMode = {}));
// @generated message type with reflection information, may provide speed optimized methods
class StoreDataRequest$Type extends MessageType {
    constructor() {
        super("dht.StoreDataRequest", [
            { no: 1, name: "key", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 2, name: "data", kind: "message", T: () => Any },
            { no: 3, name: "creator", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 4, name: "createdAt", kind: "message", T: () => Timestamp },
            { no: 5, name: "ttl", kind: "scalar", T: 13 /*ScalarType.UINT32*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.StoreDataRequest
 */
const StoreDataRequest = new StoreDataRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class StoreDataResponse$Type extends MessageType {
    constructor() {
        super("dht.StoreDataResponse", []);
    }
}
/**
 * @generated MessageType for protobuf message dht.StoreDataResponse
 */
const StoreDataResponse = new StoreDataResponse$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ExternalStoreDataRequest$Type extends MessageType {
    constructor() {
        super("dht.ExternalStoreDataRequest", [
            { no: 1, name: "key", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 2, name: "data", kind: "message", T: () => Any }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ExternalStoreDataRequest
 */
const ExternalStoreDataRequest = new ExternalStoreDataRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ExternalStoreDataResponse$Type extends MessageType {
    constructor() {
        super("dht.ExternalStoreDataResponse", [
            { no: 1, name: "storers", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => PeerDescriptor }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ExternalStoreDataResponse
 */
const ExternalStoreDataResponse = new ExternalStoreDataResponse$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ReplicateDataRequest$Type extends MessageType {
    constructor() {
        super("dht.ReplicateDataRequest", [
            { no: 1, name: "entry", kind: "message", T: () => DataEntry }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ReplicateDataRequest
 */
const ReplicateDataRequest = new ReplicateDataRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class DataEntry$Type extends MessageType {
    constructor() {
        super("dht.DataEntry", [
            { no: 1, name: "key", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 2, name: "data", kind: "message", T: () => Any },
            { no: 3, name: "creator", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 4, name: "createdAt", kind: "message", T: () => Timestamp },
            { no: 5, name: "storedAt", kind: "message", T: () => Timestamp },
            { no: 6, name: "ttl", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 7, name: "stale", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 8, name: "deleted", kind: "scalar", T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.DataEntry
 */
const DataEntry = new DataEntry$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ClosestPeersRequest$Type extends MessageType {
    constructor() {
        super("dht.ClosestPeersRequest", [
            { no: 1, name: "nodeId", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 2, name: "requestId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ClosestPeersRequest
 */
const ClosestPeersRequest = new ClosestPeersRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ClosestPeersResponse$Type extends MessageType {
    constructor() {
        super("dht.ClosestPeersResponse", [
            { no: 1, name: "peers", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => PeerDescriptor },
            { no: 2, name: "requestId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ClosestPeersResponse
 */
const ClosestPeersResponse = new ClosestPeersResponse$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ClosestRingPeersRequest$Type extends MessageType {
    constructor() {
        super("dht.ClosestRingPeersRequest", [
            { no: 1, name: "ringId", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 2, name: "requestId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ClosestRingPeersRequest
 */
const ClosestRingPeersRequest = new ClosestRingPeersRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ClosestRingPeersResponse$Type extends MessageType {
    constructor() {
        super("dht.ClosestRingPeersResponse", [
            { no: 1, name: "leftPeers", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => PeerDescriptor },
            { no: 2, name: "rightPeers", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => PeerDescriptor },
            { no: 3, name: "requestId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ClosestRingPeersResponse
 */
const ClosestRingPeersResponse = new ClosestRingPeersResponse$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RecursiveOperationRequest$Type extends MessageType {
    constructor() {
        super("dht.RecursiveOperationRequest", [
            { no: 1, name: "sessionId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "operation", kind: "enum", T: () => ["dht.RecursiveOperation", RecursiveOperation] }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.RecursiveOperationRequest
 */
const RecursiveOperationRequest = new RecursiveOperationRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RecursiveOperationResponse$Type extends MessageType {
    constructor() {
        super("dht.RecursiveOperationResponse", [
            { no: 1, name: "closestConnectedNodes", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => PeerDescriptor },
            { no: 2, name: "dataEntries", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => DataEntry },
            { no: 3, name: "noCloserNodesFound", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 4, name: "routingPath", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => PeerDescriptor }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.RecursiveOperationResponse
 */
const RecursiveOperationResponse = new RecursiveOperationResponse$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PingRequest$Type extends MessageType {
    constructor() {
        super("dht.PingRequest", [
            { no: 1, name: "requestId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.PingRequest
 */
const PingRequest = new PingRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PingResponse$Type extends MessageType {
    constructor() {
        super("dht.PingResponse", [
            { no: 1, name: "requestId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.PingResponse
 */
const PingResponse = new PingResponse$Type();
// @generated message type with reflection information, may provide speed optimized methods
class LeaveNotice$Type extends MessageType {
    constructor() {
        super("dht.LeaveNotice", []);
    }
}
/**
 * @generated MessageType for protobuf message dht.LeaveNotice
 */
const LeaveNotice = new LeaveNotice$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PeerDescriptor$Type extends MessageType {
    constructor() {
        super("dht.PeerDescriptor", [
            { no: 1, name: "nodeId", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 2, name: "type", kind: "enum", T: () => ["dht.NodeType", NodeType] },
            { no: 3, name: "udp", kind: "message", T: () => ConnectivityMethod },
            { no: 4, name: "tcp", kind: "message", T: () => ConnectivityMethod },
            { no: 5, name: "websocket", kind: "message", T: () => ConnectivityMethod },
            { no: 6, name: "region", kind: "scalar", opt: true, T: 13 /*ScalarType.UINT32*/ },
            { no: 7, name: "ipAddress", kind: "scalar", opt: true, T: 13 /*ScalarType.UINT32*/ },
            { no: 8, name: "publicKey", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 9, name: "signature", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.PeerDescriptor
 */
const PeerDescriptor = new PeerDescriptor$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ConnectivityMethod$Type extends MessageType {
    constructor() {
        super("dht.ConnectivityMethod", [
            { no: 1, name: "port", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 2, name: "host", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 3, name: "tls", kind: "scalar", T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ConnectivityMethod
 */
const ConnectivityMethod = new ConnectivityMethod$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RouteMessageWrapper$Type extends MessageType {
    constructor() {
        super("dht.RouteMessageWrapper", [
            { no: 1, name: "requestId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "sourcePeer", kind: "message", T: () => PeerDescriptor },
            { no: 3, name: "target", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 4, name: "message", kind: "message", T: () => Message },
            { no: 5, name: "reachableThrough", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => PeerDescriptor },
            { no: 6, name: "routingPath", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => PeerDescriptor },
            { no: 7, name: "parallelRootNodeIds", kind: "scalar", repeat: 2 /*RepeatType.UNPACKED*/, T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.RouteMessageWrapper
 */
const RouteMessageWrapper = new RouteMessageWrapper$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RouteMessageAck$Type extends MessageType {
    constructor() {
        super("dht.RouteMessageAck", [
            { no: 1, name: "requestId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "error", kind: "enum", opt: true, T: () => ["dht.RouteMessageError", RouteMessageError] }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.RouteMessageAck
 */
const RouteMessageAck = new RouteMessageAck$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ConnectivityRequest$Type extends MessageType {
    constructor() {
        super("dht.ConnectivityRequest", [
            { no: 1, name: "port", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 2, name: "tls", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 3, name: "host", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ },
            { no: 4, name: "allowSelfSignedCertificate", kind: "scalar", T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ConnectivityRequest
 */
const ConnectivityRequest = new ConnectivityRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ConnectivityResponse$Type extends MessageType {
    constructor() {
        super("dht.ConnectivityResponse", [
            { no: 1, name: "host", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "natType", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 3, name: "websocket", kind: "message", T: () => ConnectivityMethod },
            { no: 4, name: "ipAddress", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 5, name: "protocolVersion", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 6, name: "latitude", kind: "scalar", opt: true, T: 1 /*ScalarType.DOUBLE*/ },
            { no: 7, name: "longitude", kind: "scalar", opt: true, T: 1 /*ScalarType.DOUBLE*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ConnectivityResponse
 */
const ConnectivityResponse = new ConnectivityResponse$Type();
// @generated message type with reflection information, may provide speed optimized methods
class HandshakeRequest$Type extends MessageType {
    constructor() {
        super("dht.HandshakeRequest", [
            { no: 1, name: "sourcePeerDescriptor", kind: "message", T: () => PeerDescriptor },
            { no: 2, name: "targetPeerDescriptor", kind: "message", T: () => PeerDescriptor },
            { no: 3, name: "protocolVersion", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 4, name: "applicationVersion", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.HandshakeRequest
 */
const HandshakeRequest = new HandshakeRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class HandshakeResponse$Type extends MessageType {
    constructor() {
        super("dht.HandshakeResponse", [
            { no: 1, name: "sourcePeerDescriptor", kind: "message", T: () => PeerDescriptor },
            { no: 2, name: "error", kind: "enum", opt: true, T: () => ["dht.HandshakeError", HandshakeError] },
            { no: 3, name: "protocolVersion", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 4, name: "applicationVersion", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.HandshakeResponse
 */
const HandshakeResponse = new HandshakeResponse$Type();
// @generated message type with reflection information, may provide speed optimized methods
class Message$Type extends MessageType {
    constructor() {
        super("dht.Message", [
            { no: 1, name: "messageId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "sourceDescriptor", kind: "message", T: () => PeerDescriptor },
            { no: 3, name: "targetDescriptor", kind: "message", T: () => PeerDescriptor },
            { no: 4, name: "serviceId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 5, name: "rpcMessage", kind: "message", oneof: "body", T: () => RpcMessage },
            { no: 6, name: "connectivityRequest", kind: "message", oneof: "body", T: () => ConnectivityRequest },
            { no: 7, name: "connectivityResponse", kind: "message", oneof: "body", T: () => ConnectivityResponse },
            { no: 8, name: "handshakeRequest", kind: "message", oneof: "body", T: () => HandshakeRequest },
            { no: 9, name: "handshakeResponse", kind: "message", oneof: "body", T: () => HandshakeResponse },
            { no: 10, name: "recursiveOperationRequest", kind: "message", oneof: "body", T: () => RecursiveOperationRequest }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.Message
 */
const Message = new Message$Type();
// @generated message type with reflection information, may provide speed optimized methods
class WebsocketConnectionRequest$Type extends MessageType {
    constructor() {
        super("dht.WebsocketConnectionRequest", []);
    }
}
/**
 * @generated MessageType for protobuf message dht.WebsocketConnectionRequest
 */
const WebsocketConnectionRequest = new WebsocketConnectionRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class WebrtcConnectionRequest$Type extends MessageType {
    constructor() {
        super("dht.WebrtcConnectionRequest", []);
    }
}
/**
 * @generated MessageType for protobuf message dht.WebrtcConnectionRequest
 */
const WebrtcConnectionRequest = new WebrtcConnectionRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RtcOffer$Type extends MessageType {
    constructor() {
        super("dht.RtcOffer", [
            { no: 1, name: "description", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "connectionId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.RtcOffer
 */
const RtcOffer = new RtcOffer$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RtcAnswer$Type extends MessageType {
    constructor() {
        super("dht.RtcAnswer", [
            { no: 1, name: "description", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "connectionId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.RtcAnswer
 */
const RtcAnswer = new RtcAnswer$Type();
// @generated message type with reflection information, may provide speed optimized methods
class IceCandidate$Type extends MessageType {
    constructor() {
        super("dht.IceCandidate", [
            { no: 1, name: "candidate", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "mid", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 3, name: "connectionId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.IceCandidate
 */
const IceCandidate = new IceCandidate$Type();
// @generated message type with reflection information, may provide speed optimized methods
class LockRequest$Type extends MessageType {
    constructor() {
        super("dht.LockRequest", [
            { no: 1, name: "lockId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.LockRequest
 */
const LockRequest = new LockRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class UnlockRequest$Type extends MessageType {
    constructor() {
        super("dht.UnlockRequest", [
            { no: 1, name: "lockId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.UnlockRequest
 */
const UnlockRequest = new UnlockRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class LockResponse$Type extends MessageType {
    constructor() {
        super("dht.LockResponse", [
            { no: 1, name: "accepted", kind: "scalar", T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.LockResponse
 */
const LockResponse = new LockResponse$Type();
// @generated message type with reflection information, may provide speed optimized methods
class DisconnectNotice$Type extends MessageType {
    constructor() {
        super("dht.DisconnectNotice", [
            { no: 1, name: "disconnectMode", kind: "enum", T: () => ["dht.DisconnectMode", DisconnectMode] }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.DisconnectNotice
 */
const DisconnectNotice = new DisconnectNotice$Type();
// @generated message type with reflection information, may provide speed optimized methods
class SetPrivateRequest$Type extends MessageType {
    constructor() {
        super("dht.SetPrivateRequest", [
            { no: 1, name: "isPrivate", kind: "scalar", T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.SetPrivateRequest
 */
const SetPrivateRequest = new SetPrivateRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ExternalFetchDataRequest$Type extends MessageType {
    constructor() {
        super("dht.ExternalFetchDataRequest", [
            { no: 1, name: "key", kind: "scalar", T: 12 /*ScalarType.BYTES*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ExternalFetchDataRequest
 */
const ExternalFetchDataRequest = new ExternalFetchDataRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ExternalFetchDataResponse$Type extends MessageType {
    constructor() {
        super("dht.ExternalFetchDataResponse", [
            { no: 1, name: "entries", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => DataEntry }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ExternalFetchDataResponse
 */
const ExternalFetchDataResponse = new ExternalFetchDataResponse$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ExternalFindClosestNodesRequest$Type extends MessageType {
    constructor() {
        super("dht.ExternalFindClosestNodesRequest", [
            { no: 1, name: "nodeId", kind: "scalar", T: 12 /*ScalarType.BYTES*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ExternalFindClosestNodesRequest
 */
const ExternalFindClosestNodesRequest = new ExternalFindClosestNodesRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ExternalFindClosestNodesResponse$Type extends MessageType {
    constructor() {
        super("dht.ExternalFindClosestNodesResponse", [
            { no: 1, name: "closestNodes", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => PeerDescriptor }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message dht.ExternalFindClosestNodesResponse
 */
const ExternalFindClosestNodesResponse = new ExternalFindClosestNodesResponse$Type();
/**
 * @generated ServiceType for protobuf service dht.DhtNodeRpc
 */
const DhtNodeRpc = new ServiceType("dht.DhtNodeRpc", [
    { name: "getClosestPeers", options: {}, I: ClosestPeersRequest, O: ClosestPeersResponse },
    { name: "getClosestRingPeers", options: {}, I: ClosestRingPeersRequest, O: ClosestRingPeersResponse },
    { name: "ping", options: {}, I: PingRequest, O: PingResponse },
    { name: "leaveNotice", options: {}, I: LeaveNotice, O: Empty }
]);
/**
 * @generated ServiceType for protobuf service dht.RouterRpc
 */
const RouterRpc = new ServiceType("dht.RouterRpc", [
    { name: "routeMessage", options: {}, I: RouteMessageWrapper, O: RouteMessageAck },
    { name: "forwardMessage", options: {}, I: RouteMessageWrapper, O: RouteMessageAck }
]);
/**
 * @generated ServiceType for protobuf service dht.RecursiveOperationRpc
 */
const RecursiveOperationRpc = new ServiceType("dht.RecursiveOperationRpc", [
    { name: "routeRequest", options: {}, I: RouteMessageWrapper, O: RouteMessageAck }
]);
/**
 * @generated ServiceType for protobuf service dht.StoreRpc
 */
const StoreRpc = new ServiceType("dht.StoreRpc", [
    { name: "storeData", options: {}, I: StoreDataRequest, O: StoreDataResponse },
    { name: "replicateData", options: {}, I: ReplicateDataRequest, O: Empty }
]);
/**
 * @generated ServiceType for protobuf service dht.RecursiveOperationSessionRpc
 */
const RecursiveOperationSessionRpc = new ServiceType("dht.RecursiveOperationSessionRpc", [
    { name: "sendResponse", options: {}, I: RecursiveOperationResponse, O: Empty }
]);
/**
 * @generated ServiceType for protobuf service dht.WebsocketClientConnectorRpc
 */
const WebsocketClientConnectorRpc = new ServiceType("dht.WebsocketClientConnectorRpc", [
    { name: "requestConnection", options: {}, I: WebsocketConnectionRequest, O: Empty }
]);
/**
 * @generated ServiceType for protobuf service dht.WebrtcConnectorRpc
 */
const WebrtcConnectorRpc = new ServiceType("dht.WebrtcConnectorRpc", [
    { name: "requestConnection", options: {}, I: WebrtcConnectionRequest, O: Empty },
    { name: "rtcOffer", options: {}, I: RtcOffer, O: Empty },
    { name: "rtcAnswer", options: {}, I: RtcAnswer, O: Empty },
    { name: "iceCandidate", options: {}, I: IceCandidate, O: Empty }
]);
/**
 * @generated ServiceType for protobuf service dht.ConnectionLockRpc
 */
const ConnectionLockRpc = new ServiceType("dht.ConnectionLockRpc", [
    { name: "lockRequest", options: {}, I: LockRequest, O: LockResponse },
    { name: "unlockRequest", options: {}, I: UnlockRequest, O: Empty },
    { name: "gracefulDisconnect", options: {}, I: DisconnectNotice, O: Empty },
    { name: "setPrivate", options: {}, I: SetPrivateRequest, O: Empty }
]);
/**
 * @generated ServiceType for protobuf service dht.ExternalApiRpc
 */
const ExternalApiRpc = new ServiceType("dht.ExternalApiRpc", [
    { name: "externalFetchData", options: {}, I: ExternalFetchDataRequest, O: ExternalFetchDataResponse },
    { name: "externalStoreData", options: {}, I: ExternalStoreDataRequest, O: ExternalStoreDataResponse },
    { name: "externalFindClosestNodes", options: {}, I: ExternalFindClosestNodesRequest, O: ExternalFindClosestNodesResponse }
]);

// @generated by protobuf-ts 2.11.1 with parameter server_generic,generate_dependencies,long_type_number
// @generated from protobuf file "packages/dht/protos/DhtRpc.proto" (package "dht", syntax proto3)
// tslint:disable
/**
 * @generated from protobuf service dht.DhtNodeRpc
 */
class DhtNodeRpcClient {
    _transport;
    typeName = DhtNodeRpc.typeName;
    methods = DhtNodeRpc.methods;
    options = DhtNodeRpc.options;
    constructor(_transport) {
        this._transport = _transport;
    }
    /**
     * TODO rename to getClosestNeighbors (breaking change)
     *
     * @generated from protobuf rpc: getClosestPeers
     */
    getClosestPeers(input, options) {
        const method = this.methods[0], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * TODO rename to getClosestRingContacts (breaking change)
     *
     * @generated from protobuf rpc: getClosestRingPeers
     */
    getClosestRingPeers(input, options) {
        const method = this.methods[1], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * @generated from protobuf rpc: ping
     */
    ping(input, options) {
        const method = this.methods[2], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * @generated from protobuf rpc: leaveNotice
     */
    leaveNotice(input, options) {
        const method = this.methods[3], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
}
/**
 * @generated from protobuf service dht.RouterRpc
 */
class RouterRpcClient {
    _transport;
    typeName = RouterRpc.typeName;
    methods = RouterRpc.methods;
    options = RouterRpc.options;
    constructor(_transport) {
        this._transport = _transport;
    }
    /**
     * @generated from protobuf rpc: routeMessage
     */
    routeMessage(input, options) {
        const method = this.methods[0], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * @generated from protobuf rpc: forwardMessage
     */
    forwardMessage(input, options) {
        const method = this.methods[1], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
}
/**
 * @generated from protobuf service dht.RecursiveOperationRpc
 */
class RecursiveOperationRpcClient {
    _transport;
    typeName = RecursiveOperationRpc.typeName;
    methods = RecursiveOperationRpc.methods;
    options = RecursiveOperationRpc.options;
    constructor(_transport) {
        this._transport = _transport;
    }
    /**
     * @generated from protobuf rpc: routeRequest
     */
    routeRequest(input, options) {
        const method = this.methods[0], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
}
/**
 * @generated from protobuf service dht.StoreRpc
 */
class StoreRpcClient {
    _transport;
    typeName = StoreRpc.typeName;
    methods = StoreRpc.methods;
    options = StoreRpc.options;
    constructor(_transport) {
        this._transport = _transport;
    }
    /**
     * @generated from protobuf rpc: storeData
     */
    storeData(input, options) {
        const method = this.methods[0], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * @generated from protobuf rpc: replicateData
     */
    replicateData(input, options) {
        const method = this.methods[1], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
}
/**
 * @generated from protobuf service dht.RecursiveOperationSessionRpc
 */
class RecursiveOperationSessionRpcClient {
    _transport;
    typeName = RecursiveOperationSessionRpc.typeName;
    methods = RecursiveOperationSessionRpc.methods;
    options = RecursiveOperationSessionRpc.options;
    constructor(_transport) {
        this._transport = _transport;
    }
    /**
     * @generated from protobuf rpc: sendResponse
     */
    sendResponse(input, options) {
        const method = this.methods[0], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
}
/**
 * @generated from protobuf service dht.WebsocketClientConnectorRpc
 */
class WebsocketClientConnectorRpcClient {
    _transport;
    typeName = WebsocketClientConnectorRpc.typeName;
    methods = WebsocketClientConnectorRpc.methods;
    options = WebsocketClientConnectorRpc.options;
    constructor(_transport) {
        this._transport = _transport;
    }
    /**
     * @generated from protobuf rpc: requestConnection
     */
    requestConnection(input, options) {
        const method = this.methods[0], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
}
/**
 * @generated from protobuf service dht.WebrtcConnectorRpc
 */
class WebrtcConnectorRpcClient {
    _transport;
    typeName = WebrtcConnectorRpc.typeName;
    methods = WebrtcConnectorRpc.methods;
    options = WebrtcConnectorRpc.options;
    constructor(_transport) {
        this._transport = _transport;
    }
    /**
     * @generated from protobuf rpc: requestConnection
     */
    requestConnection(input, options) {
        const method = this.methods[0], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * @generated from protobuf rpc: rtcOffer
     */
    rtcOffer(input, options) {
        const method = this.methods[1], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * @generated from protobuf rpc: rtcAnswer
     */
    rtcAnswer(input, options) {
        const method = this.methods[2], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * @generated from protobuf rpc: iceCandidate
     */
    iceCandidate(input, options) {
        const method = this.methods[3], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
}
/**
 * @generated from protobuf service dht.ConnectionLockRpc
 */
class ConnectionLockRpcClient {
    _transport;
    typeName = ConnectionLockRpc.typeName;
    methods = ConnectionLockRpc.methods;
    options = ConnectionLockRpc.options;
    constructor(_transport) {
        this._transport = _transport;
    }
    /**
     * @generated from protobuf rpc: lockRequest
     */
    lockRequest(input, options) {
        const method = this.methods[0], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * @generated from protobuf rpc: unlockRequest
     */
    unlockRequest(input, options) {
        const method = this.methods[1], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * @generated from protobuf rpc: gracefulDisconnect
     */
    gracefulDisconnect(input, options) {
        const method = this.methods[2], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * @generated from protobuf rpc: setPrivate
     */
    setPrivate(input, options) {
        const method = this.methods[3], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
}
/**
 * @generated from protobuf service dht.ExternalApiRpc
 */
class ExternalApiRpcClient {
    _transport;
    typeName = ExternalApiRpc.typeName;
    methods = ExternalApiRpc.methods;
    options = ExternalApiRpc.options;
    constructor(_transport) {
        this._transport = _transport;
    }
    /**
     * @generated from protobuf rpc: externalFetchData
     */
    externalFetchData(input, options) {
        const method = this.methods[0], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * @generated from protobuf rpc: externalStoreData
     */
    externalStoreData(input, options) {
        const method = this.methods[1], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
    /**
     * @generated from protobuf rpc: externalFindClosestNodes
     */
    externalFindClosestNodes(input, options) {
        const method = this.methods[2], opt = this._transport.mergeOptions(options);
        return stackIntercept("unary", this._transport, method, opt, input);
    }
}

const DEFAULT_SEND_OPTIONS = {
    connect: true,
    sendIfStopped: false,
    bufferWhileConnecting: true
};

class DhtCallContext extends ProtoCallContext {
    // used by client
    targetDescriptor;
    sourceDescriptor;
    clientId;
    connect;
    sendIfStopped;
    bufferWhileConnecting;
    //used in incoming calls
    incomingSourceDescriptor;
}

class RoutingRpcCommunicator extends RpcCommunicator {
    ownServiceId;
    constructor(ownServiceId, sendFn, options) {
        super(options);
        this.ownServiceId = ownServiceId;
        this.setOutgoingMessageListener((msg, _requestId, callContext) => {
            let targetDescriptor;
            // rpc call message
            if (callContext.targetDescriptor) {
                targetDescriptor = callContext.targetDescriptor;
            }
            else { // rpc reply message
                targetDescriptor = callContext.incomingSourceDescriptor;
            }
            const message = {
                messageId: v4(),
                serviceId: this.ownServiceId,
                body: {
                    oneofKind: 'rpcMessage',
                    rpcMessage: msg
                },
                targetDescriptor
            };
            // TODO maybe sendOptions could be a separate block inside callContext
            const sendOpts = (msg.header.response !== undefined)
                ? {
                    // typically we already have a connection, but if it has disconnected for some reason
                    // the receiver could have gone offline (or it is no longer a neighbor) and therefore there
                    // is no point in trying form a new connection
                    connect: false,
                    // TODO maybe this options could be removed?
                    sendIfStopped: true,
                    // Responses should be buffered if necessary
                    bufferWhileConnecting: true
                } : {
                connect: callContext?.connect ?? DEFAULT_SEND_OPTIONS.connect,
                sendIfStopped: callContext?.sendIfStopped ?? DEFAULT_SEND_OPTIONS.sendIfStopped,
                bufferWhileConnecting: callContext?.bufferWhileConnecting ?? DEFAULT_SEND_OPTIONS.bufferWhileConnecting
            };
            return sendFn(message, sendOpts);
        });
    }
    handleMessageFromPeer(message) {
        if (message.serviceId === this.ownServiceId && message.body.oneofKind === 'rpcMessage') {
            const context = new DhtCallContext();
            context.incomingSourceDescriptor = message.sourceDescriptor;
            // TODO should we have some handling for this floating promise?
            this.handleIncomingMessage(message.body.rpcMessage, context);
        }
    }
}

// Connection locks are independent of the existence of connections
// that is why this class is needed
class ConnectionLockStates {
    localLocks = new Map();
    remoteLocks = new Map();
    // TODO: remove weakLocks use localLocks instead. When opening weakLocks from the ConnectioManager,
    // simply do not send lock requests.
    weakLocks = new Map();
    // Used to filter proxy connections from the connections view
    remotePrivateConnections = new Set();
    getLocalLockedConnectionCount() {
        return this.localLocks.size;
    }
    getRemoteLockedConnectionCount() {
        return this.remoteLocks.size;
    }
    getWeakLockedConnectionCount() {
        return this.weakLocks.size;
    }
    isLocalLocked(id, lockId) {
        if (lockId === undefined) {
            return this.localLocks.has(id);
        }
        else {
            return this.localLocks.has(id) && this.localLocks.get(id).has(lockId);
        }
    }
    isRemoteLocked(id, lockId) {
        if (lockId === undefined) {
            return this.remoteLocks.has(id);
        }
        else if (this.remoteLocks.has(id) && this.remoteLocks.get(id).has(lockId)) {
            return true;
        }
        else {
            return false;
        }
    }
    isWeakLocked(id) {
        return this.weakLocks.has(id);
    }
    isLocked(id) {
        return (this.isLocalLocked(id) || this.isRemoteLocked(id) || this.isWeakLocked(id));
    }
    addLocalLocked(id, lockId) {
        if (!this.localLocks.has(id)) {
            this.localLocks.set(id, new Set());
        }
        this.localLocks.get(id).add(lockId);
    }
    addRemoteLocked(id, lockId) {
        if (!this.remoteLocks.has(id)) {
            this.remoteLocks.set(id, new Set());
        }
        this.remoteLocks.get(id).add(lockId);
    }
    addWeakLocked(id, lockId) {
        if (!this.weakLocks.has(id)) {
            this.weakLocks.set(id, new Set());
        }
        this.weakLocks.get(id).add(lockId);
    }
    removeLocalLocked(id, lockId) {
        if (this.localLocks.has(id)) {
            this.localLocks.get(id)?.delete(lockId);
            if (this.localLocks.get(id)?.size === 0) {
                this.localLocks.delete(id);
            }
        }
    }
    removeRemoteLocked(id, lockId) {
        if (this.remoteLocks.has(id)) {
            this.remoteLocks.get(id)?.delete(lockId);
            if (this.remoteLocks.get(id)?.size === 0) {
                this.remoteLocks.delete(id);
            }
        }
    }
    removeWeakLocked(id, lockId) {
        if (this.weakLocks.has(id)) {
            this.weakLocks.get(id)?.delete(lockId);
            if (this.weakLocks.get(id)?.size === 0) {
                this.weakLocks.delete(id);
            }
        }
    }
    addPrivate(id) {
        this.remotePrivateConnections.add(id);
    }
    removePrivate(id) {
        this.remotePrivateConnections.delete(id);
    }
    getPrivateConnections() {
        return this.remotePrivateConnections;
    }
    isPrivate(id) {
        return this.remotePrivateConnections.has(id);
    }
    clearAllLocks(id) {
        this.localLocks.delete(id);
        this.remoteLocks.delete(id);
        this.weakLocks.delete(id);
    }
    clear() {
        this.localLocks.clear();
        this.remoteLocks.clear();
        this.weakLocks.clear();
    }
}

/**
 * Retrieves a string representation of a node ID from a given peer descriptor.
 * If the peer descriptor is undefined, it returns 'unknown'.
 *
 * This function is intended for logging purposes only and should be removed
 * if the conditions outlined in the TODO comment are met, such as:
 * - Refactoring ConnectionManager to prevent early processing of handshake requests,
 *   ensuring this.localPeerDescriptor is never undefined (NET-1129).
 * - Ensuring the peerDescriptor of ManagedConnection is always available.
 * - Creating stricter types for incoming messages, such as message.sourceDescriptor
 *   or disconnectNotice.peerDescriptor.
 * - Guaranteeing that ManagedConnection#peerDescriptor is never undefined.
 *
 * @param peerDescriptor - The peer descriptor from which to derive the node ID,
 *                        or undefined if not available.
 * @returns A string representation of the node ID or 'unknown' if the peer descriptor is undefined.
 */
const getNodeIdOrUnknownFromPeerDescriptor = (peerDescriptor) => {
    if (peerDescriptor !== undefined) {
        return toNodeId(peerDescriptor);
    }
    else {
        return 'unknown';
    }
};

class Connection extends EventEmitter {
    connectionId;
    connectionType;
    peerDescriptor;
    constructor(connectionType) {
        super();
        this.connectionId = createRandomConnectionId();
        this.connectionType = connectionType;
    }
    setPeerDescriptor(peerDescriptor) {
        this.peerDescriptor = peerDescriptor;
    }
    getPeerDescriptor() {
        return this.peerDescriptor;
    }
}
const createRandomConnectionId = () => {
    return v4();
};

const logger$C = new Logger('ManagedConnection');
// ManagedConnection is a component used as a wrapper for IConnection after they have been successfully handshaked.
// Should only be used in the ConnectionManager.
class ManagedConnection extends EventEmitter {
    connection;
    connectionId;
    remotePeerDescriptor;
    lastUsedTimestamp = Date.now();
    replacedAsDuplicate = false;
    stopped = false;
    openedAt = Date.now();
    bytesSent = 0;
    bytesReceived = 0;
    messagesSent = 0;
    messagesReceived = 0;
    constructor(peerDescriptor, connection) {
        super();
        this.connectionId = createRandomConnectionId();
        this.connection = connection;
        connection.on('data', (bytes) => {
            this.lastUsedTimestamp = Date.now();
            this.messagesReceived += 1;
            this.bytesReceived += bytes.length;
            this.emit('managedData', bytes, this.getPeerDescriptor());
        });
        connection.on('disconnected', (gracefulLeave) => this.onDisconnected(gracefulLeave));
        this.lastUsedTimestamp = Date.now();
        this.remotePeerDescriptor = peerDescriptor;
    }
    onDisconnected(gracefulLeave) {
        logger$C.trace(getNodeIdOrUnknownFromPeerDescriptor(this.remotePeerDescriptor) + ' onDisconnected() ' + gracefulLeave);
        if (!this.replacedAsDuplicate) {
            this.emit('disconnected', gracefulLeave);
        }
        this.removeAllListeners();
    }
    // TODO: Can this be removed if ManagedConnections can never be duplicates?
    // Handle duplicates in the ConncetorFacade and no longer have PendingConnections in ConnectionManager
    replaceAsDuplicate() {
        logger$C.trace(getNodeIdOrUnknownFromPeerDescriptor(this.remotePeerDescriptor) + ' replaceAsDuplicate');
        this.replacedAsDuplicate = true;
    }
    send(data) {
        if (this.stopped) {
            throw new SendFailed('ManagedConnection is stopped');
        }
        this.lastUsedTimestamp = Date.now();
        this.messagesSent += 1;
        this.bytesSent += data.length;
        this.connection.send(data);
    }
    async close(gracefulLeave) {
        if (this.stopped) {
            return;
        }
        await this.connection.close(gracefulLeave);
        this.removeAllListeners();
    }
    getNodeId() {
        return toNodeId(this.remotePeerDescriptor);
    }
    getLastUsedTimestamp() {
        return this.lastUsedTimestamp;
    }
    getPeerDescriptor() {
        return this.remotePeerDescriptor;
    }
    getDiagnosticInfo() {
        return {
            remotePeerDescriptor: this.remotePeerDescriptor,
            lastUsedTimestamp: this.lastUsedTimestamp,
            replacedAsDuplicate: this.replacedAsDuplicate,
            stopped: this.stopped,
            openedAt: this.openedAt,
            bytesSent: this.bytesSent,
            bytesReceived: this.bytesReceived,
            messagesSent: this.messagesSent,
            messagesReceived: this.messagesReceived
            // Add connection type?
        };
    }
}

var ConnectionType;
(function (ConnectionType) {
    ConnectionType["WEBSOCKET_SERVER"] = "websocket-server";
    ConnectionType["WEBSOCKET_CLIENT"] = "websocket-client";
    ConnectionType["WEBRTC"] = "webrtc";
    ConnectionType["SIMULATOR_SERVER"] = "simulator-server";
    ConnectionType["SIMULATOR_CLIENT"] = "simulator-client";
})(ConnectionType || (ConnectionType = {}));

// IPv4 private address ranges as specified by RFC 1918
// and private loopback addresses
const IPv4PrivateRanges = [
    '10.0.0.0/8',
    '172.16.0.0/12',
    '192.168.0.0/16',
    '127.0.0.0/8'
].map((a) => ipaddr.parseCIDR(a));
function isPrivateIPv4(address) {
    if (ipaddr.IPv4.isValid(address)) {
        const ip = ipaddr.IPv4.parse(address);
        for (const range of IPv4PrivateRanges) {
            if (ip.match(range)) {
                return true;
            }
        }
    }
    return false;
}
function getAddressFromIceCandidate(candidate) {
    const fields = candidate.split(' ').filter((field) => field.length > 0);
    return fields.length >= 5 && ipaddr.isValid(fields[4]) ? fields[4] : undefined;
}

const canOpenConnectionFromBrowser = (websocketServer) => {
    const hasPrivateAddress = ((websocketServer.host === 'localhost') || isPrivateIPv4(websocketServer.host));
    return websocketServer.tls || hasPrivateAddress;
};
const expectedConnectionType = (localPeerDescriptor, remotePeerDescriptor) => {
    if (remotePeerDescriptor.websocket
        && (localPeerDescriptor.type !== NodeType.BROWSER || canOpenConnectionFromBrowser(remotePeerDescriptor.websocket))) {
        return ConnectionType.WEBSOCKET_CLIENT;
    }
    else if (localPeerDescriptor.websocket
        && (remotePeerDescriptor.type !== NodeType.BROWSER || canOpenConnectionFromBrowser(localPeerDescriptor.websocket))) {
        return ConnectionType.WEBSOCKET_SERVER;
    }
    return ConnectionType.WEBRTC;
};

// Should connect directly to the server, timeout can be low
const WEBSOCKET_CLIENT_TIMEOUT = 5000;
// Requires a WebsocketConnectionRequest to be routed to the client before the connection can be opened
// takes a little bit longer than WEBSOCKET_CLIENT
const WEBSOCKET_SERVER_TIMEOUT = 7500;
// WebRTC connections require lots of signalling to open and might take a longer time.
const WEBRTC_TIMEOUT = 10000;
// default timeout for existing connections
const EXISTING_CONNECTION_TIMEOUT = 5000;
const getTimeout = (localPeerDescriptor, remotePeerDescriptor) => {
    const connectionType = expectedConnectionType(localPeerDescriptor, remotePeerDescriptor);
    if (connectionType === ConnectionType.WEBSOCKET_CLIENT) {
        return WEBSOCKET_CLIENT_TIMEOUT;
    }
    else if (connectionType === ConnectionType.WEBSOCKET_SERVER) {
        return WEBSOCKET_SERVER_TIMEOUT;
    }
    else if (connectionType === ConnectionType.WEBRTC) {
        return WEBRTC_TIMEOUT;
    }
    return WEBRTC_TIMEOUT;
};
class RpcRemote {
    localPeerDescriptor;
    remotePeerDescriptor;
    client;
    timeout;
    constructor(localPeerDescriptor, remotePeerDescriptor, rpcCommunicator, 
    // eslint-disable-next-line @typescript-eslint/prefer-function-type
    clientClass, timeout) {
        this.localPeerDescriptor = localPeerDescriptor;
        this.remotePeerDescriptor = remotePeerDescriptor;
        this.client = toProtoRpcClient(new clientClass(rpcCommunicator.getRpcClientTransport()));
        this.timeout = timeout;
    }
    getPeerDescriptor() {
        return this.remotePeerDescriptor;
    }
    getLocalPeerDescriptor() {
        return this.localPeerDescriptor;
    }
    getClient() {
        return this.client;
    }
    formDhtRpcOptions(opts) {
        return {
            sourceDescriptor: this.localPeerDescriptor,
            targetDescriptor: this.remotePeerDescriptor,
            timeout: this.timeout ?? getTimeout(this.localPeerDescriptor, this.remotePeerDescriptor),
            ...opts
        };
    }
}

const logger$B = new Logger('ConnectionLockRpcRemote');
class ConnectionLockRpcRemote extends RpcRemote {
    async lockRequest(lockId) {
        logger$B.trace(`Requesting locked connection to ${toNodeId(this.getPeerDescriptor())}`);
        const request = {
            lockId
        };
        const options = this.formDhtRpcOptions();
        try {
            const res = await this.getClient().lockRequest(request, options);
            return res.accepted;
        }
        catch (err) {
            logger$B.debug('Connection lock rejected', { err });
            return false;
        }
    }
    unlockRequest(lockId) {
        logger$B.trace(`Requesting connection to be unlocked from ${toNodeId(this.getPeerDescriptor())}`);
        const request = {
            lockId
        };
        const options = this.formDhtRpcOptions({
            notification: true
        });
        this.getClient().unlockRequest(request, options).catch((_e) => {
            logger$B.trace('failed to send unlockRequest');
        });
    }
    async gracefulDisconnect(disconnectMode) {
        logger$B.trace(`Notifying a graceful disconnect to ${toNodeId(this.getPeerDescriptor())}`);
        const request = {
            disconnectMode
        };
        const options = this.formDhtRpcOptions({
            connect: false,
            sendIfStopped: true,
            timeout: 2000 // TODO use options option or named constant?
        });
        await this.getClient().gracefulDisconnect(request, options);
    }
    async setPrivate(isPrivate) {
        logger$B.trace(`Setting isPrivate: ${isPrivate} for ${toNodeId(this.getPeerDescriptor())}`);
        const request = {
            isPrivate
        };
        const options = this.formDhtRpcOptions({
            connect: false,
            notification: true
        });
        await this.getClient().setPrivate(request, options);
    }
}

const logger$A = new Logger('ConnectionLockRpcLocal');
class ConnectionLockRpcLocal {
    options;
    constructor(options) {
        this.options = options;
    }
    async lockRequest(lockRequest, context) {
        const senderPeerDescriptor = context.incomingSourceDescriptor;
        if (areEqualPeerDescriptors(senderPeerDescriptor, this.options.getLocalPeerDescriptor())) {
            const response = {
                accepted: false
            };
            return response;
        }
        const remoteNodeId = toNodeId(senderPeerDescriptor);
        this.options.addRemoteLocked(remoteNodeId, lockRequest.lockId);
        const response = {
            accepted: true
        };
        return response;
    }
    async unlockRequest(unlockRequest, context) {
        const senderPeerDescriptor = context.incomingSourceDescriptor;
        const nodeId = toNodeId(senderPeerDescriptor);
        this.options.removeRemoteLocked(nodeId, unlockRequest.lockId);
        return {};
    }
    async gracefulDisconnect(disconnectNotice, context) {
        const senderPeerDescriptor = context.incomingSourceDescriptor;
        logger$A.trace(getNodeIdOrUnknownFromPeerDescriptor(senderPeerDescriptor) + ' received gracefulDisconnect notice');
        if (disconnectNotice.disconnectMode === DisconnectMode.LEAVING) {
            await this.options.closeConnection(senderPeerDescriptor, true, 'graceful leave notified');
        }
        else {
            await this.options.closeConnection(senderPeerDescriptor, false, 'graceful disconnect notified');
        }
        return {};
    }
    async setPrivate(request, context) {
        const senderPeerDescriptor = context.incomingSourceDescriptor;
        const senderId = toNodeId(senderPeerDescriptor);
        this.options.setPrivate(senderId, request.isPrivate);
        return {};
    }
}

const getOfferer = (localNodeId, remoteNodeId) => {
    return getOfferingHash(localNodeId + ',' + remoteNodeId) < getOfferingHash(remoteNodeId + ',' + localNodeId)
        ? 'local'
        : 'remote';
};
const getOfferingHash = (idPair) => {
    return computeMd5(idPair).readInt32LE(0);
};

class OutputBuffer {
    buffer = [];
    deferredPromise = new Defer();
    push(message) {
        this.buffer.push(message);
        return this.deferredPromise;
    }
    getBuffer() {
        return this.buffer;
    }
    resolve() {
        this.buffer.length = 0;
        this.deferredPromise.resolve();
    }
    reject() {
        this.buffer.length = 0;
        this.deferredPromise.reject(new SendFailed('Could not send buffered messages'));
    }
}

var NatType;
(function (NatType) {
    NatType["OPEN_INTERNET"] = "open_internet";
    NatType["UNKNOWN"] = "unknown";
})(NatType || (NatType = {}));
const logger$z = new Logger('ConnectionManager');
var ConnectionManagerState;
(function (ConnectionManagerState) {
    ConnectionManagerState["IDLE"] = "idle";
    ConnectionManagerState["RUNNING"] = "running";
    ConnectionManagerState["STOPPING"] = "stopping";
    ConnectionManagerState["STOPPED"] = "stopped";
})(ConnectionManagerState || (ConnectionManagerState = {}));
const INTERNAL_SERVICE_ID = 'system/connection-manager';
class ConnectionManager extends EventEmitter {
    options;
    metricsContext;
    // TODO use options option or named constant?
    duplicateMessageDetector = new DuplicateDetector(10000);
    metrics;
    locks = new ConnectionLockStates();
    endpoints = new Map();
    connectorFacade;
    rpcCommunicator;
    disconnectorIntervalRef;
    state = ConnectionManagerState.IDLE;
    privateClientMode = false;
    constructor(options) {
        super();
        this.options = options;
        this.onData = this.onData.bind(this);
        this.send = this.send.bind(this);
        this.onNewConnection = this.onNewConnection.bind(this);
        this.metricsContext = this.options.metricsContext ?? new MetricsContext();
        this.metrics = {
            sendMessagesPerSecond: new RateMetric(),
            sendBytesPerSecond: new RateMetric(),
            receiveMessagesPerSecond: new RateMetric(),
            receiveBytesPerSecond: new RateMetric(),
            connectionAverageCount: new LevelMetric(0),
            connectionTotalFailureCount: new CountMetric()
        };
        this.metricsContext.addMetrics('node', this.metrics);
        this.connectorFacade = this.options.createConnectorFacade();
        this.send = this.send.bind(this);
        this.rpcCommunicator = new RoutingRpcCommunicator(INTERNAL_SERVICE_ID, this.send, {
            rpcRequestTimeout: 10000 // TODO use options option or named constant?
        });
        const lockRpcLocal = new ConnectionLockRpcLocal({
            addRemoteLocked: (id, lockId) => this.locks.addRemoteLocked(id, lockId),
            removeRemoteLocked: (id, lockId) => this.locks.removeRemoteLocked(id, lockId),
            closeConnection: (peerDescriptor, gracefulLeave, reason) => this.closeConnection(peerDescriptor, gracefulLeave, reason),
            getLocalPeerDescriptor: () => this.getLocalPeerDescriptor(),
            setPrivate: (id, isPrivate) => {
                if (!this.options.allowIncomingPrivateConnections) {
                    logger$z.debug(`node ${id} attemted to set a connection as private, but it is not allowed`);
                    return;
                }
                if (isPrivate) {
                    this.locks.addPrivate(id);
                }
                else {
                    this.locks.removePrivate(id);
                }
            }
        });
        this.rpcCommunicator.registerRpcMethod(LockRequest, LockResponse, 'lockRequest', (req, context) => lockRpcLocal.lockRequest(req, context));
        this.rpcCommunicator.registerRpcNotification(UnlockRequest, 'unlockRequest', (req, context) => lockRpcLocal.unlockRequest(req, context));
        this.rpcCommunicator.registerRpcNotification(DisconnectNotice, 'gracefulDisconnect', (req, context) => lockRpcLocal.gracefulDisconnect(req, context));
        this.rpcCommunicator.registerRpcNotification(SetPrivateRequest, 'setPrivate', (req, context) => lockRpcLocal.setPrivate(req, context));
    }
    /*
     * Removes connections if there are more than maxConnections: in that case we remove unlocked connections
     * which hasn't been used within maxIdleTime.
     */
    garbageCollectConnections(maxConnections, maxIdleTime) {
        if (this.endpoints.size <= maxConnections) {
            return;
        }
        const disconnectionCandidates = new SortedContactList({
            referenceId: toNodeId(this.getLocalPeerDescriptor()),
            maxSize: 100000, // TODO use options option or named constant?
            allowToContainReferenceId: false
        });
        this.endpoints.forEach((endpoint) => {
            if (endpoint.connected) {
                const connection = endpoint.connection;
                const nodeId = connection.getNodeId();
                if (!this.locks.isLocked(nodeId) && !this.locks.isPrivate(nodeId) && Date.now() - connection.getLastUsedTimestamp() > maxIdleTime) {
                    logger$z.trace('disconnecting in timeout interval: ' + getNodeIdOrUnknownFromPeerDescriptor(connection.getPeerDescriptor()));
                    disconnectionCandidates.addContact(connection);
                }
            }
        });
        const disconnectables = disconnectionCandidates.getFurthestContacts(this.endpoints.size - maxConnections);
        for (const disconnectable of disconnectables) {
            const peerDescriptor = disconnectable.getPeerDescriptor();
            logger$z.trace('garbageCollecting ' + toNodeId(peerDescriptor));
            this.gracefullyDisconnectAsync(peerDescriptor, DisconnectMode.NORMAL).catch((_e) => { });
        }
    }
    async start() {
        if (this.state === ConnectionManagerState.RUNNING || this.state === ConnectionManagerState.STOPPED) {
            throw new CouldNotStart(`Cannot start already ${this.state} module`);
        }
        this.state = ConnectionManagerState.RUNNING;
        logger$z.trace(`Starting ConnectionManager...`);
        await this.connectorFacade.start((connection) => this.onNewConnection(connection), (nodeId) => this.hasConnection(nodeId), this);
        // Garbage collection of connections
        this.disconnectorIntervalRef = setInterval(() => {
            logger$z.trace('disconnectorInterval');
            const LAST_USED_LIMIT = 20000;
            this.garbageCollectConnections(this.options.maxConnections ?? 80, LAST_USED_LIMIT);
        }, 5000); // TODO use options option or named constant?
    }
    async stop() {
        if (this.state === ConnectionManagerState.STOPPED || this.state === ConnectionManagerState.STOPPING) {
            return;
        }
        this.state = ConnectionManagerState.STOPPING;
        logger$z.trace(`Stopping ConnectionManager`);
        if (this.disconnectorIntervalRef) {
            clearInterval(this.disconnectorIntervalRef);
        }
        await Promise.all(Array.from(this.endpoints.values()).map(async (endpoint) => {
            if (endpoint.connected) {
                try {
                    await this.gracefullyDisconnectAsync(endpoint.connection.getPeerDescriptor(), DisconnectMode.LEAVING);
                }
                catch (e) {
                    logger$z.error(e);
                }
            }
            else {
                const connection = endpoint.connection;
                logger$z.trace('handshake of connection not completed, force-closing');
                // TODO use options option or named constant?
                const eventReceived = waitForEvent(connection, 'disconnected', 2000);
                // TODO should we have some handling for this floating promise?
                connection.close(true);
                try {
                    await eventReceived;
                    logger$z.trace('resolving after receiving disconnected event from non-handshaked connection');
                }
                catch (e) {
                    endpoint.buffer.reject();
                    logger$z.trace('force-closing non-handshaked connection timed out ' + e);
                }
            }
        }));
        await this.connectorFacade.stop();
        this.state = ConnectionManagerState.STOPPED;
        this.rpcCommunicator.stop();
        this.duplicateMessageDetector.clear();
        this.locks.clear();
        this.removeAllListeners();
    }
    getLocalLockedConnectionCount() {
        return this.locks.getLocalLockedConnectionCount();
    }
    getRemoteLockedConnectionCount() {
        return this.locks.getRemoteLockedConnectionCount();
    }
    getWeakLockedConnectionCount() {
        return this.locks.getWeakLockedConnectionCount();
    }
    async send(message, opts = DEFAULT_SEND_OPTIONS) {
        if ((this.state === ConnectionManagerState.STOPPED || this.state === ConnectionManagerState.STOPPING) && !opts.sendIfStopped) {
            return;
        }
        const peerDescriptor = message.targetDescriptor;
        if (this.isConnectionToSelf(peerDescriptor)) {
            throw new CannotConnectToSelf('Cannot send to self');
        }
        const nodeId = toNodeId(peerDescriptor);
        logger$z.trace(`Sending message to: ${nodeId}`);
        message = {
            ...message,
            sourceDescriptor: this.getLocalPeerDescriptor()
        };
        let connection = this.endpoints.get(nodeId)?.connection;
        if (!connection && opts.connect) {
            connection = this.connectorFacade.createConnection(peerDescriptor);
            this.onNewConnection(connection);
        }
        else if (!connection || (connection && !this.endpoints.get(nodeId).connected && !opts.connect)) {
            throw new SendFailed('No connection to target, connect flag is false');
        }
        const binary = Message.toBinary(message);
        this.metrics.sendBytesPerSecond.record(binary.byteLength);
        this.metrics.sendMessagesPerSecond.record(1);
        if (this.endpoints.get(nodeId).connected) {
            connection.send(binary);
        }
        else if (opts.bufferWhileConnecting) {
            return this.endpoints.get(nodeId).buffer.push(binary);
        }
    }
    isConnectionToSelf(peerDescriptor) {
        return areEqualPeerDescriptors(peerDescriptor, this.getLocalPeerDescriptor()) || this.isOwnWebsocketServer(peerDescriptor);
    }
    isOwnWebsocketServer(peerDescriptor) {
        const localPeerDescriptor = this.getLocalPeerDescriptor();
        if ((peerDescriptor.websocket !== undefined) && (localPeerDescriptor.websocket !== undefined)) {
            return ((peerDescriptor.websocket.port === localPeerDescriptor.websocket.port)
                && (peerDescriptor.websocket.host === localPeerDescriptor.websocket.host));
        }
        else {
            return false;
        }
    }
    getLocalPeerDescriptor() {
        return this.connectorFacade.getLocalPeerDescriptor();
    }
    hasConnection(nodeId) {
        // TODO if we remove filtering in getConnections, this can just be this.connection.has(nodeId)
        return this.getConnections().some((c) => toNodeId(c) == nodeId);
    }
    getConnectionCount() {
        // TODO if we remove filtering in getConnections, this can just be this.connection.length
        return this.getConnections().length;
    }
    hasLocalLockedConnection(nodeId) {
        return this.locks.isLocalLocked(nodeId);
    }
    hasRemoteLockedConnection(nodeId) {
        return this.locks.isRemoteLocked(nodeId);
    }
    handleMessage(message) {
        const messageType = message.body.oneofKind;
        logger$z.trace('Received message of type ' + messageType);
        if (messageType !== 'rpcMessage') {
            logger$z.trace('Filtered out non-RPC message of type ' + messageType);
            return;
        }
        if (this.duplicateMessageDetector.isMostLikelyDuplicate(message.messageId)) {
            logger$z.trace('handleMessage filtered duplicate ' + toNodeId(message.sourceDescriptor)
                + ' ' + message.serviceId + ' ' + message.messageId);
            return;
        }
        this.duplicateMessageDetector.add(message.messageId);
        if (message.serviceId === INTERNAL_SERVICE_ID) {
            this.rpcCommunicator?.handleMessageFromPeer(message);
        }
        else {
            logger$z.trace('emit "message" ' + toNodeId(message.sourceDescriptor)
                + ' ' + message.serviceId + ' ' + message.messageId);
            this.emit('message', message);
        }
    }
    onData(data, peerDescriptor) {
        if (this.state === ConnectionManagerState.STOPPED) {
            return;
        }
        this.metrics.receiveBytesPerSecond.record(data.byteLength);
        this.metrics.receiveMessagesPerSecond.record(1);
        let message;
        try {
            message = Message.fromBinary(data);
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            logger$z.debug(`Parsing incoming data into Message failed: ${e}`);
            return;
        }
        message.sourceDescriptor = peerDescriptor;
        try {
            this.handleMessage(message);
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            logger$z.debug(`Handling incoming data failed: ${e}`);
        }
    }
    onConnected(peerDescriptor, connection) {
        const managedConnection = new ManagedConnection(peerDescriptor, connection);
        managedConnection.on('managedData', this.onData);
        managedConnection.once('disconnected', (gracefulLeave) => this.onDisconnected(peerDescriptor, gracefulLeave));
        const nodeId = toNodeId(peerDescriptor);
        const endpoint = this.endpoints.get(nodeId);
        const outputBuffer = endpoint.buffer;
        const pendingConnection = endpoint.connection;
        const buffer = outputBuffer.getBuffer();
        while (buffer.length > 0) {
            logger$z.trace('emptying buffer');
            managedConnection.send(buffer.shift());
        }
        outputBuffer.resolve();
        pendingConnection.destroy();
        this.endpoints.set(nodeId, {
            connected: true,
            connection: managedConnection
        });
        if (this.privateClientMode) {
            this.setPrivateForConnection(peerDescriptor, this.privateClientMode).catch(() => { });
        }
        this.emit('connected', peerDescriptor);
        this.onConnectionCountChange();
    }
    onDisconnected(peerDescriptor, gracefulLeave) {
        const nodeId = toNodeId(peerDescriptor);
        logger$z.trace(nodeId + ' onDisconnected() gracefulLeave: ' + gracefulLeave);
        const endpoint = this.endpoints.get(nodeId);
        if (endpoint) {
            this.locks.clearAllLocks(nodeId);
            if (endpoint.connected === false) {
                endpoint.buffer.reject();
            }
            this.endpoints.delete(nodeId);
            logger$z.trace(nodeId + ' deleted connection in onDisconnected() gracefulLeave: ' + gracefulLeave);
            this.emit('disconnected', peerDescriptor, gracefulLeave);
            this.onConnectionCountChange();
        }
    }
    onNewConnection(connection) {
        if (this.state === ConnectionManagerState.STOPPED) {
            return false;
        }
        logger$z.trace('onNewConnection()');
        if (!this.acceptNewConnection(connection)) {
            return false;
        }
        connection.once('connected', (peerDescriptor, connection) => this.onConnected(peerDescriptor, connection));
        connection.once('disconnected', (gracefulLeave) => this.onDisconnected(connection.getPeerDescriptor(), gracefulLeave));
        return true;
    }
    acceptNewConnection(newConnection) {
        const nodeId = toNodeId(newConnection.getPeerDescriptor());
        logger$z.trace(nodeId + ' acceptNewConnection()');
        if (this.endpoints.has(nodeId)) {
            if (getOfferer(toNodeId(this.getLocalPeerDescriptor()), nodeId) === 'remote') {
                let buffer;
                const endpoint = this.endpoints.get(nodeId);
                // This is a rare occurance but it does happen from time to time.
                // Could be related to WS client connections not realizing that they have been disconnected.
                // Makes refactoring duplicate connection handling to the connectors very difficult. 
                if (this.endpoints.get(nodeId).connected) {
                    logger$z.debug('replacing connected connection', { nodeId });
                    buffer = new OutputBuffer();
                }
                else {
                    buffer = endpoint.buffer;
                }
                const oldConnection = endpoint.connection;
                logger$z.trace('replaced: ' + nodeId);
                oldConnection.replaceAsDuplicate();
                this.endpoints.set(nodeId, { connected: false, connection: newConnection, buffer: buffer });
                return true;
            }
            else {
                return false;
            }
        }
        logger$z.trace(nodeId + ' added to connections at acceptNewConnection');
        this.endpoints.set(nodeId, {
            connected: false,
            buffer: new OutputBuffer(),
            connection: newConnection
        });
        return true;
    }
    async closeConnection(peerDescriptor, gracefulLeave, reason) {
        const nodeId = toNodeId(peerDescriptor);
        logger$z.trace(nodeId + ' ' + 'closeConnection() ' + reason);
        this.locks.clearAllLocks(nodeId);
        if (this.endpoints.has(nodeId)) {
            const connectionToClose = this.endpoints.get(nodeId).connection;
            await connectionToClose.close(gracefulLeave);
        }
        else {
            logger$z.trace(nodeId + ' ' + 'closeConnection() this.endpoints did not have the id');
            this.emit('disconnected', peerDescriptor, false);
        }
    }
    lockConnection(targetDescriptor, lockId) {
        if (this.state === ConnectionManagerState.STOPPED || areEqualPeerDescriptors(targetDescriptor, this.getLocalPeerDescriptor())) {
            return;
        }
        const nodeId = toNodeId(targetDescriptor);
        const rpcRemote = new ConnectionLockRpcRemote(this.getLocalPeerDescriptor(), targetDescriptor, this.rpcCommunicator, ConnectionLockRpcClient);
        this.locks.addLocalLocked(nodeId, lockId);
        rpcRemote.lockRequest(lockId)
            .then((_accepted) => logger$z.trace('LockRequest successful'))
            .catch((err) => { logger$z.debug(err); });
    }
    unlockConnection(targetDescriptor, lockId) {
        if (this.state === ConnectionManagerState.STOPPED || areEqualPeerDescriptors(targetDescriptor, this.getLocalPeerDescriptor())) {
            return;
        }
        const nodeId = toNodeId(targetDescriptor);
        this.locks.removeLocalLocked(nodeId, lockId);
        const rpcRemote = new ConnectionLockRpcRemote(this.getLocalPeerDescriptor(), targetDescriptor, this.rpcCommunicator, ConnectionLockRpcClient);
        if (this.endpoints.has(nodeId)) {
            rpcRemote.unlockRequest(lockId);
        }
    }
    weakLockConnection(nodeId, lockId) {
        if (this.state === ConnectionManagerState.STOPPED || (nodeId === toNodeId(this.getLocalPeerDescriptor()))) {
            return;
        }
        this.locks.addWeakLocked(nodeId, lockId);
    }
    weakUnlockConnection(nodeId, lockId) {
        if (this.state === ConnectionManagerState.STOPPED || (nodeId === toNodeId(this.getLocalPeerDescriptor()))) {
            return;
        }
        this.locks.removeWeakLocked(nodeId, lockId);
    }
    async enablePrivateClientMode() {
        this.privateClientMode = true;
        await Promise.all(this.getConnectedEndpoints().map((endpoint) => {
            const peerDescription = endpoint.connection.getPeerDescriptor();
            return this.setPrivateForConnection(peerDescription, true);
        }));
    }
    async disablePrivateClientMode() {
        this.privateClientMode = false;
        await Promise.all(this.getConnectedEndpoints().map((endpoint) => {
            const peerDescription = endpoint.connection.getPeerDescriptor();
            return this.setPrivateForConnection(peerDescription, false);
        }));
    }
    getConnectedEndpoints() {
        return Array.from(this.endpoints.values()).filter((endpoint) => endpoint.connected);
    }
    isPrivateClientMode() {
        return this.privateClientMode;
    }
    async setPrivateForConnection(targetDescriptor, isPrivate) {
        const rpcRemote = new ConnectionLockRpcRemote(this.getLocalPeerDescriptor(), targetDescriptor, this.rpcCommunicator, ConnectionLockRpcClient);
        await rpcRemote.setPrivate(isPrivate);
    }
    async gracefullyDisconnectAsync(targetDescriptor, disconnectMode) {
        const endpoint = this.endpoints.get(toNodeId(targetDescriptor));
        if (!endpoint) {
            logger$z.debug('gracefullyDisconnectedAsync() tried on a non-existing connection');
            return;
        }
        if (endpoint.connected) {
            const connection = endpoint.connection;
            const promise = new Promise((resolve, _reject) => {
                // TODO use options option or named constant?
                // eslint-disable-next-line promise/catch-or-return
                waitForEvent(connection, 'disconnected', 2000).then(() => {
                    logger$z.trace('disconnected event received in gracefullyDisconnectAsync()');
                })
                    .catch((e) => {
                    logger$z.trace('force-closing connection after timeout ' + e);
                    // TODO should we have some handling for this floating promise?
                    connection.close(true);
                })
                    .finally(() => {
                    logger$z.trace('resolving after receiving disconnected event');
                    resolve();
                });
            });
            await Promise.all([
                promise,
                this.doGracefullyDisconnectAsync(targetDescriptor, disconnectMode)
            ]);
        }
        else {
            endpoint.connection.close(true);
        }
    }
    async doGracefullyDisconnectAsync(targetDescriptor, disconnectMode) {
        const nodeId = toNodeId(targetDescriptor);
        logger$z.trace(nodeId + ' gracefullyDisconnectAsync()');
        const rpcRemote = new ConnectionLockRpcRemote(this.getLocalPeerDescriptor(), targetDescriptor, this.rpcCommunicator, ConnectionLockRpcClient);
        try {
            await rpcRemote.gracefulDisconnect(disconnectMode);
        }
        catch (ex) {
            logger$z.trace(nodeId + ' remote.gracefulDisconnect() failed' + ex);
        }
    }
    getConnections() {
        return Array.from(this.endpoints.values())
            .map((endpoint) => endpoint)
            // TODO is this filtering needed? (if it is, should we do the same filtering e.g.
            // in getConnection() or in other methods which access this.endpoints directly?)
            .filter((endpoint) => endpoint.connected && !this.locks.isPrivate(toNodeId(endpoint.connection.getPeerDescriptor())))
            .map((endpoint) => endpoint.connection.getPeerDescriptor());
    }
    onConnectionCountChange() {
        this.metrics.connectionAverageCount.record(this.endpoints.size);
    }
    getDiagnosticInfo() {
        const managedConnections = Array.from(this.endpoints.values())
            .filter((endpoint) => endpoint.connected)
            .map((endpoint) => endpoint.connection);
        return {
            connections: managedConnections.map((connection) => connection.getDiagnosticInfo()),
            connectionCount: this.endpoints.size
        };
    }
}

const protoClasses = [
    ClosestPeersRequest,
    ClosestPeersResponse,
    RecursiveOperationRequest,
    RecursiveOperationResponse,
    PingRequest,
    PingResponse,
    LeaveNotice,
    PeerDescriptor,
    ConnectivityMethod,
    DisconnectNotice,
    RouteMessageWrapper,
    RouteMessageAck,
    ConnectivityRequest,
    ConnectivityResponse,
    HandshakeRequest,
    HandshakeResponse,
    Message,
    WebsocketConnectionRequest,
    WebrtcConnectionRequest,
    RtcOffer,
    RtcAnswer,
    IceCandidate,
    LockRequest,
    UnlockRequest,
    LockResponse
];

const typeRegistry = protoClasses.concat(protoClasses$1);
function protoToString(protoObj, objectType) {
    let ret = '';
    try {
        ret = objectType.toJsonString(protoObj, {
            typeRegistry
        });
    }
    catch (_e) {
        ret = '[type not in type registry]';
    }
    return ret;
}

const logger$y = new Logger('SimulatorConnection');
class SimulatorConnection extends Connection {
    stopped = false;
    localPeerDescriptor;
    targetPeerDescriptor;
    simulator;
    constructor(localPeerDescriptor, targetPeerDescriptor, connectionType, simulator) {
        super(connectionType);
        this.localPeerDescriptor = localPeerDescriptor;
        this.setPeerDescriptor(targetPeerDescriptor);
        this.targetPeerDescriptor = targetPeerDescriptor;
        this.connectionType = connectionType;
        this.simulator = simulator;
        this.send = this.send.bind(this);
        this.close = this.close.bind(this);
        this.connect = this.connect.bind(this);
        this.handleIncomingData = this.handleIncomingData.bind(this);
        this.handleIncomingDisconnection = this.handleIncomingDisconnection.bind(this);
        this.destroy = this.destroy.bind(this);
        this.doDisconnect = this.doDisconnect.bind(this);
    }
    send(data) {
        logger$y.trace('send()');
        if (!this.stopped) {
            this.simulator.send(this, data);
        }
        else {
            const localNodeId = toNodeId(this.localPeerDescriptor);
            const targetNodeId = toNodeId(this.targetPeerDescriptor);
            logger$y.error(localNodeId + ', ' + targetNodeId + 'tried to send() on a stopped connection');
        }
    }
    async close(gracefulLeave) {
        const localNodeId = toNodeId(this.localPeerDescriptor);
        const targetNodeId = toNodeId(this.targetPeerDescriptor);
        logger$y.trace(localNodeId + ', ' + targetNodeId + ' close()');
        if (!this.stopped) {
            logger$y.trace(localNodeId + ', ' + targetNodeId + ' close() not stopped');
            this.stopped = true;
            try {
                logger$y.trace(localNodeId + ', ' + targetNodeId + ' close() calling simulator.disconnect()');
                this.simulator.close(this);
                logger$y.trace(localNodeId + ', ' + targetNodeId + ' close() simulator.disconnect returned');
            }
            catch (e) {
                logger$y.trace(localNodeId + ', ' + targetNodeId + 'close aborted' + e);
            }
            finally {
                logger$y.trace(localNodeId + ', ' + targetNodeId + ' calling this.doDisconnect');
                this.doDisconnect(gracefulLeave);
            }
        }
        else {
            logger$y.trace(localNodeId + ', ' + targetNodeId + ' close() tried to close a stopped connection');
        }
    }
    connect() {
        if (!this.stopped) {
            logger$y.trace('connect() called');
            this.simulator.connect(this, this.targetPeerDescriptor, (error) => {
                if (error !== undefined) {
                    logger$y.trace(error);
                    this.doDisconnect(false);
                }
                else {
                    this.emit('connected');
                }
            });
        }
        else {
            logger$y.trace('tried to connect() a stopped connection');
        }
    }
    handleIncomingData(data) {
        if (!this.stopped) {
            logger$y.trace('handleIncomingData() ' + protoToString(Message.fromBinary(data), Message));
            this.emit('data', data);
        }
        else {
            logger$y.trace('tried to call handleIncomingData() a stopped connection');
        }
    }
    handleIncomingDisconnection() {
        if (!this.stopped) {
            const localNodeId = toNodeId(this.localPeerDescriptor);
            logger$y.trace(localNodeId + ' handleIncomingDisconnection()');
            this.stopped = true;
            this.doDisconnect(false);
        }
        else {
            logger$y.trace('tried to call handleIncomingDisconnection() a stopped connection');
        }
    }
    destroy() {
        const localNodeId = toNodeId(this.localPeerDescriptor);
        if (!this.stopped) {
            logger$y.trace(localNodeId + ' destroy()');
            this.removeAllListeners();
            this.close(false).catch((_e) => { });
        }
        else {
            logger$y.trace(localNodeId + ' tried to call destroy() a stopped connection');
        }
    }
    doDisconnect(gracefulLeave) {
        const localNodeId = toNodeId(this.localPeerDescriptor);
        const targetNodeId = toNodeId(this.targetPeerDescriptor);
        logger$y.trace(localNodeId + ' doDisconnect()');
        this.stopped = true;
        logger$y.trace(localNodeId + ', ' + targetNodeId + ' doDisconnect emitting');
        this.emit('disconnected', gracefulLeave);
    }
}

const LOCAL_PROTOCOL_VERSION = '1.1';
/*
 * When two nodes negotiate whether they are compatible or not, it is up to the
 * newer version to decide if it supports the old version or not.
 *
 * The older version assumes optimistically that it may be supported by the newer
 * version. It can't know for sure, but the other node will tell if it is not
 * supported (e.g. rejecting the handshake with UNSUPPORTED_PROTOCOL_VERSION error).
 */
const isMaybeSupportedProtocolVersion = (remoteVersion) => {
    const localMajor = parseVersion(LOCAL_PROTOCOL_VERSION).major;
    const remoteMajor = parseVersion(remoteVersion)?.major;
    if ((remoteMajor === undefined) || (remoteMajor < localMajor)) {
        return false;
    }
    else {
        // TODO implement proper checking when there are new protocol versions
        return true;
    }
};
const parseVersion = (version) => {
    const parts = version.split('.');
    if (parts.length === 2) {
        const values = parts.map((p) => Number(p));
        if (!values.some((v) => isNaN(v))) {
            return { major: values[0], minor: values[1] };
        }
    }
    else {
        return undefined;
    }
};

var version = "103.3.1";

const logger$x = new Logger('Handshaker');
// Optimally the Outgoing and Incoming Handshakers could be their own separate classes
// However, in cases where the PeerDescriptor of the other end of the connection can be known
// only after a HandshakeRequest a base Handshaker class is needed as the IncomingHandshaker currently
// requires knowledge of the other ends PeerDescriptor. (Note PendingConnection requires the remote PeerDescriptor)
const createOutgoingHandshaker = (localPeerDescriptor, pendingConnection, connection, targetPeerDescriptor) => {
    const handshaker = new Handshaker(localPeerDescriptor, connection);
    const stopHandshaker = () => {
        handshaker.stop();
        connection.off('disconnected', disconnectedListener);
        connection.off('connected', connectedListener);
        handshaker.off('handshakeCompleted', handshakeCompletedListener);
        handshaker.off('handshakeFailed', handshakeFailedListener);
        pendingConnection.off('disconnected', managedConnectionDisconnectedListener);
    };
    const handshakeFailedListener = (error) => {
        if (error === HandshakeError.INVALID_TARGET_PEER_DESCRIPTOR || error === HandshakeError.UNSUPPORTED_PROTOCOL_VERSION) {
            pendingConnection.close(false);
            stopHandshaker();
        }
    };
    const handshakeCompletedListener = (peerDescriptor) => {
        logger$x.trace('handshake completed for outgoing connection, ' + toNodeId(peerDescriptor));
        pendingConnection.onHandshakeCompleted(connection);
        stopHandshaker();
    };
    const connectedListener = () => handshaker.sendHandshakeRequest(targetPeerDescriptor);
    const disconnectedListener = (graceful) => {
        pendingConnection.close(graceful);
        stopHandshaker();
    };
    const managedConnectionDisconnectedListener = () => {
        connection.close(false);
        stopHandshaker();
    };
    handshaker.once('handshakeFailed', handshakeFailedListener);
    handshaker.once('handshakeCompleted', handshakeCompletedListener);
    connection.once('connected', connectedListener);
    connection.once('disconnected', disconnectedListener);
    pendingConnection.once('disconnected', managedConnectionDisconnectedListener);
    return handshaker;
};
const createIncomingHandshaker = (localPeerDescriptor, pendingConnection, connection) => {
    const handshaker = new Handshaker(localPeerDescriptor, connection);
    const stopHandshaker = () => {
        handshaker.stop();
        pendingConnection.off('disconnected', connectionDisconnected);
        connection.off('disconnected', connectionDisconnected);
    };
    const onHandshakeRequest = () => {
        stopHandshaker();
    };
    const connectionDisconnected = (graceful) => {
        pendingConnection.close(graceful);
        stopHandshaker();
    };
    const managedConnectionDisconnected = () => {
        connection.close(false);
        stopHandshaker();
    };
    handshaker.on('handshakeRequest', onHandshakeRequest);
    connection.once('disconnected', connectionDisconnected);
    pendingConnection.once('disconnected', managedConnectionDisconnected);
    return handshaker;
};
const rejectHandshake = (pendingConnection, connection, handshaker, error) => {
    handshaker.sendHandshakeResponse(error);
    connection.destroy();
    pendingConnection.destroy();
};
const acceptHandshake = (handshaker, pendingConnection, connection) => {
    handshaker.sendHandshakeResponse();
    pendingConnection.onHandshakeCompleted(connection);
};
const createHandshakeRequest = (localPeerDescriptor, remotePeerDescriptor) => {
    const outgoingHandshake = {
        sourcePeerDescriptor: localPeerDescriptor,
        targetPeerDescriptor: remotePeerDescriptor,
        protocolVersion: LOCAL_PROTOCOL_VERSION,
        applicationVersion: version
    };
    return {
        serviceId: Handshaker.HANDSHAKER_SERVICE_ID,
        messageId: v4(),
        body: {
            oneofKind: 'handshakeRequest',
            handshakeRequest: outgoingHandshake
        }
    };
};
const createHandshakeResponse = (localPeerDescriptor, error) => {
    const outgoingHandshakeResponse = {
        sourcePeerDescriptor: localPeerDescriptor,
        error,
        protocolVersion: LOCAL_PROTOCOL_VERSION,
        applicationVersion: version
    };
    return {
        serviceId: Handshaker.HANDSHAKER_SERVICE_ID,
        messageId: v4(),
        body: {
            oneofKind: 'handshakeResponse',
            handshakeResponse: outgoingHandshakeResponse
        }
    };
};
class Handshaker extends EventEmitter {
    static HANDSHAKER_SERVICE_ID = 'system/handshaker';
    localPeerDescriptor;
    connection;
    onDataListener;
    constructor(localPeerDescriptor, connection) {
        super();
        this.localPeerDescriptor = localPeerDescriptor;
        this.connection = connection;
        this.onDataListener = (data) => this.onData(data);
        this.connection.on('data', this.onDataListener);
    }
    onData(data) {
        try {
            const message = Message.fromBinary(data);
            if (message.body.oneofKind === 'handshakeRequest') {
                logger$x.trace('handshake request received');
                const handshake = message.body.handshakeRequest;
                this.emit('handshakeRequest', handshake.sourcePeerDescriptor, handshake.protocolVersion, handshake.targetPeerDescriptor);
            }
            if (message.body.oneofKind === 'handshakeResponse') {
                logger$x.trace('handshake response received');
                const handshake = message.body.handshakeResponse;
                const error = !isMaybeSupportedProtocolVersion(handshake.protocolVersion)
                    ? HandshakeError.UNSUPPORTED_PROTOCOL_VERSION : handshake.error;
                if (error !== undefined) {
                    this.emit('handshakeFailed', error);
                }
                else {
                    this.emit('handshakeCompleted', handshake.sourcePeerDescriptor);
                }
            }
        }
        catch (err) {
            logger$x.debug('error while parsing handshake message', err);
        }
    }
    sendHandshakeRequest(remotePeerDescriptor) {
        const msg = createHandshakeRequest(this.localPeerDescriptor, remotePeerDescriptor);
        this.connection.send(Message.toBinary(msg));
        logger$x.trace('handshake request sent');
    }
    sendHandshakeResponse(error) {
        const msg = createHandshakeResponse(this.localPeerDescriptor, error);
        this.connection.send(Message.toBinary(msg));
        logger$x.trace('handshake response sent');
    }
    stop() {
        this.connection.off('data', this.onDataListener);
        this.removeAllListeners();
    }
}

const logger$w = new Logger('PendingConnection');
// PendingConnection is used as a reference to a connection that should be opened and handshaked to a given PeerDescriptor
// It does not hold a connection internally. The public method onHandshakedCompleted should be called once a connection for the 
// remotePeerDescriptor is opened and handshaked successfully.
// PendingConnections are created by the Connectors.
class PendingConnection extends EventEmitter {
    connectingAbortController = new AbortController();
    remotePeerDescriptor;
    replacedAsDuplicate = false;
    stopped = false;
    constructor(remotePeerDescriptor, timeout = 15 * 1000) {
        super();
        this.remotePeerDescriptor = remotePeerDescriptor;
        setAbortableTimeout(() => {
            this.close(false);
        }, timeout, this.connectingAbortController.signal);
    }
    replaceAsDuplicate() {
        logger$w.trace(getNodeIdOrUnknownFromPeerDescriptor(this.remotePeerDescriptor) + ' replaceAsDuplicate');
        this.replacedAsDuplicate = true;
    }
    onHandshakeCompleted(connection) {
        if (!this.replacedAsDuplicate) {
            this.emit('connected', this.remotePeerDescriptor, connection);
        }
    }
    close(graceful) {
        if (this.stopped) {
            return;
        }
        this.stopped = true;
        this.connectingAbortController.abort();
        if (!this.replacedAsDuplicate) {
            this.emit('disconnected', graceful);
        }
    }
    destroy() {
        if (this.stopped) {
            return;
        }
        this.stopped = true;
        this.connectingAbortController.abort();
        this.removeAllListeners();
    }
    getPeerDescriptor() {
        return this.remotePeerDescriptor;
    }
}

const logger$v = new Logger('SimulatorConnector');
class SimulatorConnector {
    connectingConnections = new Map();
    stopped = false;
    localPeerDescriptor;
    simulator;
    onNewConnection;
    constructor(localPeerDescriptor, simulator, onNewConnection) {
        this.localPeerDescriptor = localPeerDescriptor;
        this.simulator = simulator;
        this.onNewConnection = onNewConnection;
    }
    connect(targetPeerDescriptor) {
        logger$v.trace('connect() ' + toNodeId(targetPeerDescriptor));
        const nodeId = toNodeId(targetPeerDescriptor);
        const existingConnection = this.connectingConnections.get(nodeId);
        if (existingConnection) {
            return existingConnection;
        }
        const connection = new SimulatorConnection(this.localPeerDescriptor, targetPeerDescriptor, ConnectionType.SIMULATOR_CLIENT, this.simulator);
        const pendingConnection = new PendingConnection(targetPeerDescriptor);
        createOutgoingHandshaker(this.localPeerDescriptor, pendingConnection, connection, targetPeerDescriptor);
        this.connectingConnections.set(nodeId, pendingConnection);
        const delFunc = () => {
            this.connectingConnections.delete(nodeId);
            connection.off('disconnected', delFunc);
            pendingConnection.off('connected', delFunc);
            pendingConnection.off('disconnected', delFunc);
        };
        connection.once('disconnected', delFunc);
        pendingConnection.once('connected', delFunc);
        pendingConnection.once('disconnected', delFunc);
        connection.connect();
        return pendingConnection;
    }
    getPeerDescriptor() {
        return this.localPeerDescriptor;
    }
    handleIncomingConnection(sourceConnection) {
        // connection is incoming, so remotePeerDescriptor is localPeerDescriptor
        const remotePeerDescriptor = sourceConnection.localPeerDescriptor;
        const remoteNodeId = toNodeId(sourceConnection.localPeerDescriptor);
        logger$v.trace(remoteNodeId + ' incoming connection, stopped: ' + this.stopped);
        if (this.stopped) {
            return;
        }
        const connection = new SimulatorConnection(this.localPeerDescriptor, remotePeerDescriptor, ConnectionType.SIMULATOR_SERVER, this.simulator);
        const pendingConnection = new PendingConnection(remotePeerDescriptor);
        const handshaker = createIncomingHandshaker(this.localPeerDescriptor, pendingConnection, connection);
        logger$v.trace('connected');
        handshaker.once('handshakeRequest', () => {
            logger$v.trace(remoteNodeId + ' incoming handshake request');
            if (this.onNewConnection(pendingConnection)) {
                logger$v.trace(remoteNodeId + ' calling acceptHandshake');
                acceptHandshake(handshaker, pendingConnection, connection);
            }
            else {
                rejectHandshake(pendingConnection, connection, handshaker, HandshakeError.DUPLICATE_CONNECTION);
            }
        });
        this.simulator.accept(sourceConnection, connection);
    }
    async stop() {
        this.stopped = true;
        const conns = Array.from(this.connectingConnections.values());
        await Promise.allSettled(conns.map(async (conn) => conn.close(false)));
    }
}

class ListeningRpcCommunicator extends RoutingRpcCommunicator {
    transport;
    messageListener;
    disconnectedListener;
    constructor(ownServiceId, transport, options) {
        super(ownServiceId, (msg, opts) => transport.send(msg, opts), options);
        this.messageListener = (msg) => {
            this.handleMessageFromPeer(msg);
        };
        this.disconnectedListener = (peerDescriptor) => {
            const requests = this.getRequestIds((request) => areEqualPeerDescriptors(peerDescriptor, request.getCallContext().targetDescriptor));
            requests.forEach((id) => this.handleClientError(id, new RpcError.Disconnected('Peer disconnected')));
        };
        this.transport = transport;
        transport.on('message', this.messageListener);
        transport.on('disconnected', this.disconnectedListener);
    }
    destroy() {
        this.transport.off('message', this.messageListener);
        this.transport.off('disconnected', this.disconnectedListener);
        this.stop();
    }
}

var RtcDescription;
(function (RtcDescription) {
    RtcDescription["OFFER"] = "offer";
    RtcDescription["ANSWER"] = "answer";
})(RtcDescription || (RtcDescription = {}));

const EARLY_TIMEOUT = 5000;

var DisconnectedRtcPeerConnectionStateEnum;
(function (DisconnectedRtcPeerConnectionStateEnum) {
    DisconnectedRtcPeerConnectionStateEnum["DISCONNECTED"] = "disconnected";
    DisconnectedRtcPeerConnectionStateEnum["FAILED"] = "failed";
    DisconnectedRtcPeerConnectionStateEnum["CLOSED"] = "closed";
})(DisconnectedRtcPeerConnectionStateEnum || (DisconnectedRtcPeerConnectionStateEnum = {}));
const logger$u = new Logger('WebrtcConnection (browser)');
class WebrtcConnection extends EventEmitter {
    connectionId;
    connectionType = ConnectionType.WEBRTC;
    // We need to keep track of connection state ourselves because
    // RTCPeerConnection.connectionState is not supported on Firefox
    lastState = 'connecting';
    iceServers;
    peerConnection;
    bufferThresholdHigh = 2 ** 17;
    bufferThresholdLow = 2 ** 15;
    dataChannel;
    makingOffer = false;
    isOffering = false;
    closed = false;
    earlyTimeout;
    messageQueue = [];
    constructor(params) {
        super();
        this.connectionId = createRandomConnectionId();
        this.iceServers = params.iceServers ?? [];
        this.earlyTimeout = setTimeout(() => {
            this.doClose(false, 'timed out due to remote descriptor not being set');
        }, EARLY_TIMEOUT);
    }
    start(isOffering) {
        this.isOffering = isOffering;
        const urls = this.iceServers.map(({ url, port, username, password }) => ({
            urls: `${url}:${port}`,
            username,
            credential: password
        }));
        this.peerConnection = new RTCPeerConnection({ iceServers: urls });
        this.peerConnection.onicecandidate = (event) => {
            if ((event.candidate !== null) && (event.candidate.sdpMid !== null)) {
                this.emit('localCandidate', event.candidate.candidate, event.candidate.sdpMid);
            }
        };
        this.peerConnection.onicegatheringstatechange = () => {
            logger$u.trace(`conn.onGatheringStateChange: ${this.peerConnection?.iceGatheringState}`);
        };
        this.peerConnection.onconnectionstatechange = () => this.onStateChange();
        if (isOffering) {
            this.peerConnection.onnegotiationneeded = async () => {
                if (this.peerConnection !== undefined) {
                    this.makingOffer = true;
                    try {
                        await this.peerConnection.setLocalDescription();
                    }
                    catch (err) {
                        logger$u.warn('Failed to set local description', { err });
                    }
                    if (this.peerConnection.localDescription !== null) {
                        this.emit('localDescription', this.peerConnection.localDescription?.sdp, this.peerConnection.localDescription?.type);
                    }
                }
                this.makingOffer = false;
            };
            const dataChannel = this.peerConnection.createDataChannel('streamrDataChannel');
            this.setupDataChannel(dataChannel);
        }
        else {
            this.peerConnection.ondatachannel = (event) => {
                this.setupDataChannel(event.channel);
            };
        }
    }
    async setRemoteDescription(description, type) {
        const offerCollision = (type.toLowerCase() === RtcDescription.OFFER) && (this.makingOffer || this.peerConnection?.signalingState != 'stable');
        const ignoreOffer = this.isOffering && offerCollision;
        if (ignoreOffer) {
            return;
        }
        try {
            await this.peerConnection?.setRemoteDescription({ sdp: description, type: type.toLowerCase() });
            clearTimeout(this.earlyTimeout);
        }
        catch (err) {
            logger$u.warn('Failed to set remote description', { err });
        }
        if ((type.toLowerCase() === RtcDescription.OFFER) && (this.peerConnection !== undefined)) {
            try {
                await this.peerConnection.setLocalDescription();
            }
            catch (err) {
                logger$u.warn('Failed to set local description', { err });
            }
            if (this.peerConnection.localDescription !== null) {
                this.emit('localDescription', this.peerConnection.localDescription.sdp, this.peerConnection.localDescription.type);
            }
        }
    }
    addRemoteCandidate(candidate, mid) {
        this.peerConnection?.addIceCandidate({ candidate: candidate, sdpMid: mid })
            .catch((err) => {
            logger$u.warn('Failed to add ICE candidate', { err });
        });
    }
    isOpen() {
        return this.lastState === 'connected';
    }
    // IConnection implementation
    async close(gracefulLeave, reason) {
        this.doClose(gracefulLeave, reason);
    }
    doClose(gracefulLeave, reason) {
        if (!this.closed) {
            this.closed = true;
            this.lastState = 'closed';
            clearTimeout(this.earlyTimeout);
            this.stopListening();
            this.emit('disconnected', gracefulLeave, undefined, reason);
            this.removeAllListeners();
            if (this.dataChannel !== undefined) {
                try {
                    this.dataChannel.close();
                }
                catch (err) {
                    logger$u.warn('Failed to close data channel', { err });
                }
            }
            this.dataChannel = undefined;
            if (this.peerConnection !== undefined) {
                try {
                    this.peerConnection.close();
                }
                catch (err) {
                    logger$u.warn('Failed to close connection', { err });
                }
            }
            this.peerConnection = undefined;
        }
    }
    destroy() {
        this.removeAllListeners();
        this.doClose(false);
    }
    send(data) {
        if (this.lastState === 'connected') {
            if (this.dataChannel.bufferedAmount > this.bufferThresholdHigh) {
                this.messageQueue.push(data);
            }
            else {
                this.dataChannel?.send(data);
            }
        }
        else {
            logger$u.warn('Tried to send on a connection with last state ' + this.lastState);
        }
    }
    setupDataChannel(dataChannel) {
        this.dataChannel = dataChannel;
        this.dataChannel.binaryType = 'arraybuffer';
        this.dataChannel.bufferedAmountLowThreshold = this.bufferThresholdLow;
        dataChannel.onopen = () => {
            logger$u.trace('dc.onOpen');
            this.onDataChannelOpen();
        };
        dataChannel.onclose = () => {
            logger$u.trace('dc.onClosed');
            this.doClose(false);
        };
        dataChannel.onerror = (err) => {
            logger$u.warn('Data channel error', { err });
        };
        dataChannel.onmessage = (msg) => {
            logger$u.trace('dc.onmessage');
            this.emit('data', new Uint8Array(msg.data));
        };
        dataChannel.onbufferedamountlow = () => {
            logger$u.trace('dc.onBufferedAmountLow');
            while (this.messageQueue.length > 0 && this.dataChannel.bufferedAmount < this.bufferThresholdHigh) {
                const data = this.messageQueue.shift();
                this.dataChannel.send(data);
            }
        };
    }
    stopListening() {
        if (this.dataChannel !== undefined) {
            this.dataChannel.onopen = null;
            this.dataChannel.onclose = null;
            this.dataChannel.onerror = null;
            this.dataChannel.onbufferedamountlow = null;
            this.dataChannel.onmessage = null;
        }
        if (this.peerConnection !== undefined) {
            this.peerConnection.onconnectionstatechange = null;
            this.peerConnection.onicecandidate = null;
            this.peerConnection.onicegatheringstatechange = null;
            this.peerConnection.onnegotiationneeded = null;
            this.peerConnection.ondatachannel = null;
        }
    }
    onDataChannelOpen() {
        this.lastState = 'connected';
        this.emit('connected');
    }
    onStateChange() {
        if (this.peerConnection.connectionState === DisconnectedRtcPeerConnectionStateEnum.CLOSED
            || this.peerConnection.connectionState === DisconnectedRtcPeerConnectionStateEnum.DISCONNECTED
            || this.peerConnection.connectionState === DisconnectedRtcPeerConnectionStateEnum.FAILED) {
            this.doClose(false);
        }
    }
    setConnectionId(connectionId) {
        this.connectionId = connectionId;
    }
}

const logger$t = new Logger('WebrtcConnectorRpcRemote');
class WebrtcConnectorRpcRemote extends RpcRemote {
    requestConnection() {
        const request = {};
        const options = this.formDhtRpcOptions({
            notification: true
        });
        this.getClient().requestConnection(request, options).catch((_e) => {
            logger$t.trace('Failed to send requestConnection');
        });
    }
    sendRtcOffer(description, connectionId) {
        const request = {
            connectionId,
            description
        };
        const options = this.formDhtRpcOptions();
        this.getClient().rtcOffer(request, options).catch((_e) => {
            logger$t.trace('Failed to send rtcOffer');
        });
    }
    sendRtcAnswer(description, connectionId) {
        const request = {
            connectionId,
            description
        };
        const options = this.formDhtRpcOptions();
        this.getClient().rtcAnswer(request, options).catch((_e) => {
            logger$t.trace('Failed to send rtcAnswer');
        });
    }
    sendIceCandidate(candidate, mid, connectionId) {
        const request = {
            connectionId,
            mid,
            candidate
        };
        const options = this.formDhtRpcOptions();
        this.getClient().iceCandidate(request, options).catch((_e) => {
            logger$t.trace('Failed to send iceCandidate');
        });
    }
}

const logger$s = new Logger('WebrtcConnectorRpcLocal');
class WebrtcConnectorRpcLocal {
    options;
    constructor(options) {
        this.options = options;
    }
    async requestConnection(context) {
        const targetPeerDescriptor = context.incomingSourceDescriptor;
        if (this.options.ongoingConnectAttempts.has(toNodeId(targetPeerDescriptor))) {
            return {};
        }
        const pendingConnection = this.options.connect(targetPeerDescriptor, false);
        this.options.onNewConnection(pendingConnection);
        return {};
    }
    async rtcOffer(request, context) {
        const remotePeerDescriptor = context.incomingSourceDescriptor;
        const nodeId = toNodeId(remotePeerDescriptor);
        let connection;
        let pendingConnection;
        if (!this.options.ongoingConnectAttempts.has(nodeId)) {
            pendingConnection = this.options.connect(remotePeerDescriptor, true);
            connection = this.options.ongoingConnectAttempts.get(nodeId).connection;
            this.options.onNewConnection(pendingConnection);
        }
        else {
            pendingConnection = this.options.ongoingConnectAttempts.get(nodeId).managedConnection;
            connection = this.options.ongoingConnectAttempts.get(nodeId).connection;
        }
        // Always use offerers connectionId
        connection.setConnectionId(request.connectionId);
        connection.setRemoteDescription(request.description, 'offer');
        return {};
    }
    async rtcAnswer(request, context) {
        const remotePeerDescriptor = context.incomingSourceDescriptor;
        const nodeId = toNodeId(remotePeerDescriptor);
        const connection = this.options.ongoingConnectAttempts.get(nodeId)?.connection;
        if (!connection) {
            return {};
        }
        else if (connection.connectionId !== request.connectionId) {
            logger$s.trace(`Ignoring RTC answer due to connectionId mismatch`);
            return {};
        }
        connection.setRemoteDescription(request.description, 'answer');
        return {};
    }
    async iceCandidate(request, context) {
        const remotePeerDescriptor = context.incomingSourceDescriptor;
        const nodeId = toNodeId(remotePeerDescriptor);
        const connection = this.options.ongoingConnectAttempts.get(nodeId)?.connection;
        if (!connection) {
            return {};
        }
        else if (connection.connectionId !== request.connectionId) {
            logger$s.trace(`Ignoring remote candidate due to connectionId mismatch`);
            return {};
        }
        else if (this.isIceCandidateAllowed(request.candidate)) {
            connection.addRemoteCandidate(request.candidate, request.mid);
        }
        return {};
    }
    isIceCandidateAllowed(candidate) {
        if (!this.options.allowPrivateAddresses) {
            const address = getAddressFromIceCandidate(candidate);
            if ((address !== undefined) && isPrivateIPv4(address)) {
                return false;
            }
        }
        return true;
    }
}

const logger$r = new Logger('WebrtcConnector');
const replaceInternalIpWithExternalIp = (candidate, ip) => {
    const parsed = candidate.split(' ');
    const type = parsed[7];
    if (type === 'host') {
        parsed[4] = ip;
    }
    return parsed.join(' ');
};
class WebrtcConnector {
    static WEBRTC_CONNECTOR_SERVICE_ID = 'system/webrtc-connector';
    rpcCommunicator;
    ongoingConnectAttempts = new Map();
    localPeerDescriptor;
    stopped = false;
    options;
    constructor(options) {
        this.options = options;
        this.rpcCommunicator = new ListeningRpcCommunicator(WebrtcConnector.WEBRTC_CONNECTOR_SERVICE_ID, options.transport, {
            rpcRequestTimeout: 15000 // TODO use options option or named constant?
        });
        this.registerLocalRpcMethods(options);
    }
    registerLocalRpcMethods(options) {
        const localRpc = new WebrtcConnectorRpcLocal({
            connect: (targetPeerDescriptor, doNotRequestConnection) => this.connect(targetPeerDescriptor, doNotRequestConnection),
            onNewConnection: (connection) => this.options.onNewConnection(connection),
            ongoingConnectAttempts: this.ongoingConnectAttempts,
            rpcCommunicator: this.rpcCommunicator,
            getLocalPeerDescriptor: () => this.localPeerDescriptor,
            allowPrivateAddresses: options.allowPrivateAddresses ?? true
        });
        this.rpcCommunicator.registerRpcNotification(WebrtcConnectionRequest, 'requestConnection', async (_req, context) => {
            if (!this.stopped) {
                return localRpc.requestConnection(context);
            }
            else {
                return {};
            }
        });
        this.rpcCommunicator.registerRpcNotification(RtcOffer, 'rtcOffer', async (req, context) => {
            if (!this.stopped) {
                return localRpc.rtcOffer(req, context);
            }
            else {
                return {};
            }
        });
        this.rpcCommunicator.registerRpcNotification(RtcAnswer, 'rtcAnswer', async (req, context) => {
            if (!this.stopped) {
                return localRpc.rtcAnswer(req, context);
            }
            else {
                return {};
            }
        });
        this.rpcCommunicator.registerRpcNotification(IceCandidate, 'iceCandidate', async (req, context) => {
            if (!this.stopped) {
                return localRpc.iceCandidate(req, context);
            }
            else {
                return {};
            }
        });
    }
    connect(targetPeerDescriptor, doNotRequestConnection) {
        if (areEqualPeerDescriptors(targetPeerDescriptor, this.localPeerDescriptor)) {
            throw new CannotConnectToSelf('Cannot open WebRTC Connection to self');
        }
        logger$r.trace(`Opening WebRTC connection to ${toNodeId(targetPeerDescriptor)}`);
        const nodeId = toNodeId(targetPeerDescriptor);
        const existingConnection = this.ongoingConnectAttempts.get(nodeId);
        if (existingConnection) {
            return existingConnection.managedConnection;
        }
        const connection = this.createConnection(targetPeerDescriptor);
        const localNodeId = toNodeId(this.localPeerDescriptor);
        const targetNodeId = toNodeId(targetPeerDescriptor);
        const offering = (getOfferer(localNodeId, targetNodeId) === 'local');
        let pendingConnection;
        const remoteConnector = new WebrtcConnectorRpcRemote(this.localPeerDescriptor, targetPeerDescriptor, this.rpcCommunicator, WebrtcConnectorRpcClient);
        const delFunc = () => {
            this.ongoingConnectAttempts.delete(nodeId);
            connection.off('disconnected', delFunc);
            pendingConnection.off('disconnected', delFunc);
            pendingConnection.off('connected', delFunc);
        };
        if (offering) {
            pendingConnection = new PendingConnection(targetPeerDescriptor);
            createOutgoingHandshaker(this.localPeerDescriptor, pendingConnection, connection, targetPeerDescriptor);
            connection.once('localDescription', (description) => {
                logger$r.trace('Sending offer to remote peer');
                remoteConnector.sendRtcOffer(description, connection.connectionId);
            });
        }
        else {
            pendingConnection = new PendingConnection(targetPeerDescriptor);
            const handshaker = createIncomingHandshaker(this.localPeerDescriptor, pendingConnection, connection);
            connection.once('localDescription', (description) => {
                remoteConnector.sendRtcAnswer(description, connection.connectionId);
            });
            handshaker.on('handshakeRequest', (_sourceDescriptor, remoteVersion) => {
                if (!isMaybeSupportedProtocolVersion(remoteVersion)) {
                    rejectHandshake(pendingConnection, connection, handshaker, HandshakeError.UNSUPPORTED_PROTOCOL_VERSION);
                }
                else {
                    acceptHandshake(handshaker, pendingConnection, connection);
                }
                delFunc();
            });
        }
        this.ongoingConnectAttempts.set(targetNodeId, {
            managedConnection: pendingConnection,
            connection
        });
        connection.on('disconnected', delFunc);
        pendingConnection.on('disconnected', delFunc);
        pendingConnection.on('connected', delFunc);
        connection.on('localCandidate', (candidate, mid) => {
            if (this.options.externalIp !== undefined) {
                candidate = replaceInternalIpWithExternalIp(candidate, this.options.externalIp);
                logger$r.debug(`onLocalCandidate injected external ip ${candidate} ${mid}`);
            }
            remoteConnector.sendIceCandidate(candidate, mid, connection.connectionId);
        });
        connection.start(offering);
        if (!doNotRequestConnection && !offering) {
            remoteConnector.requestConnection();
        }
        return pendingConnection;
    }
    createConnection(targetPeerDescriptor) {
        return new WebrtcConnection({
            remotePeerDescriptor: targetPeerDescriptor,
            iceServers: this.options.iceServers,
            bufferThresholdLow: this.options.bufferThresholdLow,
            bufferThresholdHigh: this.options.bufferThresholdHigh,
            portRange: this.options.portRange
            // TODO should we pass maxMessageSize?
        });
    }
    setLocalPeerDescriptor(peerDescriptor) {
        this.localPeerDescriptor = peerDescriptor;
    }
    async stop() {
        logger$r.trace('stop()');
        this.stopped = true;
        const attempts = Array.from(this.ongoingConnectAttempts.values());
        await Promise.allSettled(attempts.map(async (conn) => {
            conn.connection.destroy();
            conn.managedConnection.close(false);
        }));
        this.rpcCommunicator.destroy();
    }
}

// https://kapeli.com/cheat_sheets/WebSocket_Status_Codes.docset/Contents/Resources/Documents/index
// Browsers send this automatically when closing a tab
const GOING_AWAY = 1001;
// The GOING_AWAY is a reserved code and we shouldn't send that from the application. Therefore
// we have a custom counterpart
const CUSTOM_GOING_AWAY = 3001;
// https://github.com/websockets/ws/blob/master/doc/ws.md#ready-state-constants
const OPEN = 1;
const logger$q = new Logger('AbstractWebsocketClientConnection');
class AbstractWebsocketClientConnection extends EventEmitter {
    connectionId;
    connectionType = ConnectionType.WEBSOCKET_CLIENT;
    destroyed = false;
    constructor() {
        super();
        this.connectionId = createRandomConnectionId();
    }
    send(data) {
        if (!this.destroyed) {
            if (this.socket?.readyState === OPEN) {
                logger$q.trace(`Sending data with size ${data.byteLength}`);
                this.socket?.send(data);
            }
            else {
                // Could this throw for faster feedback on RPC calls?
                // Currently this log line is seen if a connection is closing but the disconnected event has not been emitted yet.
                logger$q.debug('Tried to send data on a non-open connection', {
                    id: this.connectionId,
                    readyState: this.socket.readyState,
                    destroyed: this.destroyed
                });
            }
        }
        else {
            logger$q.debug('Tried to send() on stopped connection');
        }
    }
    async close(gracefulLeave) {
        this.emit('disconnected', gracefulLeave, undefined, 'close() called');
        this.removeAllListeners();
        if (!this.destroyed) {
            logger$q.trace(`Closing socket for connection ${this.connectionId}`);
            this.socket?.close(gracefulLeave ? CUSTOM_GOING_AWAY : undefined);
        }
        else {
            logger$q.debug('Tried to close() a stopped connection', { id: this.connectionId });
        }
    }
    destroy() {
        logger$q.trace('destroy() a connection');
        if (!this.destroyed) {
            this.removeAllListeners();
            if (this.socket) {
                this.stopListening();
                this.socket.close();
                this.socket = undefined;
            }
            this.destroyed = true;
        }
        else {
            logger$q.debug('Tried to destroy() a stopped connection');
        }
    }
    onOpen() {
        if (!this.destroyed) {
            logger$q.trace('WebSocket Client Connected');
            if (this.socket?.readyState === OPEN) {
                this.emit('connected');
            }
        }
    }
    onMessage(message) {
        this.emit('data', message);
    }
    onClose(code, reason) {
        if (!this.destroyed) {
            logger$q.trace('Websocket Closed');
            this.doDisconnect(code, reason);
        }
    }
    onError(error) {
        if (!this.destroyed) {
            logger$q.trace('WebSocket Client error: ' + error?.message, { error });
            this.emit('error', error.name);
        }
    }
    doDisconnect(code, reason) {
        this.destroyed = true;
        this.stopListening();
        this.socket = undefined;
        const gracefulLeave = (code === GOING_AWAY) || (code === CUSTOM_GOING_AWAY);
        this.emit('disconnected', gracefulLeave, code, reason);
        this.removeAllListeners();
    }
}

const logger$p = new Logger('WebsocketClientConnection (browser)');
const BINARY_TYPE = 'arraybuffer';
class WebsocketClientConnection extends AbstractWebsocketClientConnection {
    socket;
    // TODO explicit default value for "selfSigned" or make it required
    connect(address, selfSigned) {
        if (!this.destroyed) {
            this.socket = new w3cwebsocket(address, undefined, undefined, undefined, { rejectUnauthorized: !selfSigned });
            this.socket.binaryType = BINARY_TYPE;
            this.socket.onerror = (error) => this.onError(error);
            this.socket.onopen = () => this.onOpen();
            this.socket.onclose = (event) => this.onClose(event.code, event.reason);
            this.socket.onmessage = (message) => {
                if (!this.destroyed) {
                    if (typeof message.data === 'string') {
                        logger$p.debug('Received string data, only binary data is supported');
                    }
                    else {
                        this.onMessage(new Uint8Array(message.data));
                    }
                }
            };
        }
        else {
            logger$p.debug('Tried to connect() a stopped connection');
        }
    }
    stopListening() {
        if (this.socket) {
            this.socket.onopen = undefined;
            this.socket.onclose = undefined;
            this.socket.onerror = undefined;
            this.socket.onmessage = undefined;
        }
    }
}

class WebsocketClientConnectorRpcLocal {
    options;
    constructor(options) {
        this.options = options;
    }
    async requestConnection(_request, context) {
        if (this.options.abortSignal.aborted) {
            return {};
        }
        const senderPeerDescriptor = context.incomingSourceDescriptor;
        if (!this.options.hasConnection(toNodeId(senderPeerDescriptor))) {
            const connection = this.options.connect(senderPeerDescriptor);
            this.options.onNewConnection(connection);
        }
        return {};
    }
}

const connectivityMethodToWebsocketUrl = (ws, action) => {
    return (ws.tls ? 'wss://' : 'ws://') + ws.host + ':' + ws.port + ((action !== undefined) ? '?action=' + action : '');
};
class WebsocketClientConnector {
    static WEBSOCKET_CONNECTOR_SERVICE_ID = 'system/websocket-connector';
    websocketServer;
    geoIpLocator;
    localPeerDescriptor;
    connectingConnections = new Map();
    abortController = new AbortController();
    options;
    constructor(options) {
        this.options = options;
        this.registerLocalRpcMethods();
    }
    registerLocalRpcMethods() {
        const rpcLocal = new WebsocketClientConnectorRpcLocal({
            connect: (targetPeerDescriptor) => this.connect(targetPeerDescriptor),
            hasConnection: (nodeId) => (this.connectingConnections.has(nodeId)
                || this.options.hasConnection(nodeId)),
            onNewConnection: (connection) => this.options.onNewConnection(connection),
            abortSignal: this.abortController.signal
        });
        this.options.rpcCommunicator.registerRpcNotification(WebsocketConnectionRequest, 'requestConnection', async (req, context) => {
            if (!this.abortController.signal.aborted) {
                return rpcLocal.requestConnection(req, context);
            }
            else {
                return {};
            }
        });
    }
    isPossibleToFormConnection(targetPeerDescriptor) {
        const connectionType = expectedConnectionType(this.localPeerDescriptor, targetPeerDescriptor);
        return connectionType === ConnectionType.WEBSOCKET_CLIENT;
    }
    connect(targetPeerDescriptor) {
        const nodeId = toNodeId(targetPeerDescriptor);
        const existingConnection = this.connectingConnections.get(nodeId);
        if (existingConnection) {
            return existingConnection;
        }
        const socket = new WebsocketClientConnection();
        const url = connectivityMethodToWebsocketUrl(targetPeerDescriptor.websocket);
        const pendingConnection = new PendingConnection(targetPeerDescriptor);
        createOutgoingHandshaker(this.localPeerDescriptor, pendingConnection, socket, targetPeerDescriptor);
        this.connectingConnections.set(nodeId, pendingConnection);
        const delFunc = () => {
            if (this.connectingConnections.has(nodeId)) {
                this.connectingConnections.delete(nodeId);
            }
            socket.off('disconnected', delFunc);
            pendingConnection.off('disconnected', delFunc);
            pendingConnection.off('connected', delFunc);
        };
        socket.on('disconnected', delFunc);
        pendingConnection.on('disconnected', delFunc);
        pendingConnection.on('connected', delFunc);
        socket.connect(url, false);
        return pendingConnection;
    }
    setLocalPeerDescriptor(peerDescriptor) {
        this.localPeerDescriptor = peerDescriptor;
    }
    async destroy() {
        this.abortController.abort();
        const requests = Array.from(this.connectingConnections.values());
        requests.forEach((conn) => conn.close(true));
        await this.websocketServer?.stop();
        this.geoIpLocator?.stop();
    }
}

const createGeoipLocator = (_geoIpDatabaseFolder) => {
    throw new Error('GeoIpLocator is not supported in browser environment');
};

/* eslint-disable class-methods-use-this */
/**
 * A stub WebsocketServer for browser environment.
 */
class WebsocketServer extends EventEmitter {
    constructor(_params) {
        super();
    }
    async start() {
        throw new Error('WebsocketServer is not supported in browser environment');
    }
    async stop() {
        throw new Error('WebsocketServer is not supported in browser environment');
    }
    updateCertificate(_cert, _key) {
        throw new Error('WebsocketServer is not supported in browser environment');
    }
}

const createDefaultAutocertifierClient = (_configFile, _autoCertifierUrl, _autoCertifierRpcCommunicator, _wsServerPort) => {
    throw new Error('AutoCertifierClient is not supported in browser environment');
};

const START_TIMEOUT = 60 * 1000;
const logger$o = new Logger('AutoCertifierClientFacade');
class AutoCertifierClientFacade {
    autoCertifierClient;
    rpcCommunicator;
    options;
    constructor(options) {
        this.options = options;
        this.rpcCommunicator = new ListeningRpcCommunicator(SERVICE_ID, options.transport);
        this.autoCertifierClient = options.createClientFactory ? options.createClientFactory()
            : createDefaultAutocertifierClient(options.configFile, options.url, this.rpcCommunicator, options.wsServerPort);
    }
    async start() {
        this.autoCertifierClient.on('updatedCertificate', (subdomain) => {
            this.options.setHost(subdomain.fqdn);
            this.options.updateCertificate(subdomain.certificate, subdomain.privateKey);
            logger$o.trace(`Updated certificate`);
        });
        await Promise.all([
            waitForEvent(this.autoCertifierClient, 'updatedCertificate', START_TIMEOUT),
            this.autoCertifierClient.start()
        ]);
    }
    stop() {
        this.autoCertifierClient.stop();
        this.rpcCommunicator.destroy();
    }
}

const logger$n = new Logger('connectivityChecker');
// TODO use options option or named constant?
const connectAsync = async ({ url, allowSelfSignedCertificate, timeoutMs = 1000 }) => {
    const socket = new WebsocketClientConnection();
    let result;
    try {
        const resultPromise = raceForEvent(socket, ['connected', 'error'], timeoutMs);
        socket.connect(url, allowSelfSignedCertificate);
        result = await resultPromise;
    }
    catch {
        throw new ConnectionFailed('WebSocket connection timed out');
    }
    if (result.winnerName === 'error') {
        throw new ConnectionFailed('Could not open WebSocket connection');
    }
    return socket;
};
const CONNECTIVITY_CHECKER_SERVICE_ID = 'system/connectivity-checker';
const CONNECTIVITY_CHECKER_TIMEOUT = 5000;
const sendConnectivityRequest = async (request, entryPoint) => {
    let outgoingConnection;
    const wsServerInfo = {
        host: entryPoint.websocket.host,
        port: entryPoint.websocket.port,
        tls: entryPoint.websocket.tls,
    };
    const url = connectivityMethodToWebsocketUrl(wsServerInfo, 'connectivityRequest');
    logger$n.debug(`Attempting connectivity check with entrypoint ${url}`);
    try {
        outgoingConnection = await connectAsync({
            url,
            allowSelfSignedCertificate: request.allowSelfSignedCertificate
        });
    }
    catch (e) {
        throw new ConnectionFailed(`Failed to connect to entrypoint for connectivity check: ${url}`, e);
    }
    // send connectivity request
    const msg = {
        serviceId: CONNECTIVITY_CHECKER_SERVICE_ID,
        messageId: v4(),
        body: {
            oneofKind: 'connectivityRequest',
            connectivityRequest: request
        }
    };
    const responseAwaiter = () => {
        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                // TODO should we have some handling for this floating promise?
                outgoingConnection.close(false);
                reject(new ConnectivityResponseTimeout('timeout'));
            }, CONNECTIVITY_CHECKER_TIMEOUT);
            const listener = (bytes) => {
                // TODO should we have some handling for this floating promise?
                outgoingConnection.close(false);
                try {
                    const message = Message.fromBinary(bytes);
                    if (message.body.oneofKind === 'connectivityResponse') {
                        logger$n.debug('ConnectivityResponse received: ' + JSON.stringify(Message.toJson(message)));
                        const connectivityResponseMessage = message.body.connectivityResponse;
                        const remoteProtocolVersion = connectivityResponseMessage.protocolVersion;
                        outgoingConnection.off('data', listener);
                        clearTimeout(timeoutId);
                        if (isMaybeSupportedProtocolVersion(remoteProtocolVersion)) {
                            resolve(connectivityResponseMessage);
                        }
                        else {
                            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                            reject(`Unsupported version: ${remoteProtocolVersion}`);
                        }
                    }
                }
                catch (err) {
                    logger$n.trace('Could not parse message', { err });
                }
            };
            outgoingConnection.on('data', listener);
        });
    };
    try {
        const retPromise = responseAwaiter();
        outgoingConnection.send(Message.toBinary(msg));
        logger$n.trace('ConnectivityRequest sent: ' + JSON.stringify(Message.toJson(msg)));
        return await retPromise;
    }
    catch (e) {
        logger$n.error('error getting connectivityresponse');
        throw e;
    }
};

const DISABLE_CONNECTIVITY_PROBE = 0;
const logger$m = new Logger('connectivityRequestHandler');
const attachConnectivityRequestHandler = (connectionToListenTo, geoIpLocator) => {
    connectionToListenTo.on('data', async (data) => {
        logger$m.trace('server received data');
        try {
            const message = Message.fromBinary(data);
            if (message.body.oneofKind === 'connectivityRequest') {
                logger$m.trace('ConnectivityRequest received: ' + JSON.stringify(Message.toJson(message)));
                try {
                    await handleIncomingConnectivityRequest(connectionToListenTo, message.body.connectivityRequest, geoIpLocator);
                    logger$m.trace('handleIncomingConnectivityRequest ok');
                }
                catch (err1) {
                    logger$m.error('handleIncomingConnectivityRequest', { err: err1 });
                }
            }
        }
        catch (err2) {
            logger$m.trace('Could not parse message', { err: err2 });
        }
    });
};
const handleIncomingConnectivityRequest = async (connection, connectivityRequest, geoIpLocator) => {
    const host = connectivityRequest.host ?? connection.getRemoteIpAddress();
    const ipAddress = connection.getRemoteIpAddress();
    let connectivityResponse;
    if (connectivityRequest.port !== DISABLE_CONNECTIVITY_PROBE) {
        connectivityResponse = await connectivityProbe(connectivityRequest, ipAddress, host);
    }
    else {
        logger$m.trace('ConnectivityRequest port is 0, replying without connectivityProbe');
        connectivityResponse = {
            host,
            natType: NatType.UNKNOWN,
            ipAddress: ipv4ToNumber(ipAddress),
            protocolVersion: LOCAL_PROTOCOL_VERSION
        };
    }
    if (geoIpLocator !== undefined) {
        const location = geoIpLocator.lookup(ipAddress);
        if (location !== undefined) {
            connectivityResponse.latitude = location.latitude;
            connectivityResponse.longitude = location.longitude;
        }
    }
    const msg = {
        serviceId: CONNECTIVITY_CHECKER_SERVICE_ID,
        messageId: v4(),
        body: {
            oneofKind: 'connectivityResponse',
            connectivityResponse
        }
    };
    connection.send(Message.toBinary(msg));
    logger$m.trace('ConnectivityResponse sent: ' + JSON.stringify(Message.toJson(msg)));
};
const connectivityProbe = async (connectivityRequest, ipAddress, host) => {
    let outgoingConnection;
    let connectivityResponseMessage;
    try {
        const wsServerInfo = {
            host,
            port: connectivityRequest.port,
            tls: connectivityRequest.tls
        };
        const url = connectivityMethodToWebsocketUrl(wsServerInfo, 'connectivityProbe');
        logger$m.trace(`Attempting Connectivity Check to ${url}`);
        outgoingConnection = await connectAsync({
            url,
            allowSelfSignedCertificate: connectivityRequest.allowSelfSignedCertificate
        });
        logger$m.trace('Connectivity test produced positive result, communicating reply to the requester ' + host + ':' + connectivityRequest.port);
        connectivityResponseMessage = {
            host,
            natType: NatType.OPEN_INTERNET,
            websocket: { host, port: connectivityRequest.port, tls: connectivityRequest.tls },
            ipAddress: ipv4ToNumber(ipAddress),
            protocolVersion: LOCAL_PROTOCOL_VERSION
        };
    }
    catch (err) {
        logger$m.debug('error', { err });
        connectivityResponseMessage = {
            host,
            natType: NatType.UNKNOWN,
            ipAddress: ipv4ToNumber(ipAddress),
            protocolVersion: LOCAL_PROTOCOL_VERSION
        };
    }
    if (outgoingConnection) {
        // TODO should we have some handling for this floating promise?
        outgoingConnection.close(false);
    }
    return connectivityResponseMessage;
};

const logger$l = new Logger('WebsocketClientConnectorRpcRemote');
class WebsocketClientConnectorRpcRemote extends RpcRemote {
    async requestConnection() {
        logger$l.trace(`Requesting WebSocket connection from ${toNodeId(this.getLocalPeerDescriptor())}`);
        const request = {};
        const options = this.formDhtRpcOptions();
        return this.getClient().requestConnection(request, options);
    }
}

const logger$k = new Logger('WebsocketServerConnector');
// TODO: Move server starting logic including autocertification and connectivity checking to WebsocketServer.ts?
class WebsocketServerConnector {
    websocketServer;
    geoIpLocator;
    ongoingConnectRequests = new Map();
    host;
    autoCertifierClient;
    selectedPort;
    localPeerDescriptor;
    abortController = new AbortController();
    options;
    constructor(options) {
        this.options = options;
        this.websocketServer = options.portRange ? new WebsocketServer({
            portRange: options.portRange,
            tlsCertificate: options.tlsCertificate,
            maxMessageSize: options.maxMessageSize,
            enableTls: options.serverEnableTls
        }) : undefined;
        this.host = options.host;
    }
    async start() {
        if (!this.abortController.signal.aborted && this.websocketServer) {
            this.websocketServer.on('connected', (connection) => {
                const serverSocket = connection;
                const urlParams = new URLSearchParams(serverSocket.resourceURL.query ?? '');
                const action = urlParams.get('action');
                logger$k.trace('WebSocket client connected', { action, remoteAddress: serverSocket.getRemoteIpAddress() });
                if (action === 'connectivityRequest') {
                    attachConnectivityRequestHandler(serverSocket, this.geoIpLocator);
                }
                else if (action === 'connectivityProbe') ;
                else {
                    // The localPeerDescriptor can be undefined here as the WS server is used for connectivity checks
                    // before the localPeerDescriptor is set during start.
                    // Handshaked connections should be rejected before the localPeerDescriptor is set.
                    // eslint-disable-next-line no-lonely-if
                    if (this.localPeerDescriptor !== undefined) {
                        this.attachHandshaker(connection);
                    }
                    else {
                        logger$k.trace('incoming Websocket connection before localPeerDescriptor was set, closing connection');
                        connection.close(false).catch(() => { });
                    }
                }
            });
            if (this.options.geoIpDatabaseFolder) {
                try {
                    this.geoIpLocator = await createGeoipLocator(this.options.geoIpDatabaseFolder);
                }
                catch (err) {
                    logger$k.error('Failed to start GeoIpLocator', { err });
                }
            }
            const port = await this.websocketServer.start();
            this.selectedPort = port;
        }
    }
    attachHandshaker(connection) {
        // TODO: use createIncomingHandshaker here?
        const handshaker = new Handshaker(this.localPeerDescriptor, connection);
        handshaker.once('handshakeRequest', (localPeerDescriptor, remoteProtocolVersion, remotePeerDescriptor) => {
            this.onServerSocketHandshakeRequest(localPeerDescriptor, connection, handshaker, remoteProtocolVersion, remotePeerDescriptor);
        });
    }
    onServerSocketHandshakeRequest(remotePeerDescriptor, websocketServerConnection, handshaker, remoteProtocolVersion, targetPeerDescriptor) {
        const nodeId = toNodeId(remotePeerDescriptor);
        if (this.ongoingConnectRequests.has(nodeId)) {
            const { pendingConnection, delFunc } = this.ongoingConnectRequests.get(nodeId);
            if (!isMaybeSupportedProtocolVersion(remoteProtocolVersion)) {
                rejectHandshake(pendingConnection, websocketServerConnection, handshaker, HandshakeError.UNSUPPORTED_PROTOCOL_VERSION);
                delFunc();
            }
            else if (targetPeerDescriptor && !areEqualPeerDescriptors(this.localPeerDescriptor, targetPeerDescriptor)) {
                rejectHandshake(pendingConnection, websocketServerConnection, handshaker, HandshakeError.INVALID_TARGET_PEER_DESCRIPTOR);
                delFunc();
            }
            else {
                acceptHandshake(handshaker, pendingConnection, websocketServerConnection);
            }
        }
        else {
            const pendingConnection = new PendingConnection(remotePeerDescriptor);
            if (!isMaybeSupportedProtocolVersion(remoteProtocolVersion)) {
                rejectHandshake(pendingConnection, websocketServerConnection, handshaker, HandshakeError.UNSUPPORTED_PROTOCOL_VERSION);
            }
            else if (targetPeerDescriptor && !areEqualPeerDescriptors(this.localPeerDescriptor, targetPeerDescriptor)) {
                rejectHandshake(pendingConnection, websocketServerConnection, handshaker, HandshakeError.INVALID_TARGET_PEER_DESCRIPTOR);
            }
            else if (this.options.onNewConnection(pendingConnection)) {
                acceptHandshake(handshaker, pendingConnection, websocketServerConnection);
            }
            else {
                rejectHandshake(pendingConnection, websocketServerConnection, handshaker, HandshakeError.DUPLICATE_CONNECTION);
            }
        }
    }
    async checkConnectivity(allowSelfSignedCertificate) {
        // TODO: this could throw?
        if (this.abortController.signal.aborted) {
            return {
                host: '127.0.0.1',
                natType: NatType.UNKNOWN,
                ipAddress: ipv4ToNumber('127.0.0.1'),
                protocolVersion: LOCAL_PROTOCOL_VERSION
            };
        }
        if (!this.options.entrypoints || this.options.entrypoints.length === 0) {
            // return connectivity info given in options
            return {
                host: this.host,
                natType: NatType.OPEN_INTERNET,
                websocket: {
                    host: this.host,
                    port: this.selectedPort,
                    tls: this.options.tlsCertificate !== undefined
                },
                // TODO: Resolve the given host name or or use as is if IP was given. 
                ipAddress: ipv4ToNumber('127.0.0.1'),
                protocolVersion: LOCAL_PROTOCOL_VERSION
            };
        }
        const shuffledEntrypoints = shuffle(this.options.entrypoints);
        while (shuffledEntrypoints.length > 0 && !this.abortController.signal.aborted) {
            const entryPoint = shuffledEntrypoints[0];
            try {
                // Do real connectivity checking
                const connectivityRequest = {
                    port: this.selectedPort ?? DISABLE_CONNECTIVITY_PROBE,
                    host: this.host,
                    tls: this.websocketServer ? this.options.serverEnableTls : false,
                    allowSelfSignedCertificate
                };
                if (!this.abortController.signal.aborted) {
                    return await sendConnectivityRequest(connectivityRequest, entryPoint);
                }
                else {
                    throw new ConnectionFailed('ConnectivityChecker is destroyed');
                }
            }
            catch (err) {
                const error = `Failed to connect to entrypoint with id ${toNodeId(entryPoint)} `
                    + `and URL ${connectivityMethodToWebsocketUrl(entryPoint.websocket)}`;
                logger$k.error(error, { err });
                shuffledEntrypoints.shift();
                await wait(2000, this.abortController.signal);
            }
        }
        throw new WebsocketServerStartError(`Failed to connect to the entrypoints after ${this.options.entrypoints.length} attempts\n`
            + `Attempted hosts: ${this.options.entrypoints.map((entry) => `${entry.websocket.host}:${entry.websocket.port}`).join(', ')}`);
    }
    async autoCertify() {
        this.autoCertifierClient = new AutoCertifierClientFacade({
            configFile: this.options.autoCertifierConfigFile,
            transport: this.options.autoCertifierTransport,
            url: this.options.autoCertifierUrl,
            wsServerPort: this.selectedPort,
            setHost: (hostName) => this.setHost(hostName),
            updateCertificate: (certificate, privateKey) => this.websocketServer.updateCertificate(certificate, privateKey)
        });
        logger$k.trace(`AutoCertifying subdomain...`);
        await this.autoCertifierClient.start();
    }
    setHost(hostName) {
        logger$k.trace(`Setting host name to ${hostName}`);
        this.host = hostName;
    }
    connect(targetPeerDescriptor) {
        const nodeId = toNodeId(targetPeerDescriptor);
        if (this.ongoingConnectRequests.has(nodeId)) {
            return this.ongoingConnectRequests.get(nodeId).pendingConnection;
        }
        return this.requestConnectionFromPeer(this.localPeerDescriptor, targetPeerDescriptor);
    }
    requestConnectionFromPeer(localPeerDescriptor, targetPeerDescriptor) {
        setImmediate(() => {
            const remoteConnector = new WebsocketClientConnectorRpcRemote(localPeerDescriptor, targetPeerDescriptor, this.options.rpcCommunicator, WebsocketClientConnectorRpcClient);
            remoteConnector.requestConnection().then(() => {
                logger$k.trace('Sent WebsocketConnectionRequest notification to peer', { targetPeerDescriptor });
            }, (err) => {
                logger$k.debug('Failed to send WebsocketConnectionRequest notification to peer ', {
                    error: err, targetPeerDescriptor
                });
            });
        });
        const pendingConnection = new PendingConnection(targetPeerDescriptor);
        const nodeId = toNodeId(targetPeerDescriptor);
        // TODO: can this leak?
        const delFunc = () => {
            pendingConnection.off('connected', delFunc);
            pendingConnection.off('disconnected', delFunc);
            this.ongoingConnectRequests.delete(nodeId);
        };
        pendingConnection.on('connected', delFunc);
        pendingConnection.on('disconnected', delFunc);
        this.ongoingConnectRequests.set(nodeId, { pendingConnection, delFunc });
        return pendingConnection;
    }
    isPossibleToFormConnection(targetPeerDescriptor) {
        const connectionType = expectedConnectionType(this.localPeerDescriptor, targetPeerDescriptor);
        return (connectionType === ConnectionType.WEBSOCKET_SERVER);
    }
    setLocalPeerDescriptor(localPeerDescriptor) {
        this.localPeerDescriptor = localPeerDescriptor;
    }
    async destroy() {
        this.abortController.abort();
        const requests = Array.from(this.ongoingConnectRequests.values());
        requests.forEach((ongoingConnectRequest) => ongoingConnectRequest.pendingConnection.close(true));
        await this.websocketServer?.stop();
        this.geoIpLocator?.stop();
    }
}

const logger$j = new Logger('ConnectorFacade');
class DefaultConnectorFacade {
    options;
    localPeerDescriptor;
    websocketConnectorRpcCommunicator;
    websocketClientConnector;
    websocketServerConnector;
    webrtcConnector;
    constructor(options) {
        this.options = options;
    }
    async start(onNewConnection, hasConnection, autoCertifierTransport) {
        logger$j.trace(`Creating WebsocketConnectorRpcLocal`);
        this.websocketConnectorRpcCommunicator = new ListeningRpcCommunicator(WebsocketClientConnector.WEBSOCKET_CONNECTOR_SERVICE_ID, this.options.transport, { rpcRequestTimeout: 15000 } // TODO use options option or named constant?
        );
        const webSocketClientConnectorOptions = {
            // TODO should we use canConnect also for WebrtcConnector? (NET-1142)
            onNewConnection,
            hasConnection,
            rpcCommunicator: this.websocketConnectorRpcCommunicator
        };
        this.websocketClientConnector = new WebsocketClientConnector(webSocketClientConnectorOptions);
        const webSocketServerConnectorOptions = {
            rpcCommunicator: this.websocketConnectorRpcCommunicator,
            // TODO should we use canConnect also for WebrtcConnector? (NET-1142)
            onNewConnection,
            hasConnection,
            portRange: this.options.websocketPortRange,
            host: this.options.websocketHost,
            entrypoints: this.options.entryPoints,
            tlsCertificate: this.options.tlsCertificate,
            serverEnableTls: this.options.websocketServerEnableTls,
            autoCertifierUrl: this.options.autoCertifierUrl,
            autoCertifierConfigFile: this.options.autoCertifierConfigFile,
            autoCertifierTransport,
            maxMessageSize: this.options.maxMessageSize,
            geoIpDatabaseFolder: this.options.geoIpDatabaseFolder
        };
        this.websocketServerConnector = new WebsocketServerConnector(webSocketServerConnectorOptions);
        this.webrtcConnector = new WebrtcConnector({
            onNewConnection,
            transport: this.options.transport,
            iceServers: this.options.iceServers,
            allowPrivateAddresses: this.options.webrtcAllowPrivateAddresses,
            bufferThresholdLow: this.options.webrtcDatachannelBufferThresholdLow,
            bufferThresholdHigh: this.options.webrtcDatachannelBufferThresholdHigh,
            externalIp: this.options.externalIp,
            portRange: this.options.webrtcPortRange,
            maxMessageSize: this.options.maxMessageSize
        });
        await this.websocketServerConnector.start();
        // TODO: generate a PeerDescriptor in a single function. Requires changes to the createOwnPeerDescriptor
        // function in the options. Currently it's given by the DhtNode and it sets the PeerDescriptor for the
        // DhtNode in each call. 
        // LocalPeerDescriptor could be stored in one place and passed from there to the connectors
        const temporarilySelfSigned = (!this.options.tlsCertificate && this.options.websocketServerEnableTls === true);
        const connectivityResponse = await this.websocketServerConnector.checkConnectivity(temporarilySelfSigned);
        const localPeerDescriptor = await this.options.createLocalPeerDescriptor(connectivityResponse);
        this.setLocalPeerDescriptor(localPeerDescriptor);
        if (localPeerDescriptor.websocket && !this.options.tlsCertificate && this.options.websocketServerEnableTls) {
            try {
                await this.websocketServerConnector.autoCertify();
                const connectivityResponse = await this.websocketServerConnector.checkConnectivity(false);
                const autocertifiedLocalPeerDescriptor = await this.options.createLocalPeerDescriptor(connectivityResponse);
                if (autocertifiedLocalPeerDescriptor.websocket !== undefined) {
                    this.setLocalPeerDescriptor(autocertifiedLocalPeerDescriptor);
                }
                else {
                    logger$j.warn('Connectivity check failed after auto-certification, disabling WebSocket server TLS');
                    await this.restartWebsocketServerConnector({
                        ...webSocketServerConnectorOptions,
                        serverEnableTls: false
                    });
                }
            }
            catch (err) {
                logger$j.warn('Failed to auto-certify, disabling WebSocket server TLS', { err });
                await this.restartWebsocketServerConnector({
                    ...webSocketServerConnectorOptions,
                    serverEnableTls: false
                });
            }
        }
    }
    setLocalPeerDescriptor(peerDescriptor) {
        this.localPeerDescriptor = peerDescriptor;
        this.websocketServerConnector.setLocalPeerDescriptor(peerDescriptor);
        this.websocketClientConnector.setLocalPeerDescriptor(peerDescriptor);
        this.webrtcConnector.setLocalPeerDescriptor(peerDescriptor);
    }
    async restartWebsocketServerConnector(options) {
        await this.websocketServerConnector.destroy();
        this.websocketServerConnector = new WebsocketServerConnector(options);
        await this.websocketServerConnector.start();
        const connectivityResponse = await this.websocketServerConnector.checkConnectivity(false);
        const localPeerDescriptor = await this.options.createLocalPeerDescriptor(connectivityResponse);
        this.setLocalPeerDescriptor(localPeerDescriptor);
    }
    createConnection(peerDescriptor) {
        if (this.websocketClientConnector.isPossibleToFormConnection(peerDescriptor)) {
            return this.websocketClientConnector.connect(peerDescriptor);
        }
        else if (this.websocketServerConnector.isPossibleToFormConnection(peerDescriptor)) {
            return this.websocketServerConnector.connect(peerDescriptor);
        }
        else {
            return this.webrtcConnector.connect(peerDescriptor, false);
        }
    }
    getLocalPeerDescriptor() {
        return this.localPeerDescriptor;
    }
    async stop() {
        this.websocketConnectorRpcCommunicator.destroy();
        await this.websocketServerConnector.destroy();
        await this.websocketClientConnector.destroy();
        await this.webrtcConnector.stop();
    }
}
class SimulatorConnectorFacade {
    localPeerDescriptor;
    simulatorConnector;
    simulator;
    constructor(localPeerDescriptor, simulator) {
        this.localPeerDescriptor = localPeerDescriptor;
        this.simulator = simulator;
    }
    async start(onNewConnection) {
        logger$j.trace(`Creating SimulatorConnector`);
        this.simulatorConnector = new SimulatorConnector(this.localPeerDescriptor, this.simulator, onNewConnection);
        this.simulator.addConnector(this.simulatorConnector);
    }
    createConnection(peerDescriptor) {
        return this.simulatorConnector.connect(peerDescriptor);
    }
    getLocalPeerDescriptor() {
        return this.localPeerDescriptor;
    }
    async stop() {
        await this.simulatorConnector.stop();
    }
}

function convertUnsignedIntegerToBuffer(number) {
    const buffer = Buffer.alloc(4);
    buffer.writeUInt32BE(number);
    return buffer;
}
const createPeerDescriptorSignaturePayload = (peerDescriptor) => {
    const separator = Buffer.from(',');
    const buffers = [
        peerDescriptor.type !== undefined ? convertUnsignedIntegerToBuffer(peerDescriptor.type) : new Uint8Array(0),
        separator,
        peerDescriptor.udp !== undefined ? ConnectivityMethod.toBinary(peerDescriptor.udp) : new Uint8Array(0),
        separator,
        peerDescriptor.tcp !== undefined ? ConnectivityMethod.toBinary(peerDescriptor.tcp) : new Uint8Array(0),
        separator,
        peerDescriptor.websocket !== undefined ? ConnectivityMethod.toBinary(peerDescriptor.websocket) : new Uint8Array(0),
        separator,
        peerDescriptor.region !== undefined ? convertUnsignedIntegerToBuffer(peerDescriptor.region) : new Uint8Array(0),
        separator,
        peerDescriptor.ipAddress !== undefined ? convertUnsignedIntegerToBuffer(peerDescriptor.ipAddress) : new Uint8Array(0),
        separator,
        peerDescriptor.publicKey !== undefined ? Buffer.from(peerDescriptor.publicKey) : new Uint8Array(0)
    ];
    return Buffer.concat(buffers);
};

const signingUtil = new EcdsaSecp256k1Evm();
const calculateNodeIdRaw = async (ipAddress, privateKey) => {
    // nodeId is calculated as 
    // concatenate(
    //   get104leastSignificatBits(hash(ipAddress)), 
    //   get56leastSignificatBits(sign(ipAddress))
    // )
    const ipAsBuffer = Buffer.alloc(4);
    ipAsBuffer.writeUInt32BE(ipAddress);
    const ipHash = signingUtil.keccakHash(ipAsBuffer);
    const signature = await signingUtil.createSignature(ipAsBuffer, privateKey);
    const nodeIdRaw = Buffer.concat([
        ipHash.subarray(ipHash.length - 13, ipHash.length),
        signature.subarray(signature.length - 7, signature.length)
    ]);
    return nodeIdRaw;
};
const createPeerDescriptor = async (connectivityResponse, region, nodeId) => {
    const privateKey = randomBytes(32);
    const publicKey = randomBytes(20); // TODO calculate publicKey from privateKey
    let nodeIdRaw;
    if (nodeId !== undefined) {
        nodeIdRaw = toDhtAddressRaw(nodeId);
    }
    else {
        nodeIdRaw = await calculateNodeIdRaw(connectivityResponse.ipAddress, privateKey);
    }
    const ret = {
        nodeId: nodeIdRaw,
        type: NodeType.BROWSER ,
        ipAddress: connectivityResponse.ipAddress,
        region,
        publicKey
    };
    if (connectivityResponse.websocket) {
        ret.websocket = {
            host: connectivityResponse.websocket.host,
            port: connectivityResponse.websocket.port,
            tls: connectivityResponse.websocket.tls
        };
    }
    ret.signature = await signingUtil.createSignature(createPeerDescriptorSignaturePayload(ret), privateKey);
    return ret;
};

class Contact {
    peerDescriptor;
    constructor(peerDescriptor) {
        this.peerDescriptor = peerDescriptor;
    }
    getPeerDescriptor() {
        return this.peerDescriptor;
    }
    getNodeId() {
        return toNodeId(this.peerDescriptor);
    }
}

const getClosestNodes = (referenceId, contacts, opts) => {
    const list = new SortedContactList({
        referenceId,
        allowToContainReferenceId: true,
        excludedNodeIds: opts?.excludedNodeIds,
        maxSize: opts?.maxCount
    });
    for (const contact of contacts) {
        list.addContact(new Contact(contact));
    }
    return list.getClosestContacts().map((n) => n.getPeerDescriptor());
};

const logger$i = new Logger('DhtNodeRpcLocal');
class DhtNodeRpcLocal {
    options;
    constructor(options) {
        this.options = options;
    }
    // TODO rename to getClosestNeighbors (breaking change)
    async getClosestPeers(request, context) {
        this.options.addContact(context.incomingSourceDescriptor);
        const peers = getClosestNodes(toDhtAddress(request.nodeId), this.options.getNeighbors(), { maxCount: this.options.peerDiscoveryQueryBatchSize });
        const response = {
            peers,
            requestId: request.requestId
        };
        return response;
    }
    // TODO rename to getClosestRingContacts (breaking change)
    async getClosestRingPeers(request, context) {
        this.options.addContact(context.incomingSourceDescriptor);
        const closestContacts = this.options.getClosestRingContactsTo(request.ringId, this.options.peerDiscoveryQueryBatchSize);
        const response = {
            leftPeers: closestContacts.left,
            rightPeers: closestContacts.right,
            requestId: request.requestId
        };
        return response;
    }
    async ping(request, context) {
        logger$i.trace('received ping request: ' + toNodeId(context.incomingSourceDescriptor));
        setImmediate(() => {
            this.options.addContact(context.incomingSourceDescriptor);
        });
        const response = {
            requestId: request.requestId
        };
        return response;
    }
    async leaveNotice(context) {
        // TODO check signature??
        const sender = context.incomingSourceDescriptor;
        const senderNodeId = toNodeId(sender);
        logger$i.trace('received leave notice: ' + senderNodeId);
        this.options.removeContact(senderNodeId);
        return {};
    }
}

const logger$h = new Logger('DhtNodeRpcRemote');
class DhtNodeRpcRemote extends RpcRemote {
    static counter = 0;
    vectorClock;
    id;
    serviceId;
    constructor(localPeerDescriptor, peerDescriptor, serviceId, rpcCommunicator, rpcRequestTimeout) {
        super(localPeerDescriptor, peerDescriptor, rpcCommunicator, DhtNodeRpcClient, rpcRequestTimeout);
        this.id = this.getPeerDescriptor().nodeId;
        this.vectorClock = DhtNodeRpcRemote.counter++;
        this.serviceId = serviceId;
    }
    async getClosestPeers(nodeId) {
        logger$h.trace(`Requesting getClosestPeers on ${this.serviceId} from ${this.getNodeId()}`);
        const request = {
            nodeId: toDhtAddressRaw(nodeId),
            requestId: v4()
        };
        try {
            const peers = await this.getClient().getClosestPeers(request, this.formDhtRpcOptions());
            return peers.peers;
        }
        catch (err) {
            logger$h.trace(`getClosestPeers error ${this.serviceId}`, { err });
            throw err;
        }
    }
    // TODO rename to getClosestRingContacts (breaking change)
    async getClosestRingPeers(ringIdRaw) {
        logger$h.trace(`Requesting getClosestRingPeers on ${this.serviceId} from ${this.getNodeId()}`);
        const request = {
            ringId: ringIdRaw,
            requestId: v4()
        };
        try {
            const response = await this.getClient().getClosestRingPeers(request, this.formDhtRpcOptions());
            return { left: response.leftPeers ?? [], right: response.rightPeers ?? [] };
        }
        catch (err) {
            logger$h.trace(`getClosestRingPeers error ${this.serviceId}`, { err });
            throw err;
        }
    }
    async ping() {
        logger$h.trace(`Requesting ping on ${this.serviceId} from ${this.getNodeId()}`);
        const request = {
            requestId: v4()
        };
        const options = this.formDhtRpcOptions();
        try {
            const pong = await this.getClient().ping(request, options);
            if (pong.requestId === request.requestId) {
                return true;
            }
        }
        catch (err) {
            logger$h.trace(`ping failed on ${this.serviceId} to ${this.getNodeId()}`, { err });
        }
        return false;
    }
    leaveNotice() {
        logger$h.trace(`Sending leaveNotice on ${this.serviceId} from ${this.getNodeId()}`);
        const options = this.formDhtRpcOptions({
            notification: true
        });
        this.getClient().leaveNotice({}, options).catch((e) => {
            logger$h.trace('Failed to send leaveNotice' + e);
        });
    }
    getNodeId() {
        return toNodeId(this.getPeerDescriptor());
    }
}

class ExternalApiRpcLocal {
    options;
    constructor(options) {
        this.options = options;
    }
    async externalFetchData(request, context) {
        const senderPeerDescriptor = context.incomingSourceDescriptor;
        const result = await this.options.executeRecursiveOperation(toDhtAddress(request.key), RecursiveOperation.FETCH_DATA, toNodeId(senderPeerDescriptor));
        return ExternalFetchDataResponse.create({ entries: result.dataEntries ?? [] });
    }
    async externalStoreData(request, context) {
        const senderPeerDescriptor = context.incomingSourceDescriptor;
        const result = await this.options.storeDataToDht(toDhtAddress(request.key), request.data, toNodeId(senderPeerDescriptor));
        return ExternalStoreDataResponse.create({
            storers: result
        });
    }
    async externalFindClosestNodes(request, context) {
        const senderPeerDescriptor = context.incomingSourceDescriptor;
        const result = await this.options.executeRecursiveOperation(toDhtAddress(request.nodeId), RecursiveOperation.FIND_CLOSEST_NODES, toNodeId(senderPeerDescriptor));
        return ExternalFindClosestNodesResponse.create({
            closestNodes: result.closestNodes
        });
    }
}

const DEFAULT_TIMEOUT = 10000;
class ExternalApiRpcRemote extends RpcRemote {
    async externalFetchData(key) {
        const request = {
            key: toDhtAddressRaw(key)
        };
        const options = this.formDhtRpcOptions({
            // TODO use options option or named constant?
            timeout: DEFAULT_TIMEOUT
        });
        try {
            const data = await this.getClient().externalFetchData(request, options);
            return data.entries;
        }
        catch {
            return [];
        }
    }
    async storeData(key, data) {
        const request = {
            key: toDhtAddressRaw(key),
            data
        };
        const options = this.formDhtRpcOptions({
            // TODO use options option or named constant?
            timeout: DEFAULT_TIMEOUT
        });
        try {
            const response = await this.getClient().externalStoreData(request, options);
            return response.storers;
        }
        catch {
            return [];
        }
    }
    async externalFindClosestNode(key) {
        const request = {
            nodeId: toDhtAddressRaw(key)
        };
        const options = this.formDhtRpcOptions({
            timeout: DEFAULT_TIMEOUT
        });
        try {
            const response = await this.getClient().externalFindClosestNodes(request, options);
            return response.closestNodes;
        }
        catch {
            return [];
        }
    }
}

class ContactList extends EventEmitter {
    contactsById = new Map();
    // TODO move this to SortedContactList
    contactIds = [];
    localNodeId;
    maxSize;
    constructor(localNodeId, maxSize) {
        super();
        this.localNodeId = localNodeId;
        this.maxSize = maxSize;
    }
    getContact(id) {
        return this.contactsById.get(id);
    }
    getSize() {
        return this.contactIds.length;
    }
    clear() {
        this.contactsById.clear();
        this.contactIds = [];
    }
    stop() {
        this.removeAllListeners();
        this.clear();
    }
}

class RandomContactList extends ContactList {
    randomness;
    constructor(localNodeId, maxSize, randomness = 0.20) {
        super(localNodeId, maxSize);
        this.randomness = randomness;
    }
    addContact(contact) {
        if (this.localNodeId === contact.getNodeId()) {
            return;
        }
        if (!this.contactsById.has(contact.getNodeId())) {
            const roll = Math.random();
            if (roll < this.randomness) {
                if (this.getSize() === this.maxSize && this.getSize() > 0) {
                    const toRemove = this.contactIds[0];
                    this.removeContact(toRemove);
                }
                this.contactIds.push(contact.getNodeId());
                this.contactsById.set(contact.getNodeId(), contact);
                this.emit('contactAdded', contact);
            }
        }
    }
    removeContact(id) {
        if (this.contactsById.has(id)) {
            const removed = this.contactsById.get(id);
            const index = this.contactIds.findIndex((nodeId) => (nodeId === id));
            this.contactIds.splice(index, 1);
            this.contactsById.delete(id);
            this.emit('contactRemoved', removed);
            return true;
        }
        return false;
    }
    getContacts(limit) {
        const items = (limit === undefined)
            ? this.contactIds
            : this.contactIds.slice(0, Math.max(limit, 0));
        return items.map((contactId) => this.contactsById.get(contactId));
    }
}

const RING_SIZE = 2 ** 120 - 1; // 2^120 - 1
const binaryToBigInt = (binary) => {
    return binary.reduce((acc, val) => (acc << BigInt(8)) | BigInt(val), BigInt(0));
};
const getRingIdFromRaw = (raw) => Number(binaryToBigInt(raw));
const getRingIdRawFromPeerDescriptor = (peerDescriptor) => {
    const regionAsBuffer = Buffer.alloc(4);
    regionAsBuffer.writeUInt32BE(peerDescriptor.region ?? 0, 0);
    const ipAsbuffer = Buffer.alloc(4);
    ipAsbuffer.writeUInt32BE(peerDescriptor.ipAddress ?? 0, 0);
    const uniquePartAsBuffer = Buffer.from(peerDescriptor.nodeId.subarray(peerDescriptor.nodeId.length - 7, peerDescriptor.nodeId.length));
    const arr = [
        regionAsBuffer,
        ipAsbuffer,
        uniquePartAsBuffer
    ];
    const buffer = Buffer.concat(arr);
    return new Uint8Array(buffer);
};
const getRingIdFromPeerDescriptor = (peerDescriptor) => {
    const raw = getRingIdRawFromPeerDescriptor(peerDescriptor);
    return Number(binaryToBigInt(raw));
};
const getLeftDistance = (referenceId, id) => {
    const diff = Math.abs(referenceId - id);
    if (referenceId > id) {
        // if id is smaller than referenceId, then the distance is the difference
        return diff;
    }
    else {
        // if id is bigger than referenceId, then the distance is the ringSize - difference
        return RING_SIZE - diff;
    }
};
const getRightDistance = (referenceId, id) => {
    const diff = Math.abs(referenceId - id);
    if (referenceId > id) {
        // if id is smaller than referenceId, then the distance is the ringSize - difference
        return RING_SIZE - diff;
    }
    else {
        // if id is bigger than referenceId, then the distance is the difference
        return diff;
    }
};

class RingContactList extends EventEmitter {
    numNeighborsPerSide = 5;
    referenceId;
    excludedIds;
    leftNeighbors;
    rightNeighbors;
    constructor(rawReferenceId, excludedIds) {
        super();
        this.referenceId = getRingIdFromRaw(rawReferenceId);
        this.excludedIds = excludedIds ?? new Set();
        this.leftNeighbors = new OrderedMap();
        this.rightNeighbors = new OrderedMap();
    }
    addContact(contact) {
        const id = getRingIdFromPeerDescriptor(contact.getPeerDescriptor());
        if (id === this.referenceId || this.excludedIds.has(toNodeId(contact.getPeerDescriptor()))) {
            return;
        }
        let elementAdded = false;
        let elementRemoved = false;
        const leftDistance = getLeftDistance(this.referenceId, id);
        const lastLeftNeighbor = this.leftNeighbors.back();
        if (lastLeftNeighbor === undefined || leftDistance < lastLeftNeighbor[0]) {
            this.leftNeighbors.setElement(leftDistance, contact);
            elementAdded = true;
            if (this.leftNeighbors.size() > this.numNeighborsPerSide) {
                this.leftNeighbors.eraseElementByIterator(this.leftNeighbors.rBegin());
                elementRemoved = true;
            }
        }
        const rightDistance = getRightDistance(this.referenceId, id);
        const lastRightNeighbor = this.rightNeighbors.back();
        if (lastRightNeighbor === undefined || rightDistance < lastRightNeighbor[0]) {
            this.rightNeighbors.setElement(rightDistance, contact);
            elementAdded = true;
            if (this.rightNeighbors.size() > this.numNeighborsPerSide) {
                this.rightNeighbors.eraseElementByIterator(this.rightNeighbors.rBegin());
                elementRemoved = true;
            }
        }
        if (this.hasEventListeners() && (elementAdded || elementRemoved)) {
            if (elementAdded) {
                this.emit('contactAdded', contact);
            }
            if (elementRemoved) {
                this.emit('contactRemoved', contact);
            }
        }
    }
    removeContact(contact) {
        if (contact === undefined) {
            return;
        }
        const id = getRingIdFromPeerDescriptor(contact.getPeerDescriptor());
        const leftDistance = getLeftDistance(this.referenceId, id);
        const rightDistance = getRightDistance(this.referenceId, id);
        let elementRemoved = false;
        if (this.leftNeighbors.eraseElementByKey(leftDistance)) {
            elementRemoved = true;
        }
        if (this.rightNeighbors.eraseElementByKey(rightDistance)) {
            elementRemoved = true;
        }
        if (this.hasEventListeners() && elementRemoved) {
            this.emit('contactRemoved', contact);
        }
    }
    getContact(peerDescriptor) {
        const id = getRingIdFromPeerDescriptor(peerDescriptor);
        const leftDistance = getLeftDistance(this.referenceId, id);
        const rightDistance = getRightDistance(this.referenceId, id);
        if (this.leftNeighbors.getElementByKey(leftDistance)) {
            return this.leftNeighbors.getElementByKey(leftDistance);
        }
        if (this.rightNeighbors.getElementByKey(rightDistance)) {
            return this.rightNeighbors.getElementByKey(rightDistance);
        }
        return undefined;
    }
    getClosestContacts(limitPerSide) {
        const leftContacts = [];
        const rightContacts = [];
        let leftCount = 0;
        for (const item of this.leftNeighbors) {
            if (limitPerSide != undefined && leftCount >= limitPerSide) {
                break;
            }
            leftContacts.push(item[1]);
            leftCount++;
        }
        let rightCount = 0;
        for (const item of this.rightNeighbors) {
            if (limitPerSide != undefined && rightCount >= limitPerSide) {
                break;
            }
            rightContacts.push(item[1]);
            rightCount++;
        }
        return { left: leftContacts, right: rightContacts };
    }
    getAllContacts() {
        const ret = [];
        for (const item of this.leftNeighbors) {
            ret.push(item[1]);
        }
        for (const item of this.rightNeighbors) {
            ret.push(item[1]);
        }
        return ret;
    }
    hasEventListeners() {
        return this.eventNames().length > 0;
    }
}

const logger$g = new Logger('PeerManager');
// Returns all offline nodes, sets contacts as active if they are online
const pingNodes = async (nodes, activeContacts) => {
    const offlineNeighbors = [];
    await Promise.allSettled(nodes.map(async (contact) => {
        const isOnline = await contact.ping();
        if (!isOnline) {
            activeContacts.delete(contact.getNodeId());
            offlineNeighbors.push(contact.getPeerDescriptor());
        }
        else {
            activeContacts.add(contact.getNodeId());
        }
    }));
    return offlineNeighbors;
};
class PeerManager extends EventEmitter {
    // Glossary: 
    // * 'neighbors' are the nodes that are our neighbors according to 
    //   the protocol of the layer we are in
    // * 'connections' are the nodes that are connected to this node on Layer0
    // * 'contacts' are all non-unresponsive nodes that we know about
    neighbors;
    nearbyContacts;
    activeContacts;
    ringContacts;
    randomContacts;
    stopped = false;
    options;
    constructor(options) {
        super();
        this.options = options;
        this.neighbors = new KBucket({
            localNodeId: toDhtAddressRaw(this.options.localNodeId),
            numberOfNodesPerKBucket: this.options.numberOfNodesPerKBucket,
            numberOfNodesToPing: this.options.numberOfNodesPerKBucket
        });
        this.ringContacts = new RingContactList(getRingIdRawFromPeerDescriptor(this.options.localPeerDescriptor));
        this.ringContacts.on('contactAdded', (contact) => {
            this.emit('ringContactAdded', contact.getPeerDescriptor());
        });
        this.ringContacts.on('contactRemoved', (contact) => {
            this.emit('ringContactRemoved', contact.getPeerDescriptor());
        });
        this.neighbors.on('ping', (oldContacts, newContact) => this.onKBucketPing(oldContacts, newContact));
        this.neighbors.on('removed', (contact) => this.onKBucketRemoved(toNodeId(contact.getPeerDescriptor())));
        this.neighbors.on('added', (contact) => this.onKBucketAdded(contact));
        this.neighbors.on('updated', () => {
            // TODO: Update contact info to the connection manager and reconnect
        });
        this.nearbyContacts = new SortedContactList({
            referenceId: this.options.localNodeId,
            maxSize: this.options.maxContactCount,
            allowToContainReferenceId: false
        });
        this.nearbyContacts.on('contactRemoved', (contact) => {
            if (this.stopped) {
                return;
            }
            this.emit('nearbyContactRemoved', contact.getPeerDescriptor());
            this.randomContacts.addContact(this.options.createDhtNodeRpcRemote(contact.getPeerDescriptor()));
        });
        this.nearbyContacts.on('contactAdded', (contact) => this.emit('nearbyContactAdded', contact.getPeerDescriptor()));
        this.activeContacts = new Set();
        this.randomContacts = new RandomContactList(this.options.localNodeId, this.options.maxContactCount);
        this.randomContacts.on('contactRemoved', (removedContact) => this.emit('randomContactRemoved', removedContact.getPeerDescriptor()));
        this.randomContacts.on('contactAdded', (contactAdded) => this.emit('randomContactAdded', contactAdded.getPeerDescriptor()));
    }
    onKBucketPing(oldContacts, newContact) {
        if (this.stopped) {
            return;
        }
        const sortingList = new SortedContactList({
            referenceId: this.options.localNodeId,
            allowToContainReferenceId: false
        });
        sortingList.addContacts(oldContacts);
        const removableNodeId = sortingList.getFurthestContacts(1)[0].getNodeId();
        this.options.connectionLocker?.weakUnlockConnection(removableNodeId, this.options.lockId);
        this.neighbors.remove(toDhtAddressRaw(removableNodeId));
        this.neighbors.add(newContact);
    }
    onKBucketRemoved(nodeId) {
        if (this.stopped) {
            return;
        }
        this.options.connectionLocker?.weakUnlockConnection(nodeId, this.options.lockId);
        logger$g.trace(`Removed contact ${nodeId}`);
        if (this.neighbors.count() === 0) {
            this.emit('kBucketEmpty');
        }
    }
    onKBucketAdded(contact) {
        if (this.stopped) {
            return;
        }
        if (contact.getNodeId() !== this.options.localNodeId) {
            const peerDescriptor = contact.getPeerDescriptor();
            const nodeId = toNodeId(peerDescriptor);
            // Important to lock here, before the ping result is known
            this.options.connectionLocker?.weakLockConnection(nodeId, this.options.lockId);
            if (this.options.hasConnection(contact.getNodeId())
                || (this.options.neighborPingLimit !== undefined && this.neighbors.count() > this.options.neighborPingLimit)) {
                logger$g.trace(`Added new contact ${nodeId}`);
            }
            else { // open connection by pinging
                logger$g.trace('starting ping ' + nodeId);
                contact.ping().then((result) => {
                    if (result) {
                        logger$g.trace(`Added new contact ${nodeId}`);
                    }
                    else {
                        logger$g.trace('ping failed ' + nodeId);
                        this.options.connectionLocker?.weakUnlockConnection(nodeId, this.options.lockId);
                        this.removeContact(nodeId);
                        this.addNearbyContactToNeighbors();
                    }
                }).catch((_e) => {
                    this.options.connectionLocker?.weakUnlockConnection(nodeId, this.options.lockId);
                    this.removeContact(nodeId);
                    this.addNearbyContactToNeighbors();
                });
            }
        }
    }
    addNearbyContactToNeighbors() {
        if (this.stopped) {
            return;
        }
        const closest = this.getNearbyActiveContactNotInNeighbors();
        if (closest) {
            this.addContact(closest.getPeerDescriptor());
        }
    }
    getNearbyActiveContactNotInNeighbors() {
        for (const contactId of this.nearbyContacts.getContactIds()) {
            if (!this.neighbors.get(toDhtAddressRaw(contactId)) && this.activeContacts.has(contactId)) {
                return this.nearbyContacts.getContact(contactId);
            }
        }
        return undefined;
    }
    removeContact(nodeId) {
        if (this.stopped) {
            return;
        }
        logger$g.trace(`Removing contact ${nodeId}`);
        this.ringContacts.removeContact(this.nearbyContacts.getContact(nodeId));
        this.neighbors.remove(toDhtAddressRaw(nodeId));
        this.nearbyContacts.removeContact(nodeId);
        this.activeContacts.delete(nodeId);
        this.randomContacts.removeContact(nodeId);
    }
    removeNeighbor(nodeId) {
        this.neighbors.remove(toDhtAddressRaw(nodeId));
    }
    async pruneOfflineNodes(nodes) {
        logger$g.trace('Pruning offline nodes', { nodes: nodes.length });
        const offlineNeighbors = await pingNodes(nodes, this.activeContacts);
        offlineNeighbors.forEach((offlineNeighbor) => {
            logger$g.trace('Removing offline node', { node: toNodeId(offlineNeighbor) });
            this.removeContact(toNodeId(offlineNeighbor));
        });
    }
    stop() {
        this.stopped = true;
        this.neighbors.toArray().forEach((rpcRemote) => {
            rpcRemote.leaveNotice();
            this.neighbors.remove(rpcRemote.id);
        });
        this.neighbors.removeAllListeners();
        this.ringContacts.getAllContacts().forEach((rpcRemote) => {
            rpcRemote.leaveNotice();
            this.ringContacts.removeContact(rpcRemote);
        });
        this.nearbyContacts.stop();
        this.randomContacts.stop();
    }
    getNearbyContacts() {
        return this.nearbyContacts;
    }
    getClosestRingContactsTo(ringIdRaw, limit, excludedIds) {
        const closest = new RingContactList(ringIdRaw, excludedIds);
        this.ringContacts.getAllContacts().map((contact) => closest.addContact(contact));
        // TODO use options option or named constant?
        return closest.getClosestContacts(limit ?? 8);
    }
    getRandomContacts() {
        return this.randomContacts;
    }
    getRingContacts() {
        return this.ringContacts;
    }
    getNearbyContactCount(excludedNodeIds) {
        return this.nearbyContacts.getSize(excludedNodeIds);
    }
    getNeighborCount() {
        return this.neighbors.count();
    }
    getNeighbors() {
        return this.neighbors.toArray();
    }
    setContactActive(nodeId) {
        this.activeContacts.add(nodeId);
    }
    addContact(peerDescriptor) {
        if (this.stopped) {
            return;
        }
        const nodeId = toNodeId(peerDescriptor);
        if (nodeId !== this.options.localNodeId) {
            logger$g.trace(`Adding new contact ${nodeId}`);
            const remote = this.options.createDhtNodeRpcRemote(peerDescriptor);
            const isInNeighbors = (this.neighbors.get(peerDescriptor.nodeId) !== null);
            const isInNearbyContacts = (this.nearbyContacts.getContact(nodeId) !== undefined);
            const isInRingContacts = this.ringContacts.getContact(peerDescriptor) !== undefined;
            if (isInNeighbors || isInNearbyContacts) {
                this.randomContacts.addContact(remote);
            }
            if (!isInNeighbors) {
                this.neighbors.add(remote);
            }
            if (!isInNearbyContacts) {
                this.nearbyContacts.addContact(remote);
            }
            if (!isInRingContacts) {
                this.ringContacts.addContact(remote);
            }
        }
    }
}

const logger$f = new Logger('DiscoverySession');
class DiscoverySession {
    id = v4();
    noProgressCounter = 0;
    ongoingRequests = new Set();
    doneGate = new Gate(false);
    options;
    constructor(options) {
        this.options = options;
    }
    addContacts(contacts) {
        if (this.options.abortSignal.aborted || this.doneGate.isOpen()) {
            return;
        }
        for (const contact of contacts) {
            this.options.peerManager.addContact(contact);
        }
    }
    async fetchClosestNeighborsFromRemote(peerDescriptor) {
        if (this.options.abortSignal.aborted || this.doneGate.isOpen()) {
            return [];
        }
        const nodeId = toNodeId(peerDescriptor);
        logger$f.trace(`Getting closest neighbors from remote: ${nodeId}`);
        this.options.contactedPeers.add(nodeId);
        const remote = this.options.createDhtNodeRpcRemote(peerDescriptor);
        const returnedContacts = await remote.getClosestPeers(this.options.targetId);
        this.options.peerManager.setContactActive(nodeId);
        return returnedContacts;
    }
    onRequestSucceeded(nodeId, contacts) {
        if (!this.ongoingRequests.has(nodeId)) {
            return;
        }
        this.ongoingRequests.delete(nodeId);
        const targetId = toDhtAddressRaw(this.options.targetId);
        const oldClosestNeighbor = this.getClosestNeighbor();
        const oldClosestDistance = getPeerDistance(targetId, oldClosestNeighbor.nodeId);
        this.addContacts(contacts);
        const newClosestNeighbor = this.getClosestNeighbor();
        const newClosestDistance = getPeerDistance(targetId, newClosestNeighbor.nodeId);
        if (newClosestDistance >= oldClosestDistance) {
            this.noProgressCounter++;
        }
    }
    getClosestNeighbor() {
        return getClosestNodes(this.options.targetId, this.options.peerManager.getNeighbors().map((n) => n.getPeerDescriptor()), { maxCount: 1 })[0];
    }
    onRequestFailed(nodeId) {
        if (!this.ongoingRequests.has(nodeId)) {
            return;
        }
        this.ongoingRequests.delete(nodeId);
        this.options.peerManager.removeContact(nodeId);
    }
    findMoreContacts() {
        if (this.options.abortSignal.aborted || this.doneGate.isOpen()) {
            return;
        }
        const uncontacted = getClosestNodes(this.options.targetId, Array.from(this.options.peerManager.getNearbyContacts().getAllContactsInUndefinedOrder(), (c) => c.getPeerDescriptor()), {
            maxCount: this.options.parallelism,
            excludedNodeIds: this.options.contactedPeers
        });
        if ((uncontacted.length === 0 && this.ongoingRequests.size === 0) || (this.noProgressCounter >= this.options.noProgressLimit)) {
            this.doneGate.open();
            return;
        }
        for (const node of uncontacted) {
            if (this.ongoingRequests.size >= this.options.parallelism) {
                break;
            }
            const nodeId = toNodeId(node);
            this.ongoingRequests.add(nodeId);
            // eslint-disable-next-line promise/catch-or-return
            this.fetchClosestNeighborsFromRemote(node)
                .then((contacts) => this.onRequestSucceeded(nodeId, contacts))
                .catch(() => this.onRequestFailed(nodeId))
                .finally(() => {
                this.findMoreContacts();
            });
        }
    }
    async findClosestNodes(timeout) {
        if (this.options.peerManager.getNearbyContactCount(this.options.contactedPeers) === 0) {
            return;
        }
        setImmediate(() => {
            this.findMoreContacts();
        });
        await withTimeout(this.doneGate.waitUntilOpen(), timeout, 'discovery session timed out', this.options.abortSignal);
    }
}

const logger$e = new Logger('RingDiscoverySession');
class RingDiscoverySession {
    id = v4();
    noProgressCounter = 0;
    ongoingRequests = new Set();
    doneGate = new Gate(false);
    options;
    numContactedPeers = 0;
    targetIdAsRingId;
    constructor(options) {
        this.options = options;
        this.targetIdAsRingId = getRingIdFromRaw(this.options.targetId);
    }
    addContacts(contacts) {
        if (this.options.abortSignal.aborted || this.doneGate.isOpen()) {
            return;
        }
        for (const contact of contacts) {
            this.options.peerManager.addContact(contact);
        }
    }
    async fetchClosestContactsFromRemote(contact) {
        if (this.options.abortSignal.aborted || this.doneGate.isOpen()) {
            return { left: [], right: [] };
        }
        logger$e.trace(`Getting closest ring peers from contact: ${toNodeId(contact.getPeerDescriptor())}`);
        this.numContactedPeers++;
        this.options.contactedPeers.add(contact.getNodeId());
        const returnedContacts = await contact.getClosestRingPeers(this.options.targetId);
        this.options.peerManager.setContactActive(contact.getNodeId());
        return returnedContacts;
    }
    onRequestSucceeded(nodeId, contacts) {
        if (!this.ongoingRequests.has(nodeId)) {
            return;
        }
        this.ongoingRequests.delete(nodeId);
        const oldClosestContacts = this.options.peerManager.getClosestRingContactsTo(this.options.targetId, 1);
        const oldClosestLeftDistance = getLeftDistance(this.targetIdAsRingId, getRingIdFromPeerDescriptor(oldClosestContacts.left[0].getPeerDescriptor()));
        const oldClosestRightDistance = getLeftDistance(this.targetIdAsRingId, getRingIdFromPeerDescriptor(oldClosestContacts.right[0].getPeerDescriptor()));
        this.addContacts(contacts.left.concat(contacts.right));
        const newClosestContacts = this.options.peerManager.getClosestRingContactsTo(this.options.targetId, 1);
        const newClosestLeftDistance = getLeftDistance(this.targetIdAsRingId, getRingIdFromPeerDescriptor(newClosestContacts.left[0].getPeerDescriptor()));
        const newClosestRightDistance = getLeftDistance(this.targetIdAsRingId, getRingIdFromPeerDescriptor(newClosestContacts.right[0].getPeerDescriptor()));
        if (newClosestLeftDistance >= oldClosestLeftDistance && newClosestRightDistance >= oldClosestRightDistance) {
            this.noProgressCounter++;
        }
    }
    onRequestFailed(peer) {
        if (!this.ongoingRequests.has(peer.getNodeId())) {
            return;
        }
        this.ongoingRequests.delete(peer.getNodeId());
        this.options.peerManager.removeContact(peer.getNodeId());
    }
    findMoreContacts() {
        if (this.options.abortSignal.aborted || this.doneGate.isOpen()) {
            return;
        }
        const uncontacted = this.options.peerManager.getClosestRingContactsTo(this.options.targetId, this.options.parallelism, this.options.contactedPeers);
        if ((uncontacted.left.length === 0 && uncontacted.right.length === 0)
            || this.noProgressCounter >= this.options.noProgressLimit) {
            this.doneGate.open();
            return;
        }
        // ask from both sides equally
        const merged = [];
        const alreadyInMerged = new Set();
        const length = Math.max(uncontacted.left.length, uncontacted.right.length);
        for (let i = 0; i < length; i++) {
            if (i < uncontacted.left.length) {
                if (!alreadyInMerged.has(uncontacted.left[i].getNodeId())) {
                    merged.push(uncontacted.left[i]);
                    alreadyInMerged.add(uncontacted.left[i].getNodeId());
                }
            }
            if (i < uncontacted.right.length) {
                if (!alreadyInMerged.has(uncontacted.right[i].getNodeId())) {
                    merged.push(uncontacted.right[i]);
                    alreadyInMerged.add(uncontacted.right[i].getNodeId());
                }
            }
        }
        for (const nextPeer of merged) {
            if (this.ongoingRequests.size >= this.options.parallelism) {
                break;
            }
            this.ongoingRequests.add(nextPeer.getNodeId());
            // eslint-disable-next-line promise/catch-or-return
            this.fetchClosestContactsFromRemote(nextPeer)
                .then((contacts) => this.onRequestSucceeded(nextPeer.getNodeId(), contacts))
                .catch(() => this.onRequestFailed(nextPeer))
                .finally(() => {
                this.findMoreContacts();
            });
        }
    }
    async findClosestNodes(timeout) {
        if (this.options.peerManager.getNearbyContactCount(this.options.contactedPeers) === 0) {
            return;
        }
        setImmediate(() => {
            this.findMoreContacts();
        });
        await withTimeout(this.doneGate.waitUntilOpen(), timeout, 'discovery session timed out', this.options.abortSignal);
    }
}

// TODO move this to trackerless-network package and change serviceId to be a required paramater
const CONTROL_LAYER_NODE_SERVICE_ID = 'layer0';

const createDistantDhtAddress = (address) => {
    const raw = toDhtAddressRaw(address);
    const flipped = raw.map((val) => ~val);
    return toDhtAddress(flipped);
};
const logger$d = new Logger('PeerDiscovery');
class PeerDiscovery {
    ongoingDiscoverySessions = new Map();
    ongoingRingDiscoverySessions = new Map();
    rejoinOngoing = false;
    joinCalled = false;
    recoveryIntervalStarted = false;
    options;
    constructor(options) {
        this.options = options;
    }
    async joinDht(entryPoints, doAdditionalDistantPeerDiscovery = true, retry = true) {
        const contactedPeers = new Set();
        const distantJoinOptions = doAdditionalDistantPeerDiscovery
            ? { enabled: true, contactedPeers: new Set() } : { enabled: false };
        await Promise.all(entryPoints.map((entryPoint) => this.joinThroughEntryPoint(entryPoint, contactedPeers, distantJoinOptions, retry)));
    }
    async joinThroughEntryPoint(entryPointDescriptor, 
    // Note that this set is mutated by DiscoverySession
    contactedPeers, additionalDistantJoin, retry = true) {
        if (this.isStopped()) {
            return;
        }
        this.joinCalled = true;
        logger$d.debug(`Joining ${this.options.serviceId === CONTROL_LAYER_NODE_SERVICE_ID
            ? 'The Streamr Network' : `Control Layer for ${this.options.serviceId}`}`
            + ` via entrypoint ${toNodeId(entryPointDescriptor)}`);
        if (areEqualPeerDescriptors(entryPointDescriptor, this.options.localPeerDescriptor)) {
            return;
        }
        this.options.connectionLocker?.lockConnection(entryPointDescriptor, `${this.options.serviceId}::joinDht`);
        this.options.peerManager.addContact(entryPointDescriptor);
        const targetId = toNodeId(this.options.localPeerDescriptor);
        const sessions = [this.createSession(targetId, contactedPeers)];
        if (additionalDistantJoin.enabled) {
            sessions.push(this.createSession(createDistantDhtAddress(targetId), additionalDistantJoin.contactedPeers));
        }
        await this.runSessions(sessions, entryPointDescriptor, retry);
        this.options.connectionLocker?.unlockConnection(entryPointDescriptor, `${this.options.serviceId}::joinDht`);
    }
    async joinRing() {
        const contactedPeers = new Set();
        const sessions = [this.createRingSession(getRingIdRawFromPeerDescriptor(this.options.localPeerDescriptor), contactedPeers)];
        await this.runRingSessions(sessions);
    }
    createSession(targetId, contactedPeers) {
        const sessionOptions = {
            targetId,
            parallelism: this.options.parallelism,
            noProgressLimit: this.options.joinNoProgressLimit,
            peerManager: this.options.peerManager,
            contactedPeers,
            abortSignal: this.options.abortSignal,
            createDhtNodeRpcRemote: this.options.createDhtNodeRpcRemote
        };
        return new DiscoverySession(sessionOptions);
    }
    createRingSession(targetId, contactedPeers) {
        const sessionOptions = {
            targetId,
            parallelism: this.options.parallelism,
            noProgressLimit: this.options.joinNoProgressLimit,
            peerManager: this.options.peerManager,
            contactedPeers,
            abortSignal: this.options.abortSignal,
            createDhtNodeRpcRemote: this.options.createDhtNodeRpcRemote
        };
        return new RingDiscoverySession(sessionOptions);
    }
    async runSessions(sessions, entryPointDescriptor, retry) {
        try {
            for (const session of sessions) {
                this.ongoingDiscoverySessions.set(session.id, session);
                await session.findClosestNodes(this.options.joinTimeout);
            }
        }
        catch (_e) {
            logger$d.debug(`DHT join on ${this.options.serviceId} timed out`);
        }
        finally {
            if (!this.isStopped()) {
                if (this.options.peerManager.getNeighborCount() === 0) {
                    if (retry) {
                        // TODO should we catch possible promise rejection?
                        // TODO use options option or named constant?
                        setAbortableTimeout(() => this.rejoinDht(entryPointDescriptor), 1000, this.options.abortSignal);
                    }
                }
                else {
                    await this.ensureRecoveryIntervalIsRunning();
                }
            }
            sessions.forEach((session) => this.ongoingDiscoverySessions.delete(session.id));
        }
    }
    async runRingSessions(sessions) {
        try {
            for (const session of sessions) {
                this.ongoingRingDiscoverySessions.set(session.id, session);
                await session.findClosestNodes(this.options.joinTimeout);
            }
        }
        catch (_e) {
            logger$d.debug(`Ring join on ${this.options.serviceId} timed out`);
        }
        finally {
            sessions.forEach((session) => this.ongoingDiscoverySessions.delete(session.id));
        }
    }
    async rejoinDht(entryPoint, contactedPeers = new Set(), distantJoinContactPeers = new Set()) {
        if (this.isStopped() || this.rejoinOngoing) {
            return;
        }
        logger$d.debug(`Rejoining DHT ${this.options.serviceId}`);
        this.rejoinOngoing = true;
        try {
            await this.joinThroughEntryPoint(entryPoint, contactedPeers, { enabled: true, contactedPeers: distantJoinContactPeers });
            logger$d.debug(`Rejoined DHT successfully ${this.options.serviceId}!`);
        }
        catch {
            logger$d.warn(`Rejoining DHT ${this.options.serviceId} failed`);
            if (!this.isStopped()) {
                // TODO should we catch possible promise rejection?
                // TODO use options option or named constant?
                setAbortableTimeout(() => this.rejoinDht(entryPoint), 5000, this.options.abortSignal);
            }
        }
        finally {
            this.rejoinOngoing = false;
        }
    }
    async ensureRecoveryIntervalIsRunning() {
        if (!this.recoveryIntervalStarted) {
            this.recoveryIntervalStarted = true;
            // TODO use options option or named constant?
            await scheduleAtInterval(() => this.fetchClosestAndRandomNeighbors(), 60000, true, this.options.abortSignal);
        }
    }
    async fetchClosestAndRandomNeighbors() {
        if (this.isStopped()) {
            return;
        }
        const localNodeId = toNodeId(this.options.localPeerDescriptor);
        const nodes = this.getClosestNeighbors(localNodeId, this.options.parallelism);
        const randomNodes = this.getClosestNeighbors(randomDhtAddress(), 1);
        await Promise.allSettled([
            ...nodes.map(async (node) => {
                const remote = this.options.createDhtNodeRpcRemote(node);
                const contacts = await remote.getClosestPeers(localNodeId);
                for (const contact of contacts) {
                    this.options.peerManager.addContact(contact);
                }
            }),
            ...randomNodes.map(async (node) => {
                const remote = this.options.createDhtNodeRpcRemote(node);
                const contacts = await remote.getClosestPeers(randomDhtAddress());
                for (const contact of contacts) {
                    this.options.peerManager.addContact(contact);
                }
            })
        ]);
    }
    getClosestNeighbors(referenceId, maxCount) {
        return getClosestNodes(referenceId, this.options.peerManager.getNeighbors().map((n) => n.getPeerDescriptor()), { maxCount });
    }
    isJoinOngoing() {
        return !this.joinCalled ? true : this.ongoingDiscoverySessions.size > 0;
    }
    isJoinCalled() {
        return this.joinCalled;
    }
    isStopped() {
        return this.options.abortSignal.aborted;
    }
}

const getPreviousPeer = (routeMessage) => {
    return last(routeMessage.routingPath);
};

const logger$c = new Logger('RouterRpcRemote');
// default timeout
const ROUTING_TIMEOUT = 2000;
class RouterRpcRemote extends RpcRemote {
    async routeMessage(params) {
        const message = {
            target: params.target,
            sourcePeer: params.sourcePeer,
            message: params.message,
            requestId: params.requestId ?? v4(),
            reachableThrough: params.reachableThrough ?? [],
            routingPath: params.routingPath,
            parallelRootNodeIds: params.parallelRootNodeIds
        };
        const options = this.formDhtRpcOptions({
            connect: false
        });
        try {
            const ack = await this.getClient().routeMessage(message, options);
            // Success signal if sent to destination and error includes duplicate
            if (ack.error === RouteMessageError.DUPLICATE
                && areEqualBinaries(params.target, this.getPeerDescriptor().nodeId)) {
                return true;
            }
            else if (ack.error !== undefined) {
                return false;
            }
        }
        catch (err) {
            const previousPeer = getPreviousPeer(params);
            const fromNode = previousPeer
                ? toNodeId(previousPeer)
                : toNodeId(params.sourcePeer);
            const toNode = toNodeId(this.getPeerDescriptor());
            logger$c.trace(`Failed to send routeMessage from ${fromNode} to ${toNode}`, { err });
            return false;
        }
        return true;
    }
    async forwardMessage(params) {
        const message = {
            target: params.target,
            sourcePeer: params.sourcePeer,
            message: params.message,
            requestId: params.requestId ?? v4(),
            reachableThrough: params.reachableThrough ?? [],
            routingPath: params.routingPath,
            parallelRootNodeIds: params.parallelRootNodeIds
        };
        const options = this.formDhtRpcOptions({
            connect: false
        });
        try {
            const ack = await this.getClient().forwardMessage(message, options);
            if (ack.error !== undefined) {
                return false;
            }
        }
        catch (err) {
            const previousPeer = getPreviousPeer(params);
            const fromNode = previousPeer
                ? toNodeId(previousPeer)
                : toNodeId(params.sourcePeer);
            const toNode = toNodeId(this.getPeerDescriptor());
            logger$c.trace(`Failed to send forwardMessage from ${fromNode} to ${toNode}`, { err });
            return false;
        }
        return true;
    }
}

const logger$b = new Logger('RecursiveOperationRpcRemote');
class RecursiveOperationRpcRemote extends RpcRemote {
    async routeRequest(params) {
        const message = {
            target: params.target,
            sourcePeer: params.sourcePeer,
            message: params.message,
            requestId: params.requestId ?? v4(),
            reachableThrough: params.reachableThrough ?? [],
            routingPath: params.routingPath,
            parallelRootNodeIds: params.parallelRootNodeIds
        };
        const options = this.formDhtRpcOptions({
            connect: false
        });
        try {
            const ack = await this.getClient().routeRequest(message, options);
            if (ack.error !== undefined) {
                logger$b.trace('Next hop responded with error ' + ack.error);
                return false;
            }
        }
        catch (err) {
            const previousPeer = getPreviousPeer(params);
            const fromNode = previousPeer
                ? toNodeId(previousPeer)
                : toNodeId(params.sourcePeer);
            const toNode = toNodeId(this.getPeerDescriptor());
            logger$b.debug(`Failed to send routeRequest message from ${fromNode} to ${toNode}`, { err });
            return false;
        }
        return true;
    }
}

const logger$a = new Logger('RoutingSession');
const MAX_FAILED_HOPS = 2;
const ROUTING_TABLE_MAX_SIZE = 5;
class RoutingRemoteContact extends Contact {
    routerRpcRemote;
    recursiveOperationRpcRemote;
    constructor(peer, localPeerDescriptor, rpcCommunicator) {
        super(peer);
        this.routerRpcRemote = new RouterRpcRemote(localPeerDescriptor, peer, rpcCommunicator, RouterRpcClient, ROUTING_TIMEOUT);
        this.recursiveOperationRpcRemote = new RecursiveOperationRpcRemote(localPeerDescriptor, peer, rpcCommunicator, RecursiveOperationRpcClient, ROUTING_TIMEOUT);
    }
    getRouterRpcRemote() {
        return this.routerRpcRemote;
    }
    getRecursiveOperationRpcRemote() {
        return this.recursiveOperationRpcRemote;
    }
}
var RoutingMode;
(function (RoutingMode) {
    RoutingMode[RoutingMode["ROUTE"] = 0] = "ROUTE";
    RoutingMode[RoutingMode["FORWARD"] = 1] = "FORWARD";
    RoutingMode[RoutingMode["RECURSIVE"] = 2] = "RECURSIVE";
})(RoutingMode || (RoutingMode = {}));
class RoutingSession extends EventEmitter {
    sessionId = v4();
    ongoingRequests = new Set();
    contactedPeers = new Set();
    failedHopCounter = 0;
    successfulHopCounter = 0;
    stopped = false;
    options;
    constructor(options) {
        super();
        this.options = options;
    }
    onRequestFailed(nodeId) {
        logger$a.trace('onRequestFailed() sessionId: ' + this.sessionId);
        if (this.stopped) {
            return;
        }
        if (this.ongoingRequests.has(nodeId)) {
            this.ongoingRequests.delete(nodeId);
        }
        this.deleteParallelRootIfSource(nodeId);
        this.failedHopCounter += 1;
        if (this.failedHopCounter >= MAX_FAILED_HOPS) {
            logger$a.trace(`Stopping routing after ${MAX_FAILED_HOPS} failed attempts for sessionId: ${this.sessionId}`);
            this.emitFailure();
            return;
        }
        const contacts = this.updateAndGetRoutablePeers();
        if (contacts.length === 0 && this.ongoingRequests.size === 0) {
            logger$a.trace('routing failed, emitting routingFailed sessionId: ' + this.sessionId);
            this.emitFailure();
        }
        else {
            logger$a.trace('routing failed, retrying to route sessionId: ' + this.sessionId + ' failedHopCounter: ' + this.failedHopCounter);
            this.sendMoreRequests(contacts);
        }
    }
    emitFailure() {
        if (this.successfulHopCounter >= 1) {
            this.emit('partialSuccess');
        }
        else {
            this.emit('routingFailed');
        }
    }
    onRequestSucceeded() {
        logger$a.trace('onRequestSucceeded() sessionId: ' + this.sessionId);
        if (this.stopped) {
            return;
        }
        this.successfulHopCounter += 1;
        if (this.successfulHopCounter >= this.options.parallelism) {
            this.emit('routingSucceeded');
            return;
        }
        const contacts = this.updateAndGetRoutablePeers();
        if (contacts.length === 0) {
            this.emit('routingSucceeded');
        }
        else if (contacts.length > 0 && this.ongoingRequests.size === 0) {
            this.sendMoreRequests(contacts);
        }
    }
    async sendRouteMessageRequest(contact) {
        if (this.stopped) {
            return false;
        }
        const msg = {
            ...this.options.routedMessage,
            routingPath: this.options.routedMessage.routingPath.concat([this.options.localPeerDescriptor])
        };
        if (this.options.mode === RoutingMode.FORWARD) {
            return contact.getRouterRpcRemote().forwardMessage(msg);
        }
        else if (this.options.mode === RoutingMode.RECURSIVE) {
            return contact.getRecursiveOperationRpcRemote().routeRequest(msg);
        }
        else {
            return contact.getRouterRpcRemote().routeMessage(msg);
        }
    }
    updateAndGetRoutablePeers() {
        logger$a.trace('getRoutablePeers() sessionId: ' + this.sessionId);
        const previousPeer = getPreviousPeer(this.options.routedMessage);
        const previousId = previousPeer ? toNodeId(previousPeer) : undefined;
        const targetId = toDhtAddress(this.options.routedMessage.target);
        let routingTable;
        if (this.options.routingTablesCache.has(targetId, previousId) && this.options.routingTablesCache.get(targetId, previousId).getSize() > 0) {
            routingTable = this.options.routingTablesCache.get(targetId, previousId);
        }
        else {
            routingTable = new SortedContactList({
                referenceId: toDhtAddress(this.options.routedMessage.target),
                maxSize: ROUTING_TABLE_MAX_SIZE,
                allowToContainReferenceId: true,
                nodeIdDistanceLimit: previousId
            });
            const contacts = this.options.getConnections()
                .map((peer) => new RoutingRemoteContact(peer, this.options.localPeerDescriptor, this.options.rpcCommunicator));
            routingTable.addContacts(contacts);
            this.options.routingTablesCache.set(targetId, routingTable, previousId);
        }
        return routingTable.getClosestContacts()
            .filter((contact) => !this.contactedPeers.has(contact.getNodeId()) && !this.options.excludedNodeIds.has(contact.getNodeId()));
    }
    sendMoreRequests(uncontacted) {
        logger$a.trace('sendMoreRequests() sessionId: ' + this.sessionId);
        if (this.stopped) {
            return;
        }
        if (uncontacted.length === 0) {
            this.emitFailure();
            return;
        }
        while ((this.ongoingRequests.size < this.options.parallelism) && (uncontacted.length > 0) && !this.stopped) {
            const nextPeer = uncontacted.shift();
            logger$a.trace(`Sending routeMessage request to contact: ${toNodeId(nextPeer.getPeerDescriptor())} (sessionId=${this.sessionId})`);
            this.contactedPeers.add(nextPeer.getNodeId());
            this.ongoingRequests.add(nextPeer.getNodeId());
            this.addParallelRootIfSource(nextPeer.getNodeId());
            setImmediate(async () => {
                try {
                    const succeeded = await this.sendRouteMessageRequest(nextPeer);
                    if (succeeded) {
                        this.onRequestSucceeded();
                    }
                    else {
                        this.onRequestFailed(nextPeer.getNodeId());
                    }
                }
                catch (err) {
                    logger$a.debug('Unable to route message ', { err });
                }
                finally {
                    logger$a.trace('sendRouteMessageRequest returned');
                }
            });
        }
    }
    addParallelRootIfSource(nodeId) {
        if (this.options.mode === RoutingMode.RECURSIVE
            && areEqualPeerDescriptors(this.options.localPeerDescriptor, this.options.routedMessage.sourcePeer)) {
            this.options.routedMessage.parallelRootNodeIds.push(nodeId);
        }
    }
    deleteParallelRootIfSource(nodeId) {
        if (this.options.mode === RoutingMode.RECURSIVE
            && areEqualPeerDescriptors(this.options.localPeerDescriptor, this.options.routedMessage.sourcePeer)) {
            pull(this.options.routedMessage.parallelRootNodeIds, nodeId);
        }
    }
    stop() {
        this.stopped = true;
        this.emit('stopped');
        this.removeAllListeners();
    }
}

const logger$9 = new Logger('RecursiveOperationSessionRpcRemote');
class RecursiveOperationSessionRpcRemote extends RpcRemote {
    sendResponse(routingPath, closestConnectedNodes, dataEntries, noCloserNodesFound) {
        const report = {
            routingPath,
            closestConnectedNodes,
            dataEntries,
            noCloserNodesFound
        };
        this.getClient().sendResponse(report, this.formDhtRpcOptions()).catch((_e) => {
            logger$9.trace('Failed to send RecursiveOperationResponse');
        });
    }
}

const logger$8 = new Logger('RecursiveOperationSessionRpcLocal');
class RecursiveOperationSessionRpcLocal {
    options;
    constructor(options) {
        this.options = options;
    }
    async sendResponse(report, context) {
        const remoteNodeId = toNodeId(context.incomingSourceDescriptor);
        logger$8.trace('RecursiveOperationResponse arrived: ' + JSON.stringify(report));
        this.options.onResponseReceived(remoteNodeId, report.routingPath, report.closestConnectedNodes, report.dataEntries, report.noCloserNodesFound);
        return {};
    }
}

const RECURSIVE_OPERATION_TIMEOUT = 10 * 1000;
class RecursiveOperationSession extends EventEmitter {
    id = v4();
    rpcCommunicator;
    results;
    foundData = new Map();
    allKnownHops = new Set();
    reportedHops = new Set();
    timeoutTask;
    completionEventEmitted = false;
    noCloserNodesReceivedCounter = 0;
    noCloserNodesReceivedFrom = new Set();
    options;
    constructor(options) {
        super();
        this.options = options;
        this.results = new SortedContactList({
            referenceId: options.targetId,
            maxSize: 10, // TODO use options option or named constant?
            allowToContainReferenceId: true
        });
        this.rpcCommunicator = new ListeningRpcCommunicator(this.id, options.transport, {
            rpcRequestTimeout: RECURSIVE_OPERATION_TIMEOUT
        });
        this.registerLocalRpcMethods();
    }
    registerLocalRpcMethods() {
        const rpcLocal = new RecursiveOperationSessionRpcLocal({
            onResponseReceived: (remoteNodeId, routingPath, closestConnectedNodes, dataEntries, noCloserNodesFound) => {
                this.onResponseReceived(remoteNodeId, routingPath, closestConnectedNodes, dataEntries, noCloserNodesFound);
            }
        });
        this.rpcCommunicator.registerRpcNotification(RecursiveOperationResponse, 'sendResponse', (req, context) => rpcLocal.sendResponse(req, context));
    }
    start(serviceId) {
        const routeMessage = this.wrapRequest(serviceId);
        this.options.doRouteRequest(routeMessage);
    }
    wrapRequest(serviceId) {
        const request = {
            sessionId: this.getId(),
            operation: this.options.operation
        };
        const msg = {
            messageId: v4(),
            serviceId,
            body: {
                oneofKind: 'recursiveOperationRequest',
                recursiveOperationRequest: request
            }
        };
        const routeMessage = {
            message: msg,
            requestId: v4(),
            target: toDhtAddressRaw(this.options.targetId),
            sourcePeer: this.options.localPeerDescriptor,
            reachableThrough: [],
            routingPath: [],
            parallelRootNodeIds: []
        };
        return routeMessage;
    }
    isCompleted() {
        const unreportedHops = new Set(this.allKnownHops);
        this.reportedHops.forEach((id) => {
            unreportedHops.delete(id);
        });
        if (this.noCloserNodesReceivedCounter >= 1 && unreportedHops.size === 0) {
            if (this.options.operation === RecursiveOperation.FETCH_DATA
                && (this.hasNonStaleData() || this.noCloserNodesReceivedCounter >= this.options.waitedRoutingPathCompletions)) {
                return true;
            }
            else if (this.options.operation === RecursiveOperation.FETCH_DATA) {
                return false;
            }
            return true;
        }
        return false;
    }
    hasNonStaleData() {
        return Array.from(this.foundData.values()).some((entry) => entry.stale === false);
    }
    onResponseReceived(remoteNodeId, routingPath, closestConnectedNodes, dataEntries, noCloserNodesFound) {
        this.addKnownHops(routingPath);
        if (routingPath.length >= 1) {
            this.setHopAsReported(routingPath[routingPath.length - 1]);
        }
        closestConnectedNodes.forEach((descriptor) => {
            this.results.addContact(new Contact(descriptor));
        });
        this.processFoundData(dataEntries);
        if (noCloserNodesFound || this.noCloserNodesReceivedFrom.has(remoteNodeId)) {
            this.onNoCloserPeersFound(remoteNodeId);
        }
    }
    addKnownHops(routingPath) {
        const localNodeId = toNodeId(this.options.localPeerDescriptor);
        routingPath.forEach((desc) => {
            const newNodeId = toNodeId(desc);
            if (localNodeId !== newNodeId) {
                this.allKnownHops.add(newNodeId);
            }
        });
    }
    setHopAsReported(desc) {
        const localNodeId = toNodeId(this.options.localPeerDescriptor);
        const newNodeId = toNodeId(desc);
        if (localNodeId !== newNodeId) {
            this.reportedHops.add(newNodeId);
        }
        if (this.isCompleted()) {
            if (!this.completionEventEmitted && this.isCompleted()) {
                if (this.timeoutTask) {
                    clearTimeout(this.timeoutTask);
                    this.timeoutTask = undefined;
                }
                this.emit('completed');
                this.completionEventEmitted = true;
            }
        }
    }
    processFoundData(dataEntries) {
        dataEntries.forEach((entry) => {
            const creatorNodeId = toDhtAddress(entry.creator);
            const existingEntry = this.foundData.get(creatorNodeId);
            if (!existingEntry || existingEntry.createdAt < entry.createdAt
                || (existingEntry.createdAt <= entry.createdAt && entry.deleted)) {
                this.foundData.set(creatorNodeId, entry);
            }
        });
    }
    onNoCloserPeersFound(remoteNodeId) {
        this.noCloserNodesReceivedCounter += 1;
        this.noCloserNodesReceivedFrom.add(remoteNodeId);
        if (this.isCompleted()) {
            this.emit('completed');
            this.completionEventEmitted = true;
            if (this.timeoutTask) {
                clearTimeout(this.timeoutTask);
                this.timeoutTask = undefined;
            }
        }
        else if (!this.timeoutTask && !this.completionEventEmitted) {
            this.timeoutTask = setTimeout(() => {
                if (!this.completionEventEmitted) {
                    this.emit('completed');
                    this.completionEventEmitted = true;
                }
            }, 4000); // TODO use options option or named constant?
        }
    }
    getResults() {
        return {
            closestNodes: this.results.getClosestContacts().map((contact) => contact.getPeerDescriptor()),
            dataEntries: Array.from(this.foundData.values())
        };
    }
    getId() {
        return this.id;
    }
    stop() {
        if (this.timeoutTask) {
            clearTimeout(this.timeoutTask);
            this.timeoutTask = undefined;
        }
        this.rpcCommunicator.destroy();
        this.emit('completed');
    }
}

const logger$7 = new Logger('RouterRpcLocal');
const createRouteMessageAck = (routedMessage, error) => {
    const ack = {
        requestId: routedMessage.requestId,
        error
    };
    return ack;
};
class RouterRpcLocal {
    options;
    constructor(options) {
        this.options = options;
    }
    async routeMessage(routedMessage) {
        if (this.options.duplicateRequestDetector.isMostLikelyDuplicate(routedMessage.requestId)) {
            logger$7.trace(`Routing message ${routedMessage.requestId} from ${toNodeId(routedMessage.sourcePeer)} `
                + `to ${toDhtAddress(routedMessage.target)} is likely a duplicate`);
            return createRouteMessageAck(routedMessage, RouteMessageError.DUPLICATE);
        }
        logger$7.trace(`Processing received routeMessage ${routedMessage.requestId}`);
        this.options.duplicateRequestDetector.add(routedMessage.requestId);
        if (areEqualBinaries(this.options.localPeerDescriptor.nodeId, routedMessage.target)) {
            logger$7.trace(`routing message targeted to self ${routedMessage.requestId}`);
            this.options.setForwardingEntries(routedMessage);
            this.options.handleMessage(routedMessage.message);
            return createRouteMessageAck(routedMessage);
        }
        else {
            return this.options.doRouteMessage(routedMessage);
        }
    }
    async forwardMessage(forwardMessage) {
        if (this.options.duplicateRequestDetector.isMostLikelyDuplicate(forwardMessage.requestId)) {
            logger$7.trace(`Forwarding message ${forwardMessage.requestId} from ${toNodeId(forwardMessage.sourcePeer)} `
                + `to ${toDhtAddress(forwardMessage.target)} is likely a duplicate`);
            return createRouteMessageAck(forwardMessage, RouteMessageError.DUPLICATE);
        }
        logger$7.trace(`Processing received forward routeMessage ${forwardMessage.requestId}`);
        this.options.duplicateRequestDetector.add(forwardMessage.requestId);
        if (areEqualBinaries(this.options.localPeerDescriptor.nodeId, forwardMessage.target)) {
            return this.forwardToDestination(forwardMessage);
        }
        else {
            return this.options.doRouteMessage(forwardMessage, RoutingMode.FORWARD);
        }
    }
    forwardToDestination(routedMessage) {
        logger$7.trace(`Forwarding found message targeted to self ${routedMessage.requestId}`);
        const forwardedMessage = routedMessage.message;
        if (areEqualPeerDescriptors(this.options.localPeerDescriptor, forwardedMessage.targetDescriptor)) {
            this.options.handleMessage(forwardedMessage);
            return createRouteMessageAck(routedMessage);
        }
        return this.options.doRouteMessage({ ...routedMessage, requestId: v4(), target: forwardedMessage.targetDescriptor.nodeId });
    }
}

const logger$6 = new Logger('RecursiveOperationRpcLocal');
class RecursiveOperationRpcLocal {
    options;
    constructor(options) {
        this.options = options;
    }
    async routeRequest(routedMessage) {
        if (this.options.isMostLikelyDuplicate(routedMessage.requestId)) {
            return createRouteMessageAck(routedMessage, RouteMessageError.DUPLICATE);
        }
        const remoteNodeId = toNodeId(getPreviousPeer(routedMessage) ?? routedMessage.sourcePeer);
        logger$6.trace(`Received routeRequest call from ${remoteNodeId}`);
        this.options.addToDuplicateDetector(routedMessage.requestId);
        return this.options.doRouteRequest(routedMessage);
    }
}

const logger$5 = new Logger('RecursiveOperationManager');
class RecursiveOperationManager {
    ongoingSessions = new Map();
    stopped = false;
    options;
    constructor(options) {
        this.options = options;
        this.registerLocalRpcMethods();
    }
    registerLocalRpcMethods() {
        const rpcLocal = new RecursiveOperationRpcLocal({
            doRouteRequest: (routedMessage) => this.doRouteRequest(routedMessage),
            addContact: (contact) => this.options.addContact(contact),
            isMostLikelyDuplicate: (requestId) => this.options.router.isMostLikelyDuplicate(requestId),
            addToDuplicateDetector: (requestId) => this.options.router.addToDuplicateDetector(requestId)
        });
        this.options.rpcCommunicator.registerRpcMethod(RouteMessageWrapper, RouteMessageAck, 'routeRequest', async (routedMessage) => {
            if (this.stopped) {
                return createRouteMessageAck(routedMessage, RouteMessageError.STOPPED);
            }
            else {
                return rpcLocal.routeRequest(routedMessage);
            }
        });
    }
    async execute(targetId, operation, excludedPeer, waitForCompletion = true) {
        if (this.stopped) {
            return { closestNodes: [] };
        }
        const session = new RecursiveOperationSession({
            transport: this.options.sessionTransport,
            targetId,
            localPeerDescriptor: this.options.localPeerDescriptor,
            // TODO use options option or named constant?
            waitedRoutingPathCompletions: this.options.connectionsView.getConnectionCount() > 1 ? 2 : 1,
            operation,
            // TODO would it make sense to give excludedPeer as one of the fields RecursiveOperationSession?
            doRouteRequest: (routedMessage) => {
                return this.doRouteRequest(routedMessage, excludedPeer);
            }
        });
        if (this.options.connectionsView.getConnectionCount() === 0) {
            const dataEntries = Array.from(this.options.localDataStore.values(targetId));
            session.onResponseReceived(toNodeId(this.options.localPeerDescriptor), [this.options.localPeerDescriptor], [this.options.localPeerDescriptor], dataEntries, true);
            return session.getResults();
        }
        this.ongoingSessions.set(session.getId(), session);
        if (waitForCompletion === true) {
            try {
                const eventPromise = waitForEvent(session, 'completed', RECURSIVE_OPERATION_TIMEOUT);
                session.start(this.options.serviceId);
                await eventPromise;
            }
            catch (err) {
                logger$5.debug('start failed', { err });
            }
        }
        else {
            session.start(this.options.serviceId);
            // Wait for delete operation to be sent out by the router
            // TODO: Add a feature to wait for the router to pass the message?
            await wait(50);
        }
        if (operation === RecursiveOperation.FETCH_DATA) {
            const dataEntries = Array.from(this.options.localDataStore.values(targetId));
            if (dataEntries.length > 0) {
                this.sendResponse([this.options.localPeerDescriptor], this.options.localPeerDescriptor, session.getId(), [], dataEntries, true);
            }
        }
        else if (operation === RecursiveOperation.DELETE_DATA) {
            this.options.localDataStore.markAsDeleted(targetId, toNodeId(this.options.localPeerDescriptor));
        }
        this.ongoingSessions.delete(session.getId());
        session.stop();
        return session.getResults();
    }
    sendResponse(routingPath, targetPeerDescriptor, serviceId, closestConnectedNodes, dataEntries, noCloserNodesFound = false) {
        const isOwnNode = areEqualPeerDescriptors(this.options.localPeerDescriptor, targetPeerDescriptor);
        if (isOwnNode && this.ongoingSessions.has(serviceId)) {
            this.ongoingSessions.get(serviceId)
                .onResponseReceived(toNodeId(this.options.localPeerDescriptor), routingPath, closestConnectedNodes, dataEntries, noCloserNodesFound);
        }
        else {
            // TODO use options option or named constant?
            const remoteCommunicator = new ListeningRpcCommunicator(serviceId, this.options.sessionTransport, { rpcRequestTimeout: RECURSIVE_OPERATION_TIMEOUT });
            const rpcRemote = new RecursiveOperationSessionRpcRemote(this.options.localPeerDescriptor, targetPeerDescriptor, remoteCommunicator, RecursiveOperationSessionRpcClient, 
            // TODO use options option or named constant?
            10000);
            rpcRemote.sendResponse(routingPath, closestConnectedNodes, dataEntries, noCloserNodesFound);
            remoteCommunicator.destroy();
        }
    }
    doRouteRequest(routedMessage, excludedPeer) {
        if (this.stopped) {
            return createRouteMessageAck(routedMessage, RouteMessageError.STOPPED);
        }
        const targetId = toDhtAddress(routedMessage.target);
        const request = routedMessage.message.body.recursiveOperationRequest;
        // TODO use options option or named constant?
        const closestConnectedNodes = this.getClosestConnectedNodes(targetId, 5);
        const dataEntries = (request.operation === RecursiveOperation.FETCH_DATA)
            ? Array.from(this.options.localDataStore.values(targetId))
            : [];
        if (request.operation === RecursiveOperation.DELETE_DATA) {
            this.options.localDataStore.markAsDeleted(targetId, toNodeId(routedMessage.sourcePeer));
        }
        if (areEqualBinaries(this.options.localPeerDescriptor.nodeId, routedMessage.target)) {
            // TODO this is also very similar case to what we do at line 255, could simplify the code paths?
            this.sendResponse(routedMessage.routingPath, routedMessage.sourcePeer, request.sessionId, closestConnectedNodes, dataEntries, true);
            return createRouteMessageAck(routedMessage);
        }
        else {
            const ack = this.options.router.doRouteMessage(routedMessage, RoutingMode.RECURSIVE, excludedPeer);
            if ((ack.error === undefined) || (ack.error === RouteMessageError.NO_TARGETS)) {
                const noCloserContactsFound = (ack.error === RouteMessageError.NO_TARGETS) ||
                    (closestConnectedNodes.length > 0
                        && getPreviousPeer(routedMessage)
                        && !this.isPeerCloserToIdThanSelf(closestConnectedNodes[0], targetId));
                this.sendResponse(routedMessage.routingPath, routedMessage.sourcePeer, request.sessionId, closestConnectedNodes, dataEntries, noCloserContactsFound);
            }
            return ack;
        }
    }
    getClosestConnectedNodes(referenceId, limit) {
        const connectedNodes = this.options.connectionsView.getConnections().map((c) => this.options.createDhtNodeRpcRemote(c));
        const sorted = new SortedContactList({
            referenceId,
            maxSize: limit,
            allowToContainReferenceId: true
        });
        sorted.addContacts(connectedNodes);
        return sorted.getClosestContacts(limit).map((peer) => peer.getPeerDescriptor());
    }
    isPeerCloserToIdThanSelf(peer, nodeIdOrDataKey) {
        const nodeIdOrDataKeyRaw = toDhtAddressRaw(nodeIdOrDataKey);
        const distance1 = getPeerDistance(peer.nodeId, nodeIdOrDataKeyRaw);
        const distance2 = getPeerDistance(this.options.localPeerDescriptor.nodeId, nodeIdOrDataKeyRaw);
        return distance1 < distance2;
    }
    stop() {
        this.stopped = true;
        this.ongoingSessions.forEach((session, _id) => {
            session.stop();
        });
        this.ongoingSessions.clear();
    }
}

const createRoutingTableId = (targetId, previousId) => {
    return targetId + (previousId ?? '');
};
const DEFAULT_LRU_OPTIONS = {
    max: 1000,
    maxAge: 15 * 1000
};
/**
 * RoutingTablesCache is a cache for routing tables.
 * It is used to store the routing tables for a specific targetId and previousId.
 * Storing the previousId is important as it is used as a minimum distance for the contacts in the table.
 * Calculating a RoutingTable from scratch is an O(n log n) operation (n = number of connections of a node)
 * However,
 * - Adding a contact to a RoutingTable is an O(log n) operation.
 * - Deleting a contact from a RoutingTable is an O(1) operation.
 * Thus, holding the most frequently used routing tables in memory to be updated on
 * connections and disconnections is hugely beneficial in terms of performance.
*/
class RoutingTablesCache {
    tables = new LRUCache(DEFAULT_LRU_OPTIONS);
    get(targetId, previousId) {
        return this.tables.get(createRoutingTableId(targetId, previousId));
    }
    set(targetId, table, previousId) {
        this.tables.set(createRoutingTableId(targetId, previousId), table);
    }
    has(targetId, previousId) {
        return this.tables.has(createRoutingTableId(targetId, previousId));
    }
    onNodeDisconnected(nodeId) {
        this.tables.forEach((table) => table.removeContact(nodeId));
    }
    onNodeConnected(remote) {
        this.tables.forEach((table) => table.addContact(remote));
    }
    reset() {
        this.tables.forEach((table) => table.stop());
        this.tables.clear();
    }
}

const logger$4 = new Logger('Router');
class Router {
    forwardingTable = new Map();
    routingTablesCache = new RoutingTablesCache();
    ongoingRoutingSessions = new Map();
    // TODO use options option or named constant?
    duplicateRequestDetector = new DuplicateDetector(10000);
    stopped = false;
    options;
    messagesRouted = 0;
    messagesSent = 0;
    constructor(options) {
        this.options = options;
        this.registerLocalRpcMethods();
    }
    registerLocalRpcMethods() {
        const rpcLocal = new RouterRpcLocal({
            doRouteMessage: (routedMessage, mode) => this.doRouteMessage(routedMessage, mode),
            setForwardingEntries: (routedMessage) => this.setForwardingEntries(routedMessage),
            duplicateRequestDetector: this.duplicateRequestDetector,
            localPeerDescriptor: this.options.localPeerDescriptor,
            handleMessage: this.options.handleMessage
        });
        this.options.rpcCommunicator.registerRpcMethod(RouteMessageWrapper, RouteMessageAck, 'routeMessage', async (routedMessage) => {
            if (this.stopped) {
                return createRouteMessageAck(routedMessage, RouteMessageError.STOPPED);
            }
            return rpcLocal.routeMessage(routedMessage);
        });
        this.options.rpcCommunicator.registerRpcMethod(RouteMessageWrapper, RouteMessageAck, 'forwardMessage', async (forwardMessage) => {
            if (this.stopped) {
                return createRouteMessageAck(forwardMessage, RouteMessageError.STOPPED);
            }
            return rpcLocal.forwardMessage(forwardMessage);
        });
    }
    send(msg, reachableThrough) {
        msg.sourceDescriptor = this.options.localPeerDescriptor;
        const targetPeerDescriptor = msg.targetDescriptor;
        const forwardingEntry = this.forwardingTable.get(toNodeId(targetPeerDescriptor));
        if (forwardingEntry && forwardingEntry.peerDescriptors.length > 0) {
            const forwardedMessage = {
                message: msg,
                requestId: v4(),
                target: forwardingEntry.peerDescriptors[0].nodeId,
                sourcePeer: this.options.localPeerDescriptor,
                reachableThrough,
                routingPath: [],
                parallelRootNodeIds: []
            };
            const ack = this.doRouteMessage(forwardedMessage, RoutingMode.FORWARD);
            if (ack.error !== undefined) {
                const error = 'Could not forward message with error ' + ack.error;
                logger$4.debug(error);
                throw new Error(error);
            }
        }
        else {
            const routedMessage = {
                message: msg,
                requestId: v4(),
                target: targetPeerDescriptor.nodeId,
                sourcePeer: this.options.localPeerDescriptor,
                reachableThrough,
                routingPath: [],
                parallelRootNodeIds: []
            };
            const ack = this.doRouteMessage(routedMessage, RoutingMode.ROUTE);
            if (ack.error !== undefined) {
                const error = 'Could not route message with error ' + ack.error;
                logger$4.debug(error);
                throw new Error(error);
            }
        }
        this.messagesSent += 1;
    }
    doRouteMessage(routedMessage, mode = RoutingMode.ROUTE, excludedPeer) {
        if (this.stopped) {
            return createRouteMessageAck(routedMessage, RouteMessageError.STOPPED);
        }
        logger$4.trace(`Routing message ${routedMessage.requestId} from ${toNodeId(routedMessage.sourcePeer)} `
            + `to ${toDhtAddress(routedMessage.target)}`);
        const session = this.createRoutingSession(routedMessage, mode, excludedPeer);
        const contacts = session.updateAndGetRoutablePeers();
        if (contacts.length > 0) {
            this.addRoutingSession(session);
            logger$4.trace('starting to raceForEvent from routingSession: ' + session.sessionId);
            const eventReceived = raceForEvent(session, ['routingSucceeded', 'partialSuccess', 'routingFailed', 'stopped'], 10000 // TODO use options option or named constant?
            );
            setImmediate(async () => {
                try {
                    await eventReceived;
                    logger$4.trace('raceForEvent ended from routingSession: ' + session.sessionId);
                }
                catch {
                    logger$4.trace('raceForEvent timed out for routingSession ' + session.sessionId);
                }
                session.stop();
                this.removeRoutingSession(session.sessionId);
            });
            session.sendMoreRequests(contacts);
            this.messagesRouted += 1;
            return createRouteMessageAck(routedMessage);
        }
        else {
            logger$4.trace('no targets', { sessionId: session.sessionId });
            return createRouteMessageAck(routedMessage, RouteMessageError.NO_TARGETS);
        }
    }
    createRoutingSession(routedMessage, mode, excludedNode) {
        const excludedNodeIds = new Set(routedMessage.routingPath.map((descriptor) => toNodeId(descriptor)));
        if (excludedNode) {
            excludedNodeIds.add(excludedNode);
        }
        routedMessage.parallelRootNodeIds.forEach((nodeId) => {
            excludedNodeIds.add(nodeId);
        });
        return new RoutingSession({
            rpcCommunicator: this.options.rpcCommunicator,
            localPeerDescriptor: this.options.localPeerDescriptor,
            routedMessage,
            // TODO use options option or named constant?
            parallelism: areEqualPeerDescriptors(this.options.localPeerDescriptor, routedMessage.sourcePeer) ? 2 : 1,
            mode,
            excludedNodeIds,
            routingTablesCache: this.routingTablesCache,
            getConnections: this.options.getConnections
        });
    }
    isMostLikelyDuplicate(requestId) {
        return this.duplicateRequestDetector.isMostLikelyDuplicate(requestId);
    }
    addToDuplicateDetector(requestId) {
        this.duplicateRequestDetector.add(requestId);
    }
    addRoutingSession(session) {
        this.ongoingRoutingSessions.set(session.sessionId, session);
    }
    removeRoutingSession(sessionId) {
        this.ongoingRoutingSessions.delete(sessionId);
    }
    onNodeConnected(peerDescriptor) {
        const remote = new RoutingRemoteContact(peerDescriptor, this.options.localPeerDescriptor, this.options.rpcCommunicator);
        this.routingTablesCache.onNodeConnected(remote);
    }
    onNodeDisconnected(peerDescriptor) {
        this.routingTablesCache.onNodeDisconnected(toNodeId(peerDescriptor));
    }
    resetCache() {
        this.routingTablesCache.reset();
    }
    stop() {
        this.stopped = true;
        this.ongoingRoutingSessions.forEach((session, _id) => {
            session.stop();
        });
        this.ongoingRoutingSessions.clear();
        this.forwardingTable.forEach((entry) => {
            clearTimeout(entry.timeout);
        });
        this.forwardingTable.clear();
        this.duplicateRequestDetector.clear();
        this.routingTablesCache.reset();
    }
    getDiagnosticInfo() {
        return {
            messagesRouted: this.messagesRouted,
            messagesSent: this.messagesSent
        };
    }
    setForwardingEntries(routedMessage) {
        const reachableThroughWithoutSelf = routedMessage.reachableThrough.filter((peer) => {
            return !areEqualPeerDescriptors(peer, this.options.localPeerDescriptor);
        });
        if (reachableThroughWithoutSelf.length > 0) {
            const sourceNodeId = toNodeId(routedMessage.sourcePeer);
            if (this.forwardingTable.has(sourceNodeId)) {
                const oldEntry = this.forwardingTable.get(sourceNodeId);
                clearTimeout(oldEntry.timeout);
                this.forwardingTable.delete(sourceNodeId);
            }
            const forwardingEntry = {
                peerDescriptors: reachableThroughWithoutSelf,
                timeout: setTimeout(() => {
                    this.forwardingTable.delete(sourceNodeId);
                }, 10000) // TODO use options option or named constant?
            };
            this.forwardingTable.set(sourceNodeId, forwardingEntry);
        }
    }
}

class LocalDataStore {
    maxTtl;
    constructor(maxTtl) {
        this.maxTtl = maxTtl;
    }
    // A map into which each node can store one value per data key
    // The first key is the key of the data, the second key is the
    // DhtAddress of the creator of the data
    store = new Map();
    storeEntry(dataEntry) {
        const key = toDhtAddress(dataEntry.key);
        const creatorNodeId = toDhtAddress(dataEntry.creator);
        if (!this.store.has(key)) {
            this.store.set(key, new MapWithTtl((e) => Math.min(e.ttl, this.maxTtl)));
        }
        if (this.store.get(key).has(creatorNodeId)) {
            const storedMillis = (dataEntry.createdAt.seconds * 1000) + (dataEntry.createdAt.nanos / 1000000);
            const oldLocalEntry = this.store.get(key).get(creatorNodeId);
            const oldStoredMillis = (oldLocalEntry.createdAt.seconds * 1000) + (oldLocalEntry.createdAt.nanos / 1000000);
            // do nothing if old entry is newer than the one being replicated
            if (oldStoredMillis >= storedMillis) {
                return false;
            }
        }
        this.store.get(key).set(creatorNodeId, dataEntry);
        return true;
    }
    markAsDeleted(key, creator) {
        const item = this.store.get(key);
        if (!item?.has(creator)) {
            return false;
        }
        const storedEntry = item.get(creator);
        storedEntry.deleted = true;
        return true;
    }
    *values(key) {
        if (key !== undefined) {
            const map = this.store.get(key);
            if (map !== undefined) {
                yield* map.values();
            }
        }
        else {
            for (const v of this.store.values()) {
                yield* v.values();
            }
        }
    }
    keys() {
        return this.store.keys();
    }
    setAllEntriesAsStale(key) {
        this.store.get(key)?.forEach((value) => {
            value.stale = true;
        });
    }
    deleteEntry(key, creator) {
        const storedEntry = this.store.get(key)?.get(creator);
        if (storedEntry) {
            this.store.get(key)?.delete(creator);
            if (this.store.get(key)?.size() === 0) {
                this.store.delete(key);
            }
        }
    }
    clear() {
        this.store.forEach((value) => value.clear());
        this.store.clear();
    }
}

const logger$3 = new Logger('StoreRpcLocal');
class StoreRpcLocal {
    options;
    constructor(options) {
        this.options = options;
    }
    async storeData(request) {
        logger$3.trace('storeData()');
        const key = toDhtAddress(request.key);
        const isLocalNodeStorer = this.isLocalNodeStorer(key);
        this.options.localDataStore.storeEntry({
            key: request.key,
            data: request.data,
            creator: request.creator,
            createdAt: request.createdAt,
            storedAt: Timestamp.now(),
            ttl: request.ttl,
            stale: !isLocalNodeStorer,
            deleted: false
        });
        if (!isLocalNodeStorer) {
            this.options.localDataStore.setAllEntriesAsStale(key);
        }
        return {};
    }
    async replicateData(request, context) {
        logger$3.trace('server-side replicateData()');
        const dataEntry = request.entry;
        const wasStored = this.options.localDataStore.storeEntry(dataEntry);
        if (wasStored) {
            this.replicateDataToNeighbors(context.incomingSourceDescriptor, request.entry);
        }
        const key = toDhtAddress(dataEntry.key);
        if (!this.isLocalNodeStorer(key)) {
            this.options.localDataStore.setAllEntriesAsStale(key);
        }
        logger$3.trace('server-side replicateData() at end');
        return {};
    }
    isLocalNodeStorer(dataKey) {
        return this.options.getStorers(dataKey).some((p) => areEqualPeerDescriptors(p, this.options.localPeerDescriptor));
    }
    replicateDataToNeighbors(requestor, dataEntry) {
        const dataKey = toDhtAddress(dataEntry.key);
        const storers = this.options.getStorers(dataKey);
        const isLocalNodePrimaryStorer = areEqualPeerDescriptors(storers[0], this.options.localPeerDescriptor);
        // If we are the closest to the data, get storageRedundancyFactor - 1 nearest node to the data, and
        // replicate to all those node. Otherwise replicate only to the one closest one. And never replicate 
        // to the requestor nor to itself.
        const targets = (isLocalNodePrimaryStorer ? storers : [storers[0]]).filter((p) => !areEqualPeerDescriptors(p, requestor) && !areEqualPeerDescriptors(p, this.options.localPeerDescriptor));
        targets.forEach((target) => {
            setImmediate(() => {
                executeSafePromise(() => this.options.replicateDataToContact(dataEntry, target));
            });
        });
    }
}

const logger$2 = new Logger('StoreManager');
class StoreManager {
    options;
    constructor(options) {
        this.options = options;
        this.registerLocalRpcMethods();
    }
    registerLocalRpcMethods() {
        const rpcLocal = new StoreRpcLocal({
            localPeerDescriptor: this.options.localPeerDescriptor,
            localDataStore: this.options.localDataStore,
            replicateDataToContact: (dataEntry, contact) => this.replicateDataToContact(dataEntry, contact),
            getStorers: (dataKey) => this.getStorers(dataKey)
        });
        this.options.rpcCommunicator.registerRpcMethod(StoreDataRequest, StoreDataResponse, 'storeData', (request) => rpcLocal.storeData(request));
        this.options.rpcCommunicator.registerRpcNotification(ReplicateDataRequest, 'replicateData', (request, context) => rpcLocal.replicateData(request, context));
    }
    onContactAdded(peerDescriptor) {
        for (const key of this.options.localDataStore.keys()) {
            this.replicateAndUpdateStaleState(key, peerDescriptor);
        }
    }
    replicateAndUpdateStaleState(dataKey, newNode) {
        const storers = this.getStorers(dataKey);
        const storersBeforeContactAdded = storers.filter((p) => !areEqualPeerDescriptors(p, newNode));
        const selfWasPrimaryStorer = areEqualPeerDescriptors(storersBeforeContactAdded[0], this.options.localPeerDescriptor);
        if (selfWasPrimaryStorer) {
            if (storers.some((p) => areEqualPeerDescriptors(p, newNode))) {
                setImmediate(async () => {
                    const dataEntries = Array.from(this.options.localDataStore.values(dataKey));
                    await Promise.all(dataEntries.map(async (dataEntry) => this.replicateDataToContact(dataEntry, newNode)));
                });
            }
        }
        else if (!storers.some((p) => areEqualPeerDescriptors(p, this.options.localPeerDescriptor))) {
            this.options.localDataStore.setAllEntriesAsStale(dataKey);
        }
    }
    async replicateDataToContact(dataEntry, contact) {
        const rpcRemote = this.options.createRpcRemote(contact);
        try {
            await rpcRemote.replicateData({ entry: dataEntry }, true);
        }
        catch (e) {
            logger$2.trace('replicateData() threw an exception ' + e);
        }
    }
    async storeDataToDht(key, data, creator) {
        logger$2.debug(`Storing data to DHT ${this.options.serviceId}`);
        const result = await this.options.recursiveOperationManager.execute(key, RecursiveOperation.FIND_CLOSEST_NODES);
        const closestNodes = result.closestNodes;
        const successfulNodes = [];
        const ttl = this.options.highestTtl; // ToDo: make TTL decrease according to some nice curve
        const createdAt = Timestamp.now();
        for (let i = 0; i < closestNodes.length && successfulNodes.length < this.options.redundancyFactor; i++) {
            const keyRaw = toDhtAddressRaw(key);
            const creatorRaw = toDhtAddressRaw(creator);
            if (areEqualPeerDescriptors(this.options.localPeerDescriptor, closestNodes[i])) {
                this.options.localDataStore.storeEntry({
                    key: keyRaw,
                    data,
                    creator: creatorRaw,
                    createdAt,
                    storedAt: Timestamp.now(),
                    ttl,
                    stale: false,
                    deleted: false,
                });
                successfulNodes.push(closestNodes[i]);
                continue;
            }
            const rpcRemote = this.options.createRpcRemote(closestNodes[i]);
            try {
                await rpcRemote.storeData({
                    key: keyRaw,
                    data,
                    creator: creatorRaw,
                    createdAt,
                    ttl
                });
                successfulNodes.push(closestNodes[i]);
                logger$2.trace('remote.storeData() success');
            }
            catch (e) {
                logger$2.trace('remote.storeData() threw an exception ' + e);
            }
        }
        return successfulNodes;
    }
    async replicateDataToClosestNodes() {
        const dataEntries = Array.from(this.options.localDataStore.values());
        await Promise.all(dataEntries.map(async (dataEntry) => {
            const dataKey = toDhtAddress(dataEntry.key);
            const neighbors = getClosestNodes(dataKey, this.options.getNeighbors(), { maxCount: this.options.redundancyFactor });
            await Promise.all(neighbors.map(async (neighbor) => {
                const rpcRemote = this.options.createRpcRemote(neighbor);
                try {
                    await rpcRemote.replicateData({ entry: dataEntry }, false);
                }
                catch (err) {
                    logger$2.trace('Failed to replicate data in replicateDataToClosestNodes', { err });
                }
            }));
        }));
    }
    getStorers(dataKey, excludedNode) {
        return getClosestNodes(dataKey, [...this.options.getNeighbors(), this.options.localPeerDescriptor], {
            maxCount: this.options.redundancyFactor,
            excludedNodeIds: excludedNode !== undefined ? new Set([toNodeId(excludedNode)]) : undefined
        });
    }
    async destroy() {
        await this.replicateDataToClosestNodes();
    }
}

class StoreRpcRemote extends RpcRemote {
    async storeData(request) {
        const options = this.formDhtRpcOptions();
        try {
            await this.getClient().storeData(request, options);
        }
        catch (err) {
            const to = toNodeId(this.getPeerDescriptor());
            const from = toNodeId(this.getLocalPeerDescriptor());
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            throw new Error(`Could not store data to ${to} from ${from} ${err}`);
        }
    }
    async replicateData(request, connect) {
        const options = this.formDhtRpcOptions({
            timeout: EXISTING_CONNECTION_TIMEOUT,
            notification: true,
            connect
        });
        return this.getClient().replicateData(request, options);
    }
}

const logger$1 = new Logger('DhtNode');
const NUMBER_OF_NODES_PER_KBUCKET_DEFAULT = 8;
const PERIODICAL_PING_INTERVAL = 60 * 1000;
class DhtNode extends EventEmitter {
    options;
    rpcCommunicator;
    transport;
    localPeerDescriptor;
    router;
    storeManager;
    localDataStore;
    recursiveOperationManager;
    peerDiscovery;
    peerManager;
    connectionsView;
    connectionLocker;
    started = false;
    abortController = new AbortController();
    constructor(conf) {
        super();
        this.options = merge({
            serviceId: CONTROL_LAYER_NODE_SERVICE_ID,
            joinParallelism: 3,
            maxContactCount: 200,
            numberOfNodesPerKBucket: NUMBER_OF_NODES_PER_KBUCKET_DEFAULT,
            joinNoProgressLimit: 5,
            dhtJoinTimeout: 60000,
            peerDiscoveryQueryBatchSize: 5,
            maxConnections: 80,
            storeHighestTtl: 60000,
            storeMaxTtl: 60000,
            networkConnectivityTimeout: 10000,
            storageRedundancyFactor: 5, // TODO validate that this is > 1 (as each node should replicate the data to other node)
            metricsContext: new MetricsContext()
        }, conf);
        this.validateOptions();
        this.localDataStore = new LocalDataStore(this.options.storeMaxTtl);
        this.send = this.send.bind(this);
    }
    validateOptions() {
        const expectedNodeIdLength = KADEMLIA_ID_LENGTH_IN_BYTES * 2;
        if (this.options.nodeId !== undefined) {
            if (!/^[0-9a-fA-F]+$/.test(this.options.nodeId)) {
                throw new Error('Invalid nodeId, the nodeId should be a hex string');
            }
            else if (this.options.nodeId.length !== expectedNodeIdLength) {
                throw new Error(`Invalid nodeId, the length of the nodeId should be ${expectedNodeIdLength}`);
            }
        }
        if (this.options.peerDescriptor !== undefined) {
            if (this.options.peerDescriptor.nodeId.length !== KADEMLIA_ID_LENGTH_IN_BYTES) {
                throw new Error(`Invalid peerDescriptor, the length of the nodeId should be ${KADEMLIA_ID_LENGTH_IN_BYTES} bytes`);
            }
        }
        if (this.options.transport !== undefined && this.options.connectionsView === undefined) {
            throw new Error('connectionsView is required when transport is given');
        }
    }
    async start() {
        if (this.started || this.abortController.signal.aborted) {
            return;
        }
        logger$1.trace(`Starting new Streamr Network DHT Node with serviceId ${this.options.serviceId}`);
        this.started = true;
        {
            this.options.websocketPortRange = undefined;
            if (this.options.peerDescriptor) {
                this.options.peerDescriptor.websocket = undefined;
            }
        }
        // If transport is given, do not create a ConnectionManager
        if (this.options.transport) {
            this.transport = this.options.transport;
            this.connectionsView = this.options.connectionsView;
            this.connectionLocker = this.options.connectionLocker;
            this.localPeerDescriptor = this.transport.getLocalPeerDescriptor();
        }
        else {
            const connectorFacadeOptions = {
                transport: this,
                entryPoints: this.options.entryPoints,
                iceServers: this.options.iceServers,
                webrtcAllowPrivateAddresses: this.options.webrtcAllowPrivateAddresses,
                webrtcDatachannelBufferThresholdLow: this.options.webrtcDatachannelBufferThresholdLow,
                webrtcDatachannelBufferThresholdHigh: this.options.webrtcDatachannelBufferThresholdHigh,
                webrtcPortRange: this.options.webrtcPortRange,
                maxMessageSize: this.options.maxMessageSize,
                websocketServerEnableTls: this.options.websocketServerEnableTls,
                tlsCertificate: this.options.tlsCertificate,
                externalIp: this.options.externalIp,
                autoCertifierUrl: this.options.autoCertifierUrl,
                autoCertifierConfigFile: this.options.autoCertifierConfigFile,
                geoIpDatabaseFolder: this.options.geoIpDatabaseFolder,
                createLocalPeerDescriptor: (connectivityResponse) => this.generatePeerDescriptorCallBack(connectivityResponse)
            };
            // If own PeerDescriptor is given in options, create a ConnectionManager with ws server
            if (this.options.peerDescriptor?.websocket) {
                connectorFacadeOptions.websocketHost = this.options.peerDescriptor.websocket.host;
                connectorFacadeOptions.websocketPortRange = {
                    min: this.options.peerDescriptor.websocket.port,
                    max: this.options.peerDescriptor.websocket.port
                };
                // If websocketPortRange is given, create ws server using it, websocketHost can be undefined
            }
            else if (this.options.websocketPortRange) {
                connectorFacadeOptions.websocketHost = this.options.websocketHost;
                connectorFacadeOptions.websocketPortRange = this.options.websocketPortRange;
            }
            const connectionManager = new ConnectionManager({
                createConnectorFacade: () => new DefaultConnectorFacade(connectorFacadeOptions),
                maxConnections: this.options.maxConnections,
                metricsContext: this.options.metricsContext,
                allowIncomingPrivateConnections: this.options.allowIncomingPrivateConnections ?? false
            });
            await connectionManager.start();
            this.connectionsView = connectionManager;
            this.connectionLocker = connectionManager;
            this.transport = connectionManager;
        }
        this.rpcCommunicator = new RoutingRpcCommunicator(this.options.serviceId, (msg, opts) => this.transport.send(msg, opts), { rpcRequestTimeout: this.options.rpcRequestTimeout });
        /* eslint-disable indent */
        addManagedEventListener(// TODO remove explicit type in NET-1449
        this.transport, 'message', (message) => this.handleMessageFromTransport(message), this.abortController.signal);
        this.initPeerManager();
        this.peerDiscovery = new PeerDiscovery({
            localPeerDescriptor: this.localPeerDescriptor,
            joinNoProgressLimit: this.options.joinNoProgressLimit,
            joinTimeout: this.options.dhtJoinTimeout,
            serviceId: this.options.serviceId,
            parallelism: this.options.joinParallelism,
            connectionLocker: this.connectionLocker,
            peerManager: this.peerManager,
            abortSignal: this.abortController.signal,
            createDhtNodeRpcRemote: (peerDescriptor) => this.createDhtNodeRpcRemote(peerDescriptor),
        });
        this.router = new Router({
            rpcCommunicator: this.rpcCommunicator,
            localPeerDescriptor: this.localPeerDescriptor,
            handleMessage: (message) => this.handleMessageFromRouter(message),
            getConnections: () => this.connectionsView.getConnections()
        });
        this.recursiveOperationManager = new RecursiveOperationManager({
            rpcCommunicator: this.rpcCommunicator,
            router: this.router,
            sessionTransport: this,
            connectionsView: this.connectionsView,
            localPeerDescriptor: this.localPeerDescriptor,
            serviceId: this.options.serviceId,
            localDataStore: this.localDataStore,
            addContact: (contact) => this.peerManager.addContact(contact),
            createDhtNodeRpcRemote: (peerDescriptor) => this.createDhtNodeRpcRemote(peerDescriptor),
        });
        this.storeManager = new StoreManager({
            rpcCommunicator: this.rpcCommunicator,
            recursiveOperationManager: this.recursiveOperationManager,
            localPeerDescriptor: this.localPeerDescriptor,
            serviceId: this.options.serviceId,
            highestTtl: this.options.storeHighestTtl,
            redundancyFactor: this.options.storageRedundancyFactor,
            localDataStore: this.localDataStore,
            getNeighbors: () => this.peerManager.getNeighbors().map((n) => n.getPeerDescriptor()),
            createRpcRemote: (contact) => {
                return new StoreRpcRemote(this.localPeerDescriptor, contact, this.rpcCommunicator, StoreRpcClient, this.options.rpcRequestTimeout);
            }
        });
        this.on('nearbyContactAdded', (peerDescriptor) => {
            this.storeManager.onContactAdded(peerDescriptor);
        });
        this.bindRpcLocalMethods();
        const pruneTargets = [];
        if (this.options.periodicallyPingNeighbors === true) {
            pruneTargets.push(() => this.peerManager.getNeighbors().map((node) => this.createDhtNodeRpcRemote(node.getPeerDescriptor())));
        }
        if (this.options.periodicallyPingRingContacts === true) {
            pruneTargets.push(() => this.peerManager.getRingContacts().getAllContacts());
        }
        for (const pruneTarget of pruneTargets) {
            await scheduleAtInterval(async () => {
                const nodes = pruneTarget();
                await this.peerManager.pruneOfflineNodes(nodes);
            }, PERIODICAL_PING_INTERVAL, false, this.abortController.signal);
        }
    }
    initPeerManager() {
        this.peerManager = new PeerManager({
            numberOfNodesPerKBucket: this.options.numberOfNodesPerKBucket,
            maxContactCount: this.options.maxContactCount,
            localNodeId: this.getNodeId(),
            localPeerDescriptor: this.localPeerDescriptor,
            connectionLocker: this.connectionLocker,
            lockId: this.options.serviceId,
            createDhtNodeRpcRemote: (peerDescriptor) => this.createDhtNodeRpcRemote(peerDescriptor),
            hasConnection: (nodeId) => this.connectionsView.hasConnection(nodeId),
            neighborPingLimit: this.options.neighborPingLimit
        });
        this.peerManager.on('nearbyContactRemoved', (peerDescriptor) => {
            this.emit('nearbyContactRemoved', peerDescriptor);
        });
        this.peerManager.on('nearbyContactAdded', (peerDescriptor) => this.emit('nearbyContactAdded', peerDescriptor));
        this.peerManager.on('randomContactRemoved', (peerDescriptor) => this.emit('randomContactRemoved', peerDescriptor));
        this.peerManager.on('randomContactAdded', (peerDescriptor) => this.emit('randomContactAdded', peerDescriptor));
        this.peerManager.on('ringContactRemoved', (peerDescriptor) => {
            this.emit('ringContactRemoved', peerDescriptor);
        });
        this.peerManager.on('ringContactAdded', (peerDescriptor) => {
            this.emit('ringContactAdded', peerDescriptor);
        });
        this.peerManager.on('kBucketEmpty', () => {
            if (!this.peerDiscovery.isJoinOngoing()) {
                if (this.options.entryPoints && this.options.entryPoints.length > 0) {
                    setImmediate(async () => {
                        const contactedPeers = new Set();
                        const distantJoinContactPeers = new Set();
                        // TODO should we catch possible promise rejection?
                        await Promise.all(this.options.entryPoints.map((entryPoint) => this.peerDiscovery.rejoinDht(entryPoint, contactedPeers, distantJoinContactPeers)));
                    });
                }
                else {
                    this.emit('manualRejoinRequired');
                }
            }
        });
        /* eslint-disable indent */
        addManagedEventListener(// TODO remove explicit type in NET-1449
        this.transport, 'connected', (peerDescriptor) => {
            this.router.onNodeConnected(peerDescriptor);
            this.emit('connected', peerDescriptor);
        }, this.abortController.signal);
        /* eslint-disable indent, max-len */
        addManagedEventListener(// TODO remove explicit type in NET-1449
        this.transport, 'disconnected', (peerDescriptor, gracefulLeave) => {
            const isControlLayerNode = (this.connectionLocker !== undefined);
            if (isControlLayerNode) {
                const nodeId = toNodeId(peerDescriptor);
                if (gracefulLeave) {
                    this.peerManager.removeContact(nodeId);
                }
                else {
                    this.peerManager.removeNeighbor(nodeId);
                }
            }
            this.router.onNodeDisconnected(peerDescriptor);
            this.emit('disconnected', peerDescriptor, gracefulLeave);
        }, this.abortController.signal);
    }
    bindRpcLocalMethods() {
        if (!this.started || this.abortController.signal.aborted) {
            return;
        }
        const dhtNodeRpcLocal = new DhtNodeRpcLocal({
            peerDiscoveryQueryBatchSize: this.options.peerDiscoveryQueryBatchSize,
            getNeighbors: () => this.peerManager.getNeighbors().map((n) => n.getPeerDescriptor()),
            getClosestRingContactsTo: (ringIdRaw, limit) => {
                return this.getClosestRingContactsTo(ringIdRaw, limit);
            },
            addContact: (contact) => this.peerManager.addContact(contact),
            removeContact: (nodeId) => this.removeContact(nodeId)
        });
        this.rpcCommunicator.registerRpcMethod(ClosestPeersRequest, ClosestPeersResponse, 'getClosestPeers', (req, context) => dhtNodeRpcLocal.getClosestPeers(req, context));
        this.rpcCommunicator.registerRpcMethod(ClosestRingPeersRequest, ClosestRingPeersResponse, 'getClosestRingPeers', (req, context) => dhtNodeRpcLocal.getClosestRingPeers(req, context));
        this.rpcCommunicator.registerRpcMethod(PingRequest, PingResponse, 'ping', (req, context) => dhtNodeRpcLocal.ping(req, context));
        this.rpcCommunicator.registerRpcNotification(LeaveNotice, 'leaveNotice', (_req, context) => dhtNodeRpcLocal.leaveNotice(context));
        const externalApiRpcLocal = new ExternalApiRpcLocal({
            executeRecursiveOperation: (key, operation, excludedPeer) => {
                return this.recursiveOperationManager.execute(key, operation, excludedPeer);
            },
            storeDataToDht: (key, data, creator) => this.storeDataToDht(key, data, creator)
        });
        this.rpcCommunicator.registerRpcMethod(ExternalFetchDataRequest, ExternalFetchDataResponse, 'externalFetchData', (req, context) => externalApiRpcLocal.externalFetchData(req, context), { timeout: 10000 } // TODO use options option or named constant?
        );
        this.rpcCommunicator.registerRpcMethod(ExternalStoreDataRequest, ExternalStoreDataResponse, 'externalStoreData', (req, context) => externalApiRpcLocal.externalStoreData(req, context), { timeout: 10000 } // TODO use options option or named constant?
        );
    }
    handleMessageFromTransport(message) {
        if (message.serviceId === this.options.serviceId) {
            this.rpcCommunicator?.handleMessageFromPeer(message);
        }
    }
    handleMessageFromRouter(message) {
        if (message.serviceId === this.options.serviceId) {
            this.rpcCommunicator?.handleMessageFromPeer(message);
        }
        else {
            this.emit('message', message);
        }
    }
    async generatePeerDescriptorCallBack(connectivityResponse) {
        if (this.options.peerDescriptor !== undefined) {
            this.localPeerDescriptor = this.options.peerDescriptor;
        }
        else {
            let region = undefined;
            if (this.options.region !== undefined) {
                region = this.options.region;
                logger$1.debug(`Using region ${region} from options when generating local PeerDescriptor`);
            }
            else if (connectivityResponse.latitude !== undefined && connectivityResponse.longitude !== undefined) {
                region = getLocalRegionByCoordinates(connectivityResponse.latitude, connectivityResponse.longitude);
                logger$1.debug(`Using region ${region} from GeoIP when generating local PeerDescriptor`);
            }
            else {
                // as a fallback get the region from the CDN
                // and if it's not available, use a random region
                region = await getLocalRegionWithCache();
                logger$1.debug(`Using region ${region} from CDN when generating local PeerDescriptor`);
            }
            this.localPeerDescriptor = await createPeerDescriptor(connectivityResponse, region, this.options.nodeId);
        }
        return this.localPeerDescriptor;
    }
    getClosestContacts(limit) {
        return this.peerManager.getNearbyContacts()
            .getClosestContacts(limit)
            .map((peer) => peer.getPeerDescriptor());
    }
    getRandomContacts(limit) {
        return this.peerManager.getRandomContacts().getContacts(limit).map((c) => c.getPeerDescriptor());
    }
    getRingContacts() {
        const contacts = this.peerManager.getRingContacts().getClosestContacts();
        return {
            left: contacts.left.map((c) => c.getPeerDescriptor()),
            right: contacts.right.map((c) => c.getPeerDescriptor())
        };
    }
    getClosestRingContactsTo(ringIdRaw, limit) {
        const closest = this.peerManager.getClosestRingContactsTo(ringIdRaw, limit);
        return {
            left: closest.left.map((dhtPeer) => dhtPeer.getPeerDescriptor()),
            right: closest.right.map((dhtPeer) => dhtPeer.getPeerDescriptor())
        };
    }
    getNodeId() {
        return toNodeId(this.localPeerDescriptor);
    }
    getNeighborCount() {
        return this.peerManager.getNeighborCount();
    }
    removeContact(nodeId) {
        if (!this.started) { // the stopped state is checked in PeerManager
            return;
        }
        this.peerManager.removeContact(nodeId);
    }
    async send(msg) {
        if (!this.started || this.abortController.signal.aborted) {
            return;
        }
        const reachableThrough = this.peerDiscovery.isJoinOngoing() ? this.getConnectedEntryPoints() : [];
        this.router.send(msg, reachableThrough);
    }
    getConnectedEntryPoints() {
        return this.options.entryPoints !== undefined ? this.options.entryPoints.filter((entryPoint) => this.connectionsView.hasConnection(toNodeId(entryPoint))) : [];
    }
    async joinDht(entryPointDescriptors, doAdditionalDistantPeerDiscovery, retry) {
        if (!this.started) {
            throw new Error('Cannot join DHT before calling start() on DhtNode');
        }
        await this.peerDiscovery.joinDht(entryPointDescriptors, doAdditionalDistantPeerDiscovery, retry);
    }
    async joinRing() {
        if (!this.started) {
            throw new Error('Cannot join ring before calling start() on DhtNode');
        }
        await this.peerDiscovery.joinRing();
    }
    async storeDataToDht(key, data, creator) {
        return this.executeRecursiveOperation(() => this.storeManager.storeDataToDht(key, data, creator ?? this.getNodeId()), (connectedEntryPoint) => this.storeDataToDhtViaPeer(key, data, connectedEntryPoint));
    }
    async storeDataToDhtViaPeer(key, data, peer) {
        const rpcRemote = new ExternalApiRpcRemote(this.localPeerDescriptor, peer, this.rpcCommunicator, ExternalApiRpcClient);
        return await rpcRemote.storeData(key, data);
    }
    async fetchDataFromDht(key) {
        return this.executeRecursiveOperation(async () => {
            const result = await this.recursiveOperationManager.execute(key, RecursiveOperation.FETCH_DATA);
            return result.dataEntries ?? []; // TODO is this fallback needed?
        }, (connectedEntryPoint) => this.fetchDataFromDhtViaPeer(key, connectedEntryPoint));
    }
    async fetchDataFromDhtViaPeer(key, peer) {
        const rpcRemote = new ExternalApiRpcRemote(this.localPeerDescriptor, peer, this.rpcCommunicator, ExternalApiRpcClient);
        return await rpcRemote.externalFetchData(key);
    }
    async executeRecursiveOperation(executeDirectly, executeViaPeer) {
        const connectedEntryPoints = this.getConnectedEntryPoints();
        if (this.peerDiscovery.isJoinOngoing() && connectedEntryPoints.length > 0) {
            return executeViaPeer(sample(connectedEntryPoints));
        }
        return executeDirectly();
    }
    async deleteDataFromDht(key, waitForCompletion) {
        if (!this.abortController.signal.aborted) {
            await this.recursiveOperationManager.execute(key, RecursiveOperation.DELETE_DATA, undefined, waitForCompletion);
        }
    }
    async findClosestNodesFromDht(key) {
        return this.executeRecursiveOperation(async () => {
            const result = await this.recursiveOperationManager.execute(key, RecursiveOperation.FIND_CLOSEST_NODES);
            return result.closestNodes;
        }, (connectedEntryPoint) => this.findClosestNodesViaPeer(key, connectedEntryPoint));
    }
    async findClosestNodesViaPeer(key, peer) {
        const rpcRemote = new ExternalApiRpcRemote(this.localPeerDescriptor, peer, this.rpcCommunicator, ExternalApiRpcClient);
        return await rpcRemote.externalFindClosestNode(key);
    }
    getTransport() {
        return this.transport;
    }
    getLocalPeerDescriptor() {
        return this.localPeerDescriptor;
    }
    getNeighbors() {
        return this.started ? this.peerManager.getNeighbors().map((remote) => remote.getPeerDescriptor()) : [];
    }
    getConnectionsView() {
        return this.connectionsView;
    }
    getLocalLockedConnectionCount() {
        return this.connectionLocker.getLocalLockedConnectionCount();
    }
    getRemoteLockedConnectionCount() {
        return this.connectionLocker.getRemoteLockedConnectionCount();
    }
    getWeakLockedConnectionCount() {
        return this.connectionLocker.getWeakLockedConnectionCount();
    }
    async waitForNetworkConnectivity() {
        await until(() => this.connectionsView.getConnectionCount() > 0, this.options.networkConnectivityTimeout, 100, this.abortController.signal);
    }
    hasJoined() {
        return this.peerDiscovery.isJoinCalled();
    }
    getDiagnosticInfo() {
        return {
            localPeerDescriptor: this.localPeerDescriptor,
            transport: this.transport.getDiagnosticInfo(),
            router: this.router.getDiagnosticInfo(),
            neighborCount: this.getNeighborCount(),
            nearbyContactCount: Array.from(this.peerManager.getNearbyContacts().getAllContactsInUndefinedOrder()).length,
            randomContactCount: this.peerManager.getRandomContacts().getSize()
        };
    }
    async stop() {
        if (this.abortController.signal.aborted || !this.started) {
            return;
        }
        logger$1.trace('stop()');
        this.abortController.abort();
        await this.storeManager.destroy();
        this.localDataStore.clear();
        this.peerManager?.stop();
        this.rpcCommunicator.stop();
        this.router.stop();
        this.recursiveOperationManager.stop();
        if (this.options.transport === undefined) {
            // if the transport was not given in options, the instance was created in start() and
            // this component is responsible for stopping it
            await this.transport.stop();
        }
        this.transport = undefined;
        this.connectionLocker = undefined;
        this.removeAllListeners();
    }
    createDhtNodeRpcRemote(peerDescriptor) {
        return new DhtNodeRpcRemote(this.localPeerDescriptor, peerDescriptor, this.options.serviceId, this.rpcCommunicator, this.options.rpcRequestTimeout);
    }
}

/* eslint-disable max-len, @typescript-eslint/prefer-for-of */
// This file contains a 16x16 matrix of ping times measured between
// amazon regions. Converted to typescript from the file pings.csv
// in order to make loading into browser easier.
var RegionIndex;
(function (RegionIndex) {
    RegionIndex[RegionIndex["eu_central_1"] = 0] = "eu_central_1";
    RegionIndex[RegionIndex["eu_west_1"] = 1] = "eu_west_1";
    RegionIndex[RegionIndex["eu_west_2"] = 2] = "eu_west_2";
    RegionIndex[RegionIndex["eu_west_3"] = 3] = "eu_west_3";
    RegionIndex[RegionIndex["eu_north_1"] = 4] = "eu_north_1";
    RegionIndex[RegionIndex["us_east_1"] = 5] = "us_east_1";
    RegionIndex[RegionIndex["us_east_2"] = 6] = "us_east_2";
    RegionIndex[RegionIndex["us_west_1"] = 7] = "us_west_1";
    RegionIndex[RegionIndex["us_west_2"] = 8] = "us_west_2";
    RegionIndex[RegionIndex["ca_central_1"] = 9] = "ca_central_1";
    RegionIndex[RegionIndex["ap_south_1"] = 10] = "ap_south_1";
    RegionIndex[RegionIndex["ap_northeast_2"] = 11] = "ap_northeast_2";
    RegionIndex[RegionIndex["ap_southeast_1"] = 12] = "ap_southeast_1";
    RegionIndex[RegionIndex["ap_southeast_2"] = 13] = "ap_southeast_2";
    RegionIndex[RegionIndex["ap_northeast_1"] = 14] = "ap_northeast_1";
    RegionIndex[RegionIndex["sa_east_1"] = 15] = "sa_east_1";
})(RegionIndex || (RegionIndex = {}));
const regionPingMatrix = [
    [0.325, 23.163, 12.942, 8.923, 20.699, 89.208, 99.98, 150.077, 139.224, 90.454, 117.0, 226.823, 154.592, 286.753, 222.719, 205.563],
    [24.911, 0.195, 10.022, 18.544, 39.802, 66.161, 93.877, 136.271, 127.362, 73.733, 122.187, 229.064, 177.256, 254.277, 200.207, 186.817],
    [12.879, 9.994, 0.167, 8.626, 31.896, 74.663, 85.231, 136.185, 125.328, 77.811, 111.29, 236.34, 164.357, 262.577, 207.745, 187.383],
    [9.007, 16.823, 8.761, 0.191, 28.449, 81.969, 92.599, 140.961, 132.269, 85.444, 107.421, 243.981, 167.17, 279.322, 214.088, 197.986],
    [20.661, 40.0, 31.176, 27.645, 0.137, 104.171, 115.704, 165.01, 155.741, 107.635, 136.75, 267.321, 173.951, 292.266, 238.492, 216.048],
    [89.076, 67.701, 75.414, 81.598, 104.866, 0.284, 11.501, 61.662, 78.612, 14.115, 186.697, 174.546, 210.93, 196.071, 145.622, 114.919],
    [100.018, 93.968, 85.06, 92.582, 115.719, 11.514, 0.184, 51.668, 48.447, 25.808, 195.433, 160.031, 197.27, 186.913, 133.115, 127.954],
    [148.544, 137.18, 136.137, 141.333, 164.73, 60.257, 50.886, 0.197, 21.509, 77.668, 229.32, 134.858, 174.472, 137.329, 107.566, 175.944],
    [137.885, 128.304, 125.316, 132.271, 155.726, 63.376, 48.457, 20.743, 0.293, 59.645, 218.285, 126.988, 162.72, 138.599, 100.203, 174.612],
    [90.494, 78.203, 77.998, 84.348, 109.549, 14.136, 25.917, 77.323, 59.692, 0.163, 187.854, 169.253, 206.944, 195.467, 141.054, 126.962],
    [120.19, 119.978, 112.023, 107.434, 139.361, 185.043, 195.679, 233.008, 216.025, 186.888, 0.177, 130.21, 56.019, 145.643, 128.57, 298.98],
    [226.296, 229.187, 237.024, 243.839, 266.919, 174.727, 160.066, 136.623, 124.799, 169.854, 129.131, 0.189, 81.065, 139.967, 33.24, 284.578],
    [154.691, 178.279, 170.997, 154.261, 173.776, 211.511, 197.551, 173.735, 162.834, 206.659, 56.964, 68.853, 0.215, 91.601, 69.684, 323.146],
    [289.784, 253.234, 261.752, 277.504, 292.125, 198.099, 185.709, 136.413, 138.613, 194.986, 142.484, 147.706, 91.615, 0.156, 114.518, 311.12],
    [223.926, 201.084, 210.133, 216.779, 237.762, 145.336, 132.887, 108.26, 98.057, 142.613, 122.716, 33.185, 68.547, 114.901, 0.162, 257.403],
    [205.555, 178.082, 186.533, 195.774, 216.701, 117.089, 128.001, 175.761, 176.018, 125.551, 303.904, 285.782, 323.15, 309.618, 255.904, 0.262]
];
function getRegionDelayMatrix() {
    const ret = [];
    for (let i = 0; i < regionPingMatrix.length; i++) {
        const row = [];
        for (let j = 0; j < regionPingMatrix[i].length; j++) {
            row.push(regionPingMatrix[i][j] / 2);
        }
        ret.push(row);
    }
    return ret;
}
function getRandomRegion() {
    return Math.floor(Math.random() * 15);
}

const logger = new Logger('Simulator');
var LatencyType;
(function (LatencyType) {
    LatencyType["NONE"] = "NONE";
    LatencyType["RANDOM"] = "RANDOM";
    LatencyType["REAL"] = "REAL";
    LatencyType["FIXED"] = "FIXED";
})(LatencyType || (LatencyType = {}));
// One-way 'pipe' of messages
class Association {
    connectedCallback;
    sourceConnection;
    destinationConnection;
    lastOperationAt = 0;
    closing = false;
    constructor(sourceConnection, destinationConnection, connectedCallback) {
        this.connectedCallback = connectedCallback;
        this.sourceConnection = sourceConnection;
        this.destinationConnection = destinationConnection;
    }
    setDestinationConnection(connection) {
        this.destinationConnection = connection;
    }
    getLastOperationAt() {
        return this.lastOperationAt;
    }
    setLastOperationAt(executionTime) {
        this.lastOperationAt = executionTime;
    }
    setClosing() {
        this.closing = true;
    }
    isClosing() {
        return this.closing;
    }
}
class SimulatorOperation {
    executionTime;
    association;
    static objectCounter = 0;
    objectId = 0;
    constructor(executionTime, association) {
        this.executionTime = executionTime;
        this.association = association;
        this.objectId = SimulatorOperation.objectCounter;
        SimulatorOperation.objectCounter++;
    }
}
class ConnectOperation extends SimulatorOperation {
    sourceConnection;
    targetDescriptor;
    constructor(executionTime, association, sourceConnection, targetDescriptor) {
        super(executionTime, association);
        this.sourceConnection = sourceConnection;
        this.targetDescriptor = targetDescriptor;
    }
}
class SendOperation extends SimulatorOperation {
    data;
    constructor(executionTime, association, data) {
        super(executionTime, association);
        this.data = data;
    }
}
class CloseOperation extends SimulatorOperation {
}
class Simulator {
    stopped = false;
    connectors = new Map();
    latencyTable;
    associations = new Map();
    latencyType;
    fixedLatency;
    loopCounter = 0;
    MAX_LOOPS = 1000;
    operationQueue = new Heap((a, b) => {
        if ((a.executionTime - b.executionTime) === 0) {
            return (a.objectId - b.objectId);
        }
        else {
            return (a.executionTime - b.executionTime);
        }
    });
    simulatorTimeout;
    constructor(latencyType = LatencyType.NONE, fixedLatency) {
        this.latencyType = latencyType;
        this.fixedLatency = fixedLatency;
        if (this.latencyType === LatencyType.REAL) {
            this.latencyTable = getRegionDelayMatrix();
        }
        if ((this.latencyType === LatencyType.FIXED) && (this.fixedLatency === undefined)) {
            throw new Error('LatencyType.FIXED requires the desired latency to be given as second parameter');
        }
        this.generateExecutionTime = this.generateExecutionTime.bind(this);
        this.getLatency = this.getLatency.bind(this);
        this.executeCloseOperation = this.executeCloseOperation.bind(this);
        this.executeConnectOperation = this.executeConnectOperation.bind(this);
        this.executeSendOperation = this.executeSendOperation.bind(this);
        this.executeQueuedOperations = this.executeQueuedOperations.bind(this);
        this.accept = this.accept.bind(this);
        this.send = this.send.bind(this);
        this.close = this.close.bind(this);
        this.scheduleNextTimeout = this.scheduleNextTimeout.bind(this);
        this.scheduleOperation = this.scheduleOperation.bind(this);
    }
    generateExecutionTime(association, sourceRegion, targetRegion) {
        let executionTime = Date.now() + this.getLatency(sourceRegion, targetRegion);
        if (association.getLastOperationAt() > executionTime) {
            executionTime = association.getLastOperationAt();
        }
        return executionTime;
    }
    getLatency(sourceRegion, targetRegion) {
        let latency = 0;
        if (this.latencyType === LatencyType.FIXED) {
            latency = this.fixedLatency;
        }
        if (this.latencyType === LatencyType.REAL) {
            if (sourceRegion === undefined || targetRegion === undefined || sourceRegion > 15 || targetRegion > 15) {
                logger.error('invalid region index given to Simulator');
                throw new Error('invalid region index given to Simulator');
            }
            latency = this.latencyTable[sourceRegion][targetRegion];
        }
        if (this.latencyType === LatencyType.RANDOM) {
            latency = Math.random() * (250 - 5) + 5;
        }
        return latency;
    }
    accept(sourceConnection, targetConnection) {
        const sourceAssociation = this.associations.get(sourceConnection.connectionId);
        if (!sourceAssociation) {
            logger.error('source association not found in accept()');
            return;
        }
        sourceAssociation.setDestinationConnection(targetConnection);
        const targetAssociation = new Association(targetConnection, sourceConnection);
        this.associations.set(targetConnection.connectionId, targetAssociation);
        sourceAssociation.connectedCallback();
    }
    addConnector(connector) {
        this.connectors.set(toNodeId(connector.getPeerDescriptor()), connector);
    }
    executeConnectOperation(operation) {
        const target = this.connectors.get(toNodeId(operation.targetDescriptor));
        if (!target) {
            logger.error('Target connector not found when executing connect operation');
            operation.association.connectedCallback('Target connector not found');
            return;
        }
        target.handleIncomingConnection(operation.sourceConnection);
    }
    executeCloseOperation(operation) {
        if (this.stopped) {
            return;
        }
        const target = operation.association.destinationConnection;
        let counterAssociation;
        if (target) {
            counterAssociation = this.associations.get(target.connectionId);
        }
        if (!target || !counterAssociation) {
            this.associations.delete(operation.association.sourceConnection.connectionId);
        }
        else if (!counterAssociation.isClosing()) {
            target.handleIncomingDisconnection();
            this.close(target);
        }
        else {
            // this is the 'ack' of the CloseOperation to the original closer
            this.associations.delete(target.connectionId);
            this.associations.delete(operation.association.sourceConnection.connectionId);
        }
    }
    executeSendOperation(operation) {
        if (this.stopped) {
            return;
        }
        const target = operation.association.destinationConnection;
        target.handleIncomingData(operation.data);
    }
    executeQueuedOperations() {
        const currentTime = Date.now();
        while (this.operationQueue.size() > 0
            && this.operationQueue.peek().executionTime <= currentTime) {
            const operation = this.operationQueue.pop();
            if (operation instanceof ConnectOperation) {
                this.executeConnectOperation(operation);
            }
            else if (operation instanceof CloseOperation) {
                this.executeCloseOperation(operation);
            }
            else if (operation instanceof SendOperation) {
                this.executeSendOperation(operation);
            }
            else {
                logger.error('Unknown SimulatorOperation');
            }
            this.loopCounter++;
            if (this.loopCounter >= this.MAX_LOOPS) {
                this.loopCounter = 0;
                setTimeout(() => this.executeQueuedOperations(), 0);
                return;
            }
        }
        this.scheduleNextTimeout();
    }
    scheduleNextTimeout() {
        if (this.simulatorTimeout) {
            clearTimeout(this.simulatorTimeout);
            this.simulatorTimeout = undefined;
        }
        const currentTime = Date.now();
        const firstOperation = this.operationQueue.peek();
        if (!firstOperation) {
            return;
        }
        const firstOperationTime = firstOperation.executionTime;
        const timeDifference = firstOperationTime - currentTime;
        this.simulatorTimeout = setTimeout(this.executeQueuedOperations, timeDifference);
    }
    scheduleOperation(operation) {
        this.operationQueue.push(operation);
        this.scheduleNextTimeout();
    }
    connect(sourceConnection, targetDescriptor, connectedCallback) {
        if (this.stopped) {
            logger.error('connect() called on a stopped simulator ' + (new Error().stack));
            return;
        }
        this.operationQueue.size();
        const association = new Association(sourceConnection, undefined, connectedCallback);
        this.associations.set(sourceConnection.connectionId, association);
        const executionTime = this.generateExecutionTime(association, sourceConnection.localPeerDescriptor.region, targetDescriptor.region);
        association.setLastOperationAt(executionTime);
        const operation = new ConnectOperation(executionTime, association, sourceConnection, targetDescriptor);
        this.scheduleOperation(operation);
    }
    close(sourceConnection) {
        if (this.stopped) {
            return;
        }
        const association = this.associations.get(sourceConnection.connectionId);
        if (!association) {
            return;
        }
        association.setClosing();
        const executionTime = this.generateExecutionTime(association, sourceConnection.localPeerDescriptor.region, sourceConnection.getPeerDescriptor()?.region);
        association.setLastOperationAt(executionTime);
        const operation = new CloseOperation(executionTime, association);
        this.scheduleOperation(operation);
    }
    send(sourceConnection, data) {
        if (this.stopped) {
            return;
        }
        const association = this.associations.get(sourceConnection.connectionId);
        if (!association) {
            return;
        }
        if (association.isClosing()) {
            logger.trace('Tried to call send() on a closing association');
            return;
        }
        const executionTime = this.generateExecutionTime(association, sourceConnection.localPeerDescriptor.region, association.destinationConnection.localPeerDescriptor.region);
        association.setLastOperationAt(executionTime);
        const operation = new SendOperation(executionTime, association, data);
        this.scheduleOperation(operation);
    }
    stop() {
        this.stopped = true;
        logger.info(this.associations.size + ' associations in the beginning of stop()');
        if (this.simulatorTimeout) {
            clearTimeout(this.simulatorTimeout);
        }
    }
}

class SimulatorTransport extends ConnectionManager {
    constructor(localPeerDescriptor, simulator) {
        super({
            createConnectorFacade: () => new SimulatorConnectorFacade(localPeerDescriptor, simulator),
            metricsContext: new MetricsContext(),
            allowIncomingPrivateConnections: false
        });
    }
}

export { ConnectionManager, ConnectionType, DataEntry, DefaultConnectorFacade, DhtCallContext, DhtNode, EXISTING_CONNECTION_TIMEOUT, LatencyType, ListeningRpcCommunicator, ManagedConnection, Message, NodeType, PeerDescriptor, PendingConnection, RoutingRpcCommunicator, RpcRemote, Simulator, SimulatorTransport, WebsocketClientConnection, areEqualPeerDescriptors, createOutgoingHandshaker, getRandomRegion, getRegionDelayMatrix, randomDhtAddress, toDhtAddress, toDhtAddressRaw, toNodeId };
//# sourceMappingURL=exports-browser.js.map
